import { FormEvent, Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { formatPeso } from "../shared/domain";
import { useCurrentUnitId, useSelectedUnitId, withCurrentUnit } from "./unitContext";
import { usePermissions } from "./access";
import { userFacingApiError } from "./errors";
import { demoMode } from "./environment";
import { clearGuardStorage, endGuardShiftForSignOut, getGuardMeta } from "./guard-store";
import { ModalSurface, useDialogs } from "./dialogs";
const Visitors = lazy(() => import("./Visitors").then((module) => ({ default: module.Visitors })));
const VisitorPass = lazy(() => import("./Visitors").then((module) => ({ default: module.VisitorPass })));
const Permits = lazy(() => import("./Permits").then((module) => ({ default: module.Permits })));
const Deliveries = lazy(() => import("./Deliveries").then((module) => ({ default: module.Deliveries })));
const Mobility = lazy(() => import("./Mobility").then((module) => ({ default: module.Mobility })));
const Staff = lazy(() => import("./Staff").then((module) => ({ default: module.Staff })));
const OperationsSettings = lazy(() => import("./OperationsSettings").then((module) => ({ default: module.OperationsSettings })));
const HandoverAcceptance = lazy(() => import("./OperationsSettings").then((module) => ({ default: module.HandoverAcceptance })));
const TechnicalDiagnostics = lazy(() => import("./OperationsSettings").then((module) => ({ default: module.TechnicalDiagnostics })));
const Accounting = lazy(() => import("./Accounting").then((module) => ({ default: module.Accounting })));
const TaxCompliance = lazy(() => import("./TaxCompliance").then((module) => ({ default: module.TaxCompliance })));
const BillingOperations = lazy(() => import("./BillingOperations").then((module) => ({ default: module.BillingOperations })));
const Payments = lazy(() => import("./Payments").then((module) => ({ default: module.Payments })));
const PoolAccess = lazy(() => import("./PoolAccess").then((module) => ({ default: module.PoolAccess })));
const Reservations = lazy(() => import("./Reservations").then((module) => ({ default: module.Reservations })));
const AccessWallet = lazy(() => import("./AccessWallet").then((module) => ({ default: module.AccessWallet })));
const AccessDesk = lazy(() => import("./AccessWallet").then((module) => ({ default: module.AccessDesk })));
const GuardDesk = lazy(() => import("./GuardDesk").then((module) => ({ default: module.GuardDesk })));
const GuardIntake = lazy(() => import("./GuardIntake").then((module) => ({ default: module.GuardIntake })));
const PublicReservations = lazy(() => import("./Reservations").then((module) => ({ default: module.PublicReservations })));
const ParkingPatrol = lazy(() => import("./ParkingPatrol").then((module) => ({ default: module.ParkingPatrol })));
const MarketplaceSubmissionForm = lazy(() => import("./MarketplaceSubmissions").then((module) => ({ default: module.MarketplaceSubmissionForm })));
const MarketplaceSubmissionList = lazy(() => import("./MarketplaceSubmissions").then((module) => ({ default: module.MarketplaceSubmissionList })));
const PublicMarketplaceSubmission = lazy(() => import("./MarketplaceSubmissions").then((module) => ({ default: module.PublicMarketplaceSubmission })));
const Transparency = lazy(() => import("./Transparency").then((module) => ({ default: module.Transparency })));

type Persona = "admin" | "resident" | "maintenance";
type SessionUser = {
  userId: string;
  name: string;
  email: string;
  role: string;
};
type AppNotification = {
  id: string;
  title: string;
  body: string;
  href?: string;
  readAt?: number;
  createdAt: number;
};
type Ticket = {
  id: string;
  number: string;
  title: string;
  category: string;
  status: string;
  priority?: string;
  requestType?: string;
  nextAction?: string;
  location: string;
  updated: string;
  assignee?: string;
};
type TicketStatusFilter = "active" | "open" | "triaged" | "scheduled" | "in_progress" | "awaiting_parts" | "awaiting_resident" | "awaiting_vendor" | "resolved" | "reopened" | "closed" | "cancelled";
type MaintenanceQueueItem = {
  key: string;
  kind: string;
  title: string;
  detail?: string;
  status: string;
  priority: "urgent" | "high" | "normal";
  nextAction: string;
  href: string;
  createdAt: number;
  dueAt?: number;
};
type MaintenanceQueue = {
  role: "security_desk" | "operations" | "resident_services" | "finance_encoder" | "finance_approver";
  available: boolean;
  items: MaintenanceQueueItem[];
  awaitingAction: number;
  hasMore: boolean;
};
const ticketStatusOptions: Array<{ value: Exclude<TicketStatusFilter, "active">; label: string }> = [
  { value: "open", label: "Submitted" },
  { value: "triaged", label: "Triaged" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "awaiting_parts", label: "Awaiting parts" },
  { value: "awaiting_resident", label: "Awaiting resident" },
  { value: "awaiting_vendor", label: "Awaiting vendor" },
  { value: "resolved", label: "Resolved" },
  { value: "reopened", label: "Reopened" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];
const residentTicketStatusOptions = [
  { value: "active", label: "Active", description: "All except closed or cancelled" },
  { value: "all", label: "All", description: "Every request" },
  ...ticketStatusOptions.map((option) => ({ ...option, description: undefined })),
];
type TicketOptions = {
  categories: Array<{ id: string; name: string }>;
  units: Array<{ id: string; code: string; buildingId: string; buildingName: string }>;
  buildings: Array<{ id: string; name: string }>;
  assignees: Array<{ userId: string; name: string }>;
};
type TicketDetail = {
  id: string;
  publicNumber: string;
  title: string;
  description: string;
  status: string;
  category: string;
  locationLabel: string;
  locationType: string;
  unitCode?: string;
  buildingName?: string;
  assignedTo?: string;
  assignedToUserId?: string;
  scheduledVisitStart?: number;
  scheduledVisitEnd?: number;
  scheduledWorkerUserId?: string;
  scheduledWorkerName?: string;
  scheduledVisitNote?: string;
  scheduledAt?: number;
  currentAssignmentId?: string;
  currentAppointmentId?: string;
  appointmentResidentConfirmedAt?: number;
  guardNoticeStatus?: string;
  requestType?: string;
  reportedPriority?: string;
  priority?: string;
  nextAction?: string;
  responseDueAt?: number;
  dispatchDueAt?: number;
  resolutionDueAt?: number;
  breachRisk?: string;
  resolutionEventId?: string;
  residentConfirmedAt?: number;
  version?: number;
  estimates?: Array<{
    id: string;
    revision: number;
    amountCentavos: number;
    approvalThresholdCentavos: number;
    description: string;
    status: string;
    submittedByUserId: string;
    approvedByUserId?: string;
    approvedAt?: number;
    createdAt: number;
  }>;
  incidents?: Array<{
    id: string;
    incidentType: string;
    severity: string;
    status: string;
    ownerUserId?: string;
    acknowledgedAt?: number;
    mitigatedAt?: number;
    safetyMessage: string;
    createdAt: number;
  }>;
  warrantyCases?: Array<{
    id: string;
    vendorId?: string;
    warrantyUntil: number;
    status: string;
    note?: string;
    createdAt: number;
  }>;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  reopenCount: number;
  events: Array<{
    id: string;
    fromStatus?: string;
    toStatus: string;
    comment?: string;
    createdAt: number;
    actorName?: string;
  }>;
  attachments: Array<{
    id: string;
    originalName: string;
    contentType: string;
    status: string;
    failureReason?: string;
    fileUrl?: string;
  }>;
};
type AdminDashboardData = {
  profileName: string;
  pendingRegistrations: number;
  readyRegistrations: number;
  ticketCounts: Array<{ status: string; count: number }>;
  portfolioBalanceCentavos: number;
  activeUnits: number;
  buildingCount: number;
  unassignedTickets: number;
  billedThisMonthCentavos: number;
  collectedThisMonthCentavos: number;
  recentRegistrations: Array<{
    id: string;
    name: string;
    unitLabel?: string;
    relationshipType: string;
    documentCount: number;
    cleanDocumentCount: number;
  }>;
  emailQueue: { pending: number; failed: number };
  scannerEnabled: boolean;
  lastBackupAt: number | null;
  workItems: Array<{
    id: string;
    type: string;
    title: string;
    detail: string;
    status: string;
    createdAt: number;
    href: string;
  }>;
};
type ResidentDashboardData = {
  profileName: string;
  units: Array<{
    id: string;
    code: string;
    floor?: string;
    buildingName: string;
    relationshipType: string;
    balanceCentavos: number;
  }>;
  tickets: Array<{
    id: string;
    publicNumber: string;
    title: string;
    status: string;
    locationLabel?: string;
    updatedAt: number;
    category: string;
  }>;
  announcements: Array<{
    id: string;
    title: string;
    body: string;
    publishedAt: number;
  }>;
  unreadNotifications: number;
};

const ticketsSeed: Ticket[] = [
  {
    id: "1",
    number: "TK-4921",
    title: "Master bath faucet leak",
    category: "Plumbing",
    status: "in_progress",
    location: "Tower 3 · Unit 3012B",
    updated: "2h ago",
    assignee: "Marcus Rivera",
  },
  {
    id: "2",
    number: "TK-4885",
    title: "Hallway light flickering",
    category: "Electrical",
    status: "open",
    location: "Tower 2 · Floor 12",
    updated: "Yesterday",
  },
  {
    id: "3",
    number: "TK-4720",
    title: "Mailbox key replacement",
    category: "General",
    status: "resolved",
    location: "Tower 3 · Unit 3012B",
    updated: "3 days ago",
    assignee: "Elena Cruz",
  },
  {
    id: "4",
    number: "TK-4688",
    title: "AC cooling issue",
    category: "Climate",
    status: "reopened",
    location: "Tower 3 · Unit 2414A",
    updated: "4 days ago",
    assignee: "Marcus Rivera",
  },
];

const ledgerSeed = [
  {
    id: "l1",
    date: "Jul 01, 2026",
    type: "Charge",
    category: "Association dues",
    reference: "July monthly dues",
    amount: 850000,
    balance: 850000,
  },
  {
    id: "l2",
    date: "Jul 04, 2026",
    type: "Payment",
    category: "Association dues",
    reference: "Bank deposit · OR-4902",
    amount: -500000,
    balance: 350000,
  },
  {
    id: "l3",
    date: "Jul 08, 2026",
    type: "Charge",
    category: "Utilities",
    reference: "Water submeter",
    amount: 137500,
    balance: 487500,
  },
  {
    id: "l4",
    date: "Jul 12, 2026",
    type: "Adjustment",
    category: "Other",
    reference: "Prior billing correction",
    amount: -25000,
    balance: 462500,
  },
];

const announcementsSeed = [
  {
    id: "a1",
    date: "TODAY · 10:00 AM",
    title: "Main lobby improvement works",
    body: "Please use the east entrance from July 20–24 while the lobby floor is resealed.",
    scope: "All buildings",
  },
  {
    id: "a2",
    date: "JUL 15 · 2:30 PM",
    title: "Water interruption notice",
    body: "Tower 3 water service will pause from 1:00–3:00 PM on Saturday for preventive maintenance.",
    scope: "Tower 3",
  },
  {
    id: "a3",
    date: "JUL 10 · 9:00 AM",
    title: "Quarterly pest control",
    body: "Common-area treatment begins Monday. Unit access is not required.",
    scope: "All buildings",
  },
];

type RegistrationRow = {
  id: string;
  name: string;
  email: string;
  unit: string;
  relationship: string;
  scan: string;
  submitted: string;
  emailVerified: boolean;
  preVerified?: boolean;
  requiredDocuments?: string[];
};
type RegistrationReviewDocument = {
  id: string;
  type: string;
  originalName: string;
  status: string;
  failureReason?: string;
  createdAt: number;
  fileUrl?: string;
};

const registrationsSeed: RegistrationRow[] = [
  {
    id: "r1",
    name: "Camille Santos",
    email: "camille@example.com",
    unit: "Tower 3 · 1809A",
    relationship: "Owner",
    scan: "Clean",
    submitted: "18 min ago",
    emailVerified: true,
  },
  {
    id: "r2",
    name: "Paolo Reyes",
    email: "paolo@example.com",
    unit: "Tower 2 · 1711",
    relationship: "Tenant",
    scan: "Processing",
    submitted: "1h ago",
    emailVerified: true,
  },
  {
    id: "r3",
    name: "Mina Villanueva",
    email: "mina@example.com",
    unit: "Tower 1 · 1202",
    relationship: "Owner",
    scan: "Clean",
    submitted: "Yesterday",
    emailVerified: true,
  },
];

type PropertyFacility = {
  id: string;
  name: string;
  facilityType: string;
  locationDescription: string;
  accessPolicy: string;
  accessInstructions?: string;
  requiresAccessLog: number;
  buildingName?: string;
  levelCode?: string;
};

const propertyFacilitiesSeed: PropertyFacility[] = [
  {
    id: "facility-admin-office",
    name: "Admin office",
    facilityType: "admin_office",
    locationDescription: "Ground floor of Tower 1",
    accessPolicy: "resident_and_staff",
    accessInstructions: "Resident services and billing inquiries.",
    requiresAccessLog: 0,
    buildingName: "Tower 1",
    levelCode: "G",
  },
  {
    id: "facility-gym",
    name: "Gym",
    facilityType: "gym",
    locationDescription: "5th floor of Tower 2",
    accessPolicy: "authenticated_resident",
    accessInstructions:
      "From Tower 3, take the elevator to P4 and cross through the connecting garden.",
    requiresAccessLog: 0,
    buildingName: "Tower 2",
    levelCode: "5",
  },
  {
    id: "facility-garden-connector",
    name: "Connecting garden",
    facilityType: "garden",
    locationDescription: "Tower 3 P4 to Tower 2 level 5",
    accessPolicy: "authenticated_resident",
    accessInstructions:
      "Garden walkway connecting Tower 3 to the Tower 2 gym level.",
    requiresAccessLog: 0,
    buildingName: "Tower 3",
    levelCode: "P4",
  },
  {
    id: "facility-basketball-court",
    name: "Basketball court",
    facilityType: "basketball_court",
    locationDescription: "Property location to be confirmed",
    accessPolicy: "authenticated_resident",
    requiresAccessLog: 0,
  },
  {
    id: "facility-swimming-pool",
    name: "Swimming pool",
    facilityType: "swimming_pool",
    locationDescription: "Property location to be confirmed",
    accessPolicy: "authenticated_signed_log",
    accessInstructions:
      "Sign in with your name and unit, then provide a signature before access.",
    requiresAccessLog: 1,
  },
  {
    id: "facility-kids-play-area",
    name: "Kids play area",
    facilityType: "kids_play_area",
    locationDescription: "Ground floor of Tower 3",
    accessPolicy: "authenticated_resident",
    requiresAccessLog: 0,
    buildingName: "Tower 3",
    levelCode: "G",
  },
  {
    id: "facility-common-area",
    name: "Common area",
    facilityType: "common_area",
    locationDescription: "Ground floor between Tower 3 and Tower 2",
    accessPolicy: "authenticated_resident",
    requiresAccessLog: 0,
  },
];

type NavItem = { to: string; label: string; glyph: string };
type NavGroup = { id: string; label: string; items: NavItem[] };
const nav: Record<Persona, NavGroup[]> = {
  admin: [
    {
      id: "core",
      label: "Core",
      items: [
        { to: "/admin/overview", label: "Overview", glyph: "▦" },
        { to: "/admin/units", label: "Unit masterlist", glyph: "▤" },
        { to: "/admin/registrations", label: "Registrations", glyph: "◎" },
        { to: "/admin/transparency", label: "PMO transparency", glyph: "◌" },
      ],
    },
    {
      id: "finance",
      label: "Finance",
      items: [
        { to: "/admin/ledger", label: "Unit ledgers", glyph: "₱" },
        {
          to: "/admin/billing-documents",
          label: "Billing documents",
          glyph: "▤",
        },
        {
          to: "/admin/billing-operations",
          label: "Rates, readings & dues",
          glyph: "◫",
        },
        { to: "/admin/payments", label: "Payments & receipts", glyph: "₱" },
        { to: "/admin/accounting", label: "Accounting", glyph: "±" },
        { to: "/admin/tax", label: "Tax compliance", glyph: "▥" },
      ],
    },
    {
      id: "property",
      label: "Property & access",
      items: [
        { to: "/admin/tickets", label: "Tickets & requests", glyph: "⌑" },
        { to: "/admin/visitors", label: "Visitors", glyph: "⌁" },
        { to: "/admin/access", label: "Access validation", glyph: "▣" },
        { to: "/admin/guard", label: "Guard reliability", glyph: "◈" },
        { to: "/admin/permits", label: "Permits", glyph: "▧" },
        { to: "/admin/packages", label: "Packages", glyph: "▥" },
        { to: "/admin/mobility", label: "Parking & RFID", glyph: "⌁" },
        { to: "/admin/parking-rounds", label: "Parking rounds", glyph: "◉" },
        { to: "/admin/pool", label: "Pool attendance", glyph: "≈" },
        { to: "/admin/reservations", label: "Amenity reservations", glyph: "◫" },
      ],
    },
    {
      id: "community",
      label: "Community",
      items: [
        { to: "/admin/announcements", label: "Announcements", glyph: "◖" },
        { to: "/admin/events", label: "Events", glyph: "◫" },
        { to: "/admin/community", label: "Community board", glyph: "◇" },
        { to: "/admin/marketplace", label: "Marketplace", glyph: "▣" },
      ],
    },
    {
      id: "administration",
      label: "Administration",
      items: [
        { to: "/admin/staff/directory", label: "Staff directory", glyph: "◷" },
        { to: "/admin/staff/schedules", label: "Shifts & schedules", glyph: "◷" },
        { to: "/admin/staff/leaves", label: "Leave requests", glyph: "◷" },
        { to: "/admin/imports", label: "Data Imports", glyph: "⇧" },
        { to: "/admin/audit", label: "Audit trail", glyph: "≡" },
        {
          to: "/admin/operations-settings",
          label: "Operations settings",
          glyph: "⚙",
        },
      ],
    },
  ],
  resident: [
    {
      id: "core",
      label: "Home",
      items: [
        { to: "/resident/home", label: "Home", glyph: "▦" },
        { to: "/resident/access", label: "My Access", glyph: "▣" },
        { to: "/resident/tickets", label: "My tickets", glyph: "⌑" },
        { to: "/resident/transparency", label: "Community transparency", glyph: "◌" },
      ],
    },
    {
      id: "finance",
      label: "Finance",
      items: [
        { to: "/resident/statements", label: "Statements", glyph: "▤" },
        { to: "/resident/billing", label: "My ledger", glyph: "₱" },
        { to: "/resident/receipts", label: "Payment receipts", glyph: "✓" },
      ],
    },
    {
      id: "property",
      label: "Access & requests",
      items: [
        { to: "/resident/visitors", label: "My visitors", glyph: "⌁" },
        { to: "/resident/permits", label: "My permits", glyph: "▧" },
        { to: "/resident/packages", label: "My packages", glyph: "▥" },
        { to: "/resident/mobility", label: "Vehicles & parking", glyph: "⌁" },
        { to: "/resident/pool", label: "Pool access", glyph: "≈" },
        { to: "/resident/reservations", label: "Amenity reservations", glyph: "◫" },
      ],
    },
    {
      id: "community",
      label: "Community",
      items: [
        { to: "/resident/announcements", label: "Announcements", glyph: "◖" },
        { to: "/resident/events", label: "Events", glyph: "◫" },
        { to: "/resident/community", label: "Community board", glyph: "◇" },
        { to: "/resident/marketplace", label: "Marketplace", glyph: "▣" },
      ],
    },
  ],
  maintenance: [
    {
      id: "core",
      label: "Workspace",
      items: [
        { to: "/maintenance/home", label: "My workspace", glyph: "▦" },
        { to: "/maintenance/tickets", label: "Assigned tickets", glyph: "⌑" },
        { to: "/maintenance/registrations", label: "Registration approvals", glyph: "◎" },
        { to: "/maintenance/transparency", label: "PMO transparency", glyph: "◌" },
        { to: "/maintenance/visitors", label: "Visitor desk", glyph: "⌁" },
        { to: "/maintenance/access", label: "Access validation", glyph: "▣" },
        { to: "/maintenance/guard", label: "Guard front desk", glyph: "◈" },
        { to: "/maintenance/permits", label: "Permit review", glyph: "▧" },
        { to: "/maintenance/packages", label: "Package desk", glyph: "▥" },
        { to: "/maintenance/parking-rounds", label: "Parking rounds", glyph: "◉" },
        { to: "/maintenance/pool", label: "Pool attendance", glyph: "≈" },
        { to: "/maintenance/reservations", label: "Amenity reservations", glyph: "◫" },
        { to: "/maintenance/announcements", label: "Announcements", glyph: "◖" },
        { to: "/maintenance/events", label: "Events", glyph: "◫" },
        { to: "/maintenance/marketplace", label: "Marketplace review", glyph: "▣" },
        { to: "/maintenance/schedule", label: "My schedule", glyph: "◷" },
      ],
    },
    {
      id: "finance",
      label: "Finance",
      items: [
        { to: "/maintenance/billing-operations", label: "Billing operations", glyph: "▤" },
        { to: "/maintenance/billing-documents", label: "Billing runs", glyph: "▧" },
        { to: "/maintenance/payments", label: "Payments", glyph: "✓" },
        { to: "/maintenance/accounting", label: "Financial reports", glyph: "₱" },
        { to: "/maintenance/tax", label: "Tax compliance", glyph: "▥" },
      ],
    },
  ],
};

const featureForRoute = (path: string) =>
  path.includes("/tax")
    ? "tax_compliance"
    : path.includes("transparency")
    ? "pmo_transparency"
    : path.includes("access")
    ? "access_wallet"
    : path.includes("billing") ||
  path.includes("ledger") ||
  path.includes("statements") ||
  path.includes("accounting") ||
  path.includes("payments") ||
  path.includes("receipts")
    ? "billing"
    : path.includes("community")
      ? "community"
      : path.includes("marketplace")
        ? "marketplace"
        : path.includes("events")
          ? "events"
          : path.includes("visitors")
            ? "visitors"
            : path.includes("permits")
              ? "permits"
              : path.includes("packages")
                ? "packages"
                : path.includes("mobility") || path.includes("parking-rounds")
                  ? "mobility"
                  : path.includes("staff") || path.includes("schedule")
                    ? "staff"
                    : path.includes("reservation")
                      ? "reservations"
                    : undefined;

const permissionForRoute = (path: string) =>
  path.includes("/tax") ? "tax.view"
    : path.includes("/transparency") ? "transparency.view"
    : path.includes("/guard") ? "guard.events.sync"
    : path.includes("/access") ? "access.verify"
    : path.includes("/parking-rounds") ? "parking.patrol.view"
    : path.includes("/billing-operations") || path.includes("/billing-documents") || path.includes("/payments") || path.includes("/accounting") ? "billing.view"
    : path.includes("/visitors") ? "visitors.view"
    : path.includes("/permits") ? "permits.view_all"
      : path.includes("/packages") ? "packages.view"
          : path.includes("/pool") ? "pool.view"
          : path.includes("/reservations") ? "reservations.manage"
          : path.includes("/registrations") ? "registrations.view"
          : path.includes("/announcements") ? "announcements.publish"
          : path.includes("/events") ? "events.manage"
          : path.includes("/marketplace") ? "marketplace.review"
          : path.includes("/tickets") ? "tickets.view_assigned"
          : undefined;

export function App() {
  const [persona, setPersona] = useState<Persona>(
    () =>
      (localStorage.getItem("two-carats-persona") as Persona) ||
      (localStorage.getItem("crysta-persona") as Persona) ||
      "admin",
  );
  const [authState, setAuthState] = useState<
    | "loading"
    | "authenticated"
    | "unauthenticated"
    | "setup_2fa"
    | "registration"
  >(demoMode ? "authenticated" : "loading");
  const [tickets, setTickets] = useState<Ticket[]>(
    demoMode ? ticketsSeed : [],
  );
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const currentUnitId = useCurrentUnitId();
  const location = useLocation();
  const navigate = useNavigate();
  const authRoute =
    location.pathname === "/login" ||
    location.pathname === "/accept-invite" ||
    location.pathname === "/register";
  const handoverRoute = location.pathname === "/accept-handover";
  const publicEventRoute =
    location.pathname === "/events" || location.pathname.startsWith("/events/");
  const publicVisitorRoute = location.pathname === "/visitor-pass";
  const publicMarketplaceRoute =
    location.pathname === "/marketplace/submit" ||
    location.pathname === "/marketplace/submit/verify";
  const publicReservationRoute = location.pathname === "/reservations" || location.pathname === "/reservations/verify" || location.pathname === "/reservations/status";
  const publicGuardIntakeRoute = location.pathname === "/guard-intake";
  const guardRoute = location.pathname === "/admin/guard" || location.pathname === "/maintenance/guard";
  const offlineGuardRoute = location.pathname === "/maintenance/guard";

  useEffect(() => {
    const publicEventDetail = /^\/events\/[^/]+$/.test(location.pathname);
    const pageName =
      location.pathname === "/events"
        ? "Community Events"
        : publicEventDetail
          ? "Community Event"
          : location.pathname === "/login"
            ? "Sign In"
            : location.pathname === "/register"
              ? "Resident Registration"
              : humanStatus(location.pathname.split("/").filter(Boolean).at(-1) ?? "Home");
    document.title = `${pageName} | Two Carats`;

    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      description.content =
        location.pathname === "/events" || publicEventDetail
          ? "Community events hosted by Two Carats property management."
          : "Two Carats property management portal.";
    }

    const indexablePublicEvent = location.pathname === "/events" || publicEventDetail;
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"][data-two-carats]');
    if (indexablePublicEvent) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        canonical.dataset.twoCarats = "true";
        document.head.appendChild(canonical);
      }
      canonical.href = new URL(location.pathname, window.location.origin).href;
    } else {
      canonical?.remove();
    }
    document.querySelector('script[type="application/ld+json"][data-two-carats-event]')?.remove();
  }, [location.pathname]);

  useEffect(() => {
    if (!guardRoute || !("serviceWorker" in navigator)) return;
    const scope = location.pathname === "/admin/guard" ? "/admin/guard" : "/maintenance/guard";
    void navigator.serviceWorker.register("/guard-sw.js", { scope }).catch(() => undefined);
  }, [guardRoute, location.pathname]);

  useEffect(() => {
    if (
      demoMode ||
      authRoute ||
      handoverRoute ||
      publicEventRoute ||
      publicVisitorRoute ||
      publicMarketplaceRoute
      || publicReservationRoute
      || publicGuardIntakeRoute
    )
      return;
    fetch("/api/session", { headers: { Accept: "application/json" } })
      .then(async (response) => {
        const result = (await response.json()) as {
          user?: SessionUser & { accountStatus?: string };
          tenant?: { id?: string };
          error?: string;
        };
        if (response.ok && result.user?.role) {
          if (guardRoute && result.tenant?.id) {
            const localGuardMeta = await getGuardMeta().catch(() => undefined);
            const cachedTenantId = localGuardMeta?.tenantId ?? localGuardMeta?.bundle?.tenantId;
            if (cachedTenantId && cachedTenantId !== result.tenant.id) await clearGuardStorage().catch(() => undefined);
          }
          const role = result.user.role;
          setSessionUser(result.user);
          setPersona(
            role.startsWith("resident_")
              ? "resident"
              : role === "maintenance" || role === "security_desk"
                ? "maintenance"
                : "admin",
          );
          setAuthState(
            result.user.accountStatus === "active"
              ? "authenticated"
              : "registration",
          );
        } else
          setAuthState(
            result.error === "TWO_FACTOR_ENROLLMENT_REQUIRED"
              ? "setup_2fa"
              : "unauthenticated",
          );
      })
      .catch(async () => {
        if (offlineGuardRoute) {
          const meta = await getGuardMeta().catch(() => undefined);
          if (meta?.sessionId && meta.guardUserId) {
            setPersona("maintenance");
            setAuthState("authenticated");
            return;
          }
        }
        setAuthState("unauthenticated");
      });
  }, [authRoute, publicEventRoute, publicVisitorRoute, publicMarketplaceRoute, publicReservationRoute, publicGuardIntakeRoute, guardRoute, offlineGuardRoute]);

  useEffect(() => {
    if (demoMode || authState !== "authenticated") return;
    const loadTickets = async () => {
      let ticketResult: { tickets?: Record<string, unknown>[] };
      if (persona !== "resident") {
        try {
          ticketResult = await apiJson<{ tickets?: Record<string, unknown>[] }>("/api/maintenance/queue");
        } catch {
          ticketResult = await apiJson<{ tickets?: Record<string, unknown>[] }>("/api/tickets");
        }
      } else {
        ticketResult = await apiJson<{ tickets?: Record<string, unknown>[] }>(withCurrentUnit("/api/tickets", currentUnitId));
      }
      {
        setTickets(
          (ticketResult.tickets ?? []).map((ticket) => ({
            id: String(ticket.id),
            number: String(ticket.publicNumber),
            title: String(ticket.title),
            category: String(ticket.category),
            status: String(ticket.status),
            priority: ticket.priority ? String(ticket.priority) : undefined,
            requestType: ticket.requestType ? String(ticket.requestType) : undefined,
            nextAction: ticket.nextAction ? String(ticket.nextAction) : undefined,
            location: String(
              ticket.locationLabel ?? ticket.unitCode ?? "Property",
            ),
            updated: formatDateTime(
              Number(ticket.updatedAt ?? Date.now() / 1000),
            ),
            assignee: ticket.assignedTo ? String(ticket.assignedTo) : undefined,
          })),
        );
      }
    };
    void loadTickets().catch(() => undefined);
  }, [authState, currentUnitId, persona]);

  useEffect(() => {
    if (demoMode || authState !== "authenticated") return;
    apiJson<{ features: Array<{ featureKey: string; enabled: boolean }> }>(
      "/api/features",
    )
      .then((result) =>
        setFeatures(
          Object.fromEntries(
            result.features.map((item) => [item.featureKey, item.enabled]),
          ),
        ),
      )
      .catch(() => undefined);
  }, [authState]);
  useEffect(() => {
    if (demoMode || authState !== "authenticated") return;
    apiJson<{ permissions: string[] }>("/api/access/me")
      .then((result) => setPermissions(new Set(result.permissions)))
      .catch(() => undefined);
  }, [authState]);
  useEffect(() => {
    const update = (event: Event) => {
      const detail = (
        event as CustomEvent<{ featureKey: string; enabled: boolean }>
      ).detail;
      setFeatures((current) => ({
        ...current,
        [detail.featureKey]: detail.enabled,
      }));
    };
    window.addEventListener("tenant-feature-changed", update);
    return () => window.removeEventListener("tenant-feature-changed", update);
  }, []);

  const changePersona = (next: Persona) => {
    localStorage.setItem("two-carats-persona", next);
    setPersona(next);
    navigate(
      next === "admin"
        ? "/admin/overview"
        : next === "resident"
          ? "/resident/home"
          : "/maintenance/tickets",
    );
  };

  if (location.pathname === "/register") return <ResidentRegistration />;
  if (publicEventRoute) return <PublicEvents />;
  if (publicVisitorRoute) return <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Validating visitor pass…</b></div>}><VisitorPass /></Suspense>;
  if (publicMarketplaceRoute) return <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Loading marketplace…</b></div>}><PublicMarketplaceSubmission /></Suspense>;
  if (publicReservationRoute) return <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Loading reservations…</b></div>}><PublicReservations /></Suspense>;
  if (publicGuardIntakeRoute) return <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Loading guest intake…</b></div>}><GuardIntake /></Suspense>;
  if (handoverRoute) return <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Loading handover…</b></div>}><HandoverAcceptance /></Suspense>;
  if (sessionUser?.role === "technical_admin") return <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Loading diagnostics…</b></div>}><TechnicalDiagnostics /></Suspense>;
  if (authRoute)
    return <AuthScreen invite={location.pathname.includes("invite")} />;
  if (authState === "loading")
    return (
      <div className="loading-screen" role="status" aria-live="polite">
        <span className="brand-mark" aria-hidden="true">2C</span>
        <b>Loading Two Carats…</b>
      </div>
    );
  if (authState === "setup_2fa") return <TwoFactorSetup />;
  if (authState === "registration") return <RegistrationWorkspace />;
  if (authState === "unauthenticated") return <Navigate to="/login" replace />;

  return (
    <Shell
      persona={persona}
      onPersona={changePersona}
      features={features}
      permissions={permissions}
      sessionUser={sessionUser}
    >
      <Suspense fallback={<div className="loading-screen" role="status" aria-live="polite"><b>Loading workspace…</b></div>}><Routes>
        <Route
          path="/"
          element={
            <Navigate
              to={`/${persona}/${persona === "admin" ? "overview" : persona === "resident" ? "home" : "home"}`}
              replace
            />
          }
        />
        <Route
          path="/admin/overview"
          element={<AdminOverview tickets={tickets} />}
        />
        <Route path="/admin/registrations" element={<Registrations />} />
        <Route path="/admin/transparency" element={<Transparency admin />} />
        <Route path="/admin/units" element={<UnitMasterlist />} />
        <Route
          path="/admin/tickets"
          element={<Tickets tickets={tickets} setTickets={setTickets} admin maintenanceEnabled={features.maintenance_concierge !== false} />}
        />
        <Route path="/admin/ledger" element={<Ledger admin />} />
        <Route path="/admin/billing-documents" element={<BillingDocuments />} />
        <Route
          path="/admin/billing-operations"
          element={<BillingOperations />}
        />
        <Route path="/admin/payments" element={<Payments admin />} />
        <Route path="/admin/accounting" element={<Accounting />} />
        <Route path="/admin/tax" element={<TaxCompliance />} />
        <Route path="/admin/announcements" element={<Announcements admin />} />
        <Route path="/admin/events" element={<Events admin />} />
        <Route path="/admin/visitors" element={<Visitors admin />} />
        <Route path="/admin/access" element={<AccessDesk />} />
        <Route path="/admin/guard" element={persona === "admin" ? <GuardDesk admin /> : <Navigate to="/maintenance/guard" replace />} />
        <Route path="/admin/permits" element={<Permits admin canManageWorkflow={demoMode || sessionUser?.role === "property_admin" || sessionUser?.role === "super_admin"} />} />
        <Route path="/admin/packages" element={<Deliveries admin />} />
        <Route path="/admin/mobility" element={<Mobility admin />} />
        <Route path="/admin/parking-rounds" element={<ParkingPatrol />} />
        <Route path="/admin/pool" element={<PoolAccess admin />} />
        <Route path="/admin/reservations" element={<Reservations admin />} />
        <Route path="/admin/staff" element={<Navigate to="/admin/staff/directory" replace />} />
        <Route path="/admin/staff/directory" element={<Staff admin section="directory" />} />
        <Route path="/admin/staff/schedules" element={<Staff admin section="schedules" />} />
        <Route path="/admin/staff/leaves" element={<Staff admin section="leaves" />} />
        <Route path="/admin/community" element={<CommunityBoard admin />} />
        <Route path="/admin/marketplace" element={<Marketplace admin canPublishDirect />} />
        <Route path="/admin/imports" element={<Imports />} />
        <Route path="/admin/audit" element={<Audit />} />
        <Route
          path="/admin/operations-settings"
          element={<OperationsSettings />}
        />
        <Route path="/resident/home" element={<ResidentHome canViewFinance={sessionUser?.role !== "resident_occupant"} />} />
        <Route path="/resident/access" element={<AccessWallet />} />
        <Route
          path="/resident/tickets"
          element={<Tickets tickets={tickets} setTickets={setTickets} />}
        />
        <Route path="/resident/transparency" element={<Transparency />} />
        <Route path="/resident/statements" element={<ResidentStatements />} />
        <Route path="/resident/billing" element={<Ledger />} />
        <Route path="/resident/receipts" element={<Payments />} />
        <Route path="/resident/announcements" element={<Announcements />} />
        <Route path="/resident/events" element={<Events />} />
        <Route path="/resident/visitors" element={<Visitors />} />
        <Route path="/resident/permits" element={<Permits />} />
        <Route path="/resident/packages" element={<Deliveries />} />
        <Route path="/resident/mobility" element={<Mobility />} />
        <Route path="/resident/pool" element={<PoolAccess />} />
        <Route path="/resident/reservations" element={<Reservations />} />
        <Route path="/resident/community" element={<CommunityBoard />} />
        <Route path="/resident/marketplace" element={<Marketplace />} />
        <Route
          path="/maintenance/tickets"
          element={<Tickets tickets={tickets} setTickets={setTickets} maintenance maintenanceEnabled={features.maintenance_concierge !== false} canCreate={permissions.has("tickets.create")} />}
        />
        <Route path="/maintenance/home" element={<EmployeeHome tickets={tickets} mode={permissions.has("billing.record") || permissions.has("billing.publish") ? "finance" : permissions.has("registrations.review") ? "resident-services" : "operations"} />} />
        <Route path="/maintenance/registrations" element={<Registrations />} />
        <Route path="/maintenance/transparency" element={<Transparency admin />} />
        <Route path="/maintenance/visitors" element={<Visitors admin />} />
        <Route path="/maintenance/access" element={<AccessDesk />} />
        <Route path="/maintenance/guard" element={<GuardDesk />} />
        <Route path="/maintenance/permits" element={<Permits admin canManageWorkflow={demoMode || sessionUser?.role === "property_admin" || sessionUser?.role === "super_admin"} />} />
        <Route path="/maintenance/packages" element={<Deliveries admin />} />
        <Route path="/maintenance/parking-rounds" element={<ParkingPatrol />} />
        <Route path="/maintenance/pool" element={<PoolAccess admin />} />
        <Route path="/maintenance/reservations" element={<Reservations admin />} />
        <Route path="/maintenance/announcements" element={<Announcements admin canManage={demoMode || permissions.has("announcements.publish")} />} />
        <Route path="/maintenance/events" element={<Events admin canManage={demoMode || permissions.has("events.manage")} />} />
        <Route path="/maintenance/marketplace" element={<Marketplace admin />} />
        <Route path="/maintenance/billing-operations" element={<BillingOperations />} />
        <Route path="/maintenance/billing-documents" element={<BillingDocuments />} />
        <Route path="/maintenance/payments" element={<Payments admin />} />
        <Route path="/maintenance/accounting" element={<Accounting />} />
        <Route path="/maintenance/tax" element={<TaxCompliance />} />
        <Route path="/maintenance/schedule" element={<Staff />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes></Suspense>
    </Shell>
  );
}

let validationMessageCounter = 0;
function markInvalidField(event: FormEvent<HTMLDivElement>) {
  const field = event.target;
  if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;
  const firstInvalid = !field.form?.querySelector('[aria-invalid="true"]');
  field.setAttribute("aria-invalid", "true");
  if (!field.getAttribute("aria-describedby")) {
    const messageId = `form-validation-${++validationMessageCounter}`;
    field.setAttribute("aria-describedby", messageId);
    const message = document.createElement("span"); message.id = messageId; message.className = "form-help form-validation-message"; message.textContent = field.validationMessage || "Please review this field."; field.insertAdjacentElement("afterend", message);
  }
  if (firstInvalid) window.setTimeout(() => { field.focus({ preventScroll: true }); field.scrollIntoView({ behavior: "smooth", block: "center" }); }, 0);
}

function clearInvalidField(event: FormEvent<HTMLDivElement>) {
  const field = event.target;
  if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;
  if (field.checkValidity()) { field.removeAttribute("aria-invalid"); const describedBy = field.getAttribute("aria-describedby"); const message = describedBy ? document.getElementById(describedBy) : null; message?.remove(); field.removeAttribute("aria-describedby"); }
}

function Shell({
  persona,
  onPersona,
  features,
  permissions,
  sessionUser,
  children,
}: {
  persona: Persona;
  onPersona: (persona: Persona) => void;
  features: Record<string, boolean>;
  permissions: Set<string>;
  sessionUser: SessionUser | null;
  children: React.ReactNode;
}) {
  const dialogs = useDialogs();
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [businessMatches, setBusinessMatches] = useState<Array<{ id: string; type: string; title: string; detail: string; href: string }>>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [residentUnits, setResidentUnits] = useState<Array<{ id: string; code: string; buildingName: string; relationshipType?: string }>>([]);
  const [selectedResidentUnit, selectResidentUnit] = useSelectedUnitId(residentUnits.map((unit) => unit.id));
  const location = useLocation();
  const navigate = useNavigate();
  const activeGroup =
    nav[persona].find((group) =>
      group.items.some((item) => item.to === location.pathname),
    )?.id ?? "core";
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(["core", activeGroup]),
  );
  useEffect(() => {
    setExpanded((current) => new Set([...current, activeGroup]));
  }, [activeGroup, persona]);
  useEffect(() => {
    if (!open) {
      document.body.classList.remove("nav-open");
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
      return;
    }

    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : menuButtonRef.current;
    document.body.classList.add("nav-open");
    const focusable = () => Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>("a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])") ?? []).filter((element) => !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true");
    const firstFocusable = focusable()[0];
    firstFocusable?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("nav-open");
    };
  }, [open]);
  const visibleGroups = nav[persona]
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const feature = featureForRoute(item.to);
        const permission = persona === "maintenance" ? permissionForRoute(item.to) : undefined;
        const permitted = !permission || permissions.has(permission) || (item.to === "/maintenance/tickets" && permissions.has("tickets.create")) || (permission === "permits.view_all" && permissions.has("permits.view_approved"));
        const occupantCanView = sessionUser?.role !== "resident_occupant" || feature !== "billing";
        return (!feature || features[feature] !== false) && permitted && occupantCanView;
      }),
    }))
    .filter((group) => group.items.length);
  const demoProfile =
    persona === "admin"
      ? ["Alexis Dela Cruz", "Property administrator"]
      : persona === "resident"
        ? ["Alex Johnson", "Primary resident"]
        : ["Marcus Rivera", "Maintenance"];
  const profileName = sessionUser?.name ?? demoProfile[0];
  const profileRole = sessionUser
    ? humanStatus(sessionUser.role)
    : demoProfile[1];
  const searchMatches = query.trim()
    ? visibleGroups
        .flatMap((group) => group.items)
        .filter((item) =>
          item.label.toLowerCase().includes(query.trim().toLowerCase()),
        )
        .slice(0, 8)
    : [];
  useEffect(() => {
    if (persona !== "admin" || query.trim().length < 2 || demoMode) { setBusinessMatches([]); return; }
    const controller = new AbortController();
    const timer = window.setTimeout(() => { apiJson<{ results: typeof businessMatches }>(`/api/search?q=${encodeURIComponent(query.trim())}`, { signal: controller.signal }).then((result) => setBusinessMatches(result.results)).catch(() => undefined); }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [query, persona]);
  const unread = notifications.filter((item) => !item.readAt).length;
  useEffect(() => {
    if (persona !== "resident" || demoMode) { setResidentUnits([]); return; }
    const timer = window.setTimeout(() => {
      apiJson<{ units: Array<{ id: string; code: string; buildingName: string; relationshipType?: string }> }>("/api/units")
        .then((result) => setResidentUnits(result.units))
        .catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [persona]);
  useEffect(() => {
    if (demoMode) {
      setNotifications([
        {
          id: "demo-notification",
          title: "Billing statement available",
          body: "Your latest statement is ready to review.",
          href:
            persona === "resident"
              ? "/resident/statements"
              : `/${persona}/tickets`,
          createdAt: Math.floor(Date.now() / 1000),
        },
      ]);
      return;
    }
    const timer = window.setTimeout(() => {
      apiJson<{ notifications: AppNotification[] }>("/api/notifications")
        .then((result) => setNotifications(result.notifications))
        .catch(() => undefined);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [persona]);
  const openNotification = async (notification: AppNotification) => {
    if (!notification.readAt) {
      if (!demoMode)
        await fetch(`/api/notifications/${notification.id}/read`, {
          method: "POST",
        }).catch(() => undefined);
      setNotifications((items) =>
        items.map((item) =>
          item.id === notification.id
            ? { ...item, readAt: Math.floor(Date.now() / 1000) }
            : item,
        ),
      );
    }
    setNotificationsOpen(false);
    if (notification.href) navigate(notification.href);
  };
  const markAllRead = async () => {
    if (!demoMode)
      await fetch("/api/notifications/read-all", { method: "POST" }).catch(
        () => undefined,
      );
    setNotifications((items) =>
      items.map((item) => ({
        ...item,
        readAt: item.readAt ?? Math.floor(Date.now() / 1000),
      })),
    );
  };
  const signOut = async () => {
    const guardMeta = await getGuardMeta().catch(() => undefined);
    const ownsGuardSession = Boolean(guardMeta?.sessionId && guardMeta.guardUserId && (!sessionUser || guardMeta.guardUserId === sessionUser.userId));
    if (ownsGuardSession) {
      try { await endGuardShiftForSignOut(); } catch (caught) { await dialogs.notice({ title: "Cannot sign out safely", description: caught instanceof Error && caught.message === "GUARD_QUEUED_EVENTS_REQUIRE_SYNC" ? "Sync the guard queue before signing out." : "The guard shift could not be ended safely. Try again or contact a supervisor.", actionLabel: "Close" }); return; }
    }
    try {
      await fetch("/api/auth/sign-out", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        credentials: "same-origin",
      });
    } finally {
      localStorage.removeItem("two-carats-persona");
      localStorage.removeItem("crysta-persona");
      window.location.assign("/login");
    }
  };
  return (
    <div className="app-shell" onInvalidCapture={markInvalidField} onInputCapture={clearInvalidField}>
      <aside id="primary-navigation" ref={sidebarRef} className={open ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">2C</span>
          <span className="brand-copy">
            <b>Two Carats</b>
            <small>
              {persona === "resident"
                ? "Resident portal"
                : persona === "maintenance"
                  ? "Maintenance workspace"
                  : "Property operations"}
            </small>
          </span>
          <button type="button" className="sidebar-close" aria-label="Close navigation" onClick={() => setOpen(false)}>×</button>
        </div>
        <nav aria-label="Primary navigation">
          {visibleGroups.map((group) => (
            <section className="nav-group" key={group.id}>
              <button
                type="button"
                className="nav-group-toggle"
                aria-expanded={expanded.has(group.id)}
                onClick={() =>
                  setExpanded((current) => {
                    const next = new Set(current);
                    if (next.has(group.id) && group.id !== activeGroup)
                      next.delete(group.id);
                    else next.add(group.id);
                    return next;
                  })
                }
              >
                <span>{group.label}</span>
                <i>{expanded.has(group.id) ? "−" : "+"}</i>
              </button>
              {expanded.has(group.id) && (
                <div className="nav-group-links">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                    >
                      <span>{item.glyph}</span>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </section>
          ))}
        </nav>
        <div className="sidebar-note">
          <b>
            {persona === "maintenance" ? "Operations access" : "Need a hand?"}
          </b>
          <span>
            {persona === "maintenance"
              ? "Assigned tickets and your staff schedule are visible."
              : "Support is available weekdays, 8 AM–6 PM."}
          </span>
        </div>
        <div className="profile">
          <div className="profile-identity">
            <span className="avatar" aria-hidden="true">{initials(profileName)}</span>
            <span>
              <b>{profileName}</b>
              <small>{profileRole}</small>
            </span>
          </div>
          <button
            type="button"
            className="profile-signout"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </aside>
      {open && <div className="sidebar-backdrop" aria-hidden="true" onClick={() => setOpen(false)} />}
      <div className="workspace">
        <header className="topbar">
          <button
            ref={menuButtonRef}
            type="button"
            className="menu"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="primary-navigation"
          >
            ☰
          </button>
          <button
            type="button"
            className="mobile-signout"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
          <div className="shell-search">
            <label className="search">
              <span aria-hidden="true">⌕</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={persona === "admin" ? "Search…" : "Find a page"}
                aria-label="Search Two Carats"
              />
            </label>
            {searchMatches.length > 0 && (
              <div className="search-results">
                {searchMatches.map((item) => (
                  <button
                    key={item.to}
                    onClick={() => {
                      navigate(item.to);
                      setQuery("");
                    }}
                  >
                    <span aria-hidden="true">{item.glyph}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            {businessMatches.length > 0 && searchMatches.length === 0 && <div className="search-results business-search-results">{businessMatches.map((item) => <button key={`${item.type}-${item.id}`} onClick={() => { navigate(item.href); setQuery(""); }}><span>⌕</span><span><b>{item.title}</b><small>{humanStatus(item.type)} · {item.detail}</small></span></button>)}</div>}
          </div>
          <div className="top-actions notification-wrap">
            {persona === "resident" && residentUnits.length > 1 && <label className="unit-context"><span>Current unit</span><select value={selectedResidentUnit} onChange={(event) => selectResidentUnit(event.target.value)}>{residentUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.buildingName} · {unit.code}</option>)}</select></label>}
            <button
              className="icon-button"
              aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
              aria-expanded={notificationsOpen}
              onClick={() => setNotificationsOpen((value) => !value)}
            >
              ♢{unread > 0 && <i />}
            </button>
            {notificationsOpen && (
              <div className="notification-panel">
                <div>
                  <b>Notifications</b>
                  {unread > 0 && (
                    <button onClick={() => void markAllRead()}>
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.map((notification) => (
                  <button
                    className={notification.readAt ? "read" : ""}
                    key={notification.id}
                    onClick={() => void openNotification(notification)}
                  >
                    <b>{notification.title}</b>
                    <span>{notification.body}</span>
                    <small>{formatDateTime(notification.createdAt)}</small>
                  </button>
                ))}
                {!notifications.length && <p>You’re all caught up.</p>}
              </div>
            )}
            {demoMode && (
              <label className="demo-persona">
                <span>Demo view</span>
                <select
                  value={persona}
                  onChange={(event) => onPersona(event.target.value as Persona)}
                >
                  <option value="admin">Admin</option>
                  <option value="resident">Resident</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </label>
            )}
          </div>
        </header>
        <main>{children}</main>
        <FeedbackRegion />
      </div>
    </div>
  );
}

function FeedbackRegion() {
  const [feedback, setFeedback] = useState<{ message: string; tone: "success" | "error" | "info" } | null>(null);
  useEffect(() => {
    let timer = 0;
    const receive = (event: Event) => {
      const detail = (event as CustomEvent<{ message: string; tone: "success" | "error" | "info" }>).detail;
      setFeedback(detail);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setFeedback(null), 4500);
    };
    window.addEventListener("two-carats-feedback", receive);
    return () => { window.removeEventListener("two-carats-feedback", receive); window.clearTimeout(timer); };
  }, []);
  if (!feedback) return null;
  return <div className={`feedback-toast ${feedback.tone}`} role={feedback.tone === "error" ? "alert" : "status"}><span>{feedback.message}</span><button type="button" aria-label="Dismiss message" onClick={() => setFeedback(null)}>×</button></div>;
}

function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

function EmployeeHome({ tickets, mode = "operations" }: { tickets: Ticket[]; mode?: "operations" | "finance" | "resident-services" }) {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [assigned, setAssigned] = useState<Array<{ id: string; publicNumber: string; title: string; status: string; locationLabel?: string }>>([]);
  const [queue, setQueue] = useState<MaintenanceQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [access, dashboard, maintenanceResponse] = await Promise.all([
        apiJson<{ permissions: string[] }>("/api/access/me"),
        apiJson<{ assignedTickets: Array<{ id: string; publicNumber: string; title: string; status: string; locationLabel?: string }>; queue?: MaintenanceQueue }>("/api/dashboard"),
        fetch("/api/maintenance/queue?limit=8"),
      ]);
      setPermissions(new Set(access.permissions));
      setAssigned(dashboard.assignedTickets ?? []);
      if (maintenanceResponse.ok) {
        const maintenance = (await maintenanceResponse.json()) as { tickets?: Array<Record<string, unknown>>; nextCursor?: string | null };
        const items = (maintenance.tickets ?? []).map((ticket) => {
          const risk = String(ticket.breachRisk ?? "unknown");
          const priority = risk === "breached" || ticket.priority === "emergency" ? "urgent" : risk === "high" || ticket.priority === "urgent" ? "high" : "normal";
          return {
            key: `maintenance_ticket:${String(ticket.id)}`,
            kind: "maintenance_ticket",
            title: String(ticket.title ?? ticket.publicNumber ?? "Maintenance ticket"),
            detail: `${String(ticket.publicNumber ?? "")} · ${humanStatus(String(ticket.status ?? "open"))}`,
            status: String(ticket.status ?? "open"),
            priority,
            nextAction: String(ticket.nextAction ?? "Review maintenance ticket"),
            href: `/maintenance/tickets?ticket=${String(ticket.id)}`,
            createdAt: Number(ticket.createdAt ?? 0),
            ...(ticket.riskDeadlineAt == null ? {} : { dueAt: Number(ticket.riskDeadlineAt) }),
          } satisfies MaintenanceQueueItem;
        });
        setQueue({ role: "operations", available: true, items, awaitingAction: items.length, hasMore: Boolean(maintenance.nextCursor) });
      } else {
        setQueue(dashboard.queue ?? null);
      }
    } catch {
      setError("Your workspace could not be loaded. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const modules = [
    { permission: "tickets.view_assigned", title: "Assigned tickets", detail: "Review and progress your active maintenance work", href: "/maintenance/tickets", glyph: "⌑" },
    { permission: "visitors.view", title: "Visitor desk", detail: "Validate arrivals and active passes", href: "/maintenance/visitors", glyph: "⌁" },
    { permission: "pool.view", title: "Pool attendance", detail: "Validate passes and attendance", href: "/maintenance/pool", glyph: "≈" },
    { permission: "permits.view_all", alternate: "permits.view_approved", title: "Permit workspace", detail: "Review or inspect authorized work", href: "/maintenance/permits", glyph: "▧" },
    { permission: "packages.view", title: "Package desk", detail: "Record and release deliveries", href: "/maintenance/packages", glyph: "▥" },
    { permission: "reservations.manage", title: "Amenity reservations", detail: "Review requests and resident cancellations", href: "/maintenance/reservations", glyph: "◫" },
    { permission: "parking.patrol.view", title: "Parking rounds", detail: "Record vehicles, safety issues, and patrol notes", href: "/maintenance/parking-rounds", glyph: "◉" },
    { permission: "billing.view", title: "Finance workspace", detail: permissions.has("billing.publish") ? "Review and publish financial work" : "Prepare financial records", href: permissions.has("billing.publish") ? "/maintenance/billing-documents" : "/maintenance/billing-operations", glyph: "₱" },
    { permission: "tax.view", title: "Tax compliance", detail: "Review shadow tax calculations, draft invoices, and withholding evidence", href: "/maintenance/tax", glyph: "▥" },
    { permission: "registrations.view", title: "Registration approvals", detail: "Review resident access and documents", href: "/maintenance/registrations", glyph: "◎" },
    { permission: "events.manage", title: "Events", detail: "Plan, publish, and review event registrations", href: "/maintenance/events", glyph: "◫" },
    { permission: "announcements.publish", title: "Announcements", detail: "Publish resident-facing updates", href: "/maintenance/announcements", glyph: "◖" },
    { permission: "marketplace.review", title: "Marketplace review", detail: "Review resident and vendor submissions", href: "/maintenance/marketplace", glyph: "▣" },
  ].filter((item) => permissions.has(item.permission) || (item.alternate && permissions.has(item.alternate)));
  const allowedQueueHref = (href: string) => /^\/maintenance\/(?:tickets|registrations|visitors|pool|permits|packages|reservations|marketplace|billing-documents|billing-operations|payments|accounting|tax)(?:[/?#]|$)/.test(href);
  const queueItems = (queue?.items ?? []).filter((item) => allowedQueueHref(item.href));
  const fallbackQueueRole: MaintenanceQueue["role"] = mode === "finance" ? (permissions.has("billing.publish") ? "finance_approver" : "finance_encoder") : mode === "resident-services" ? "resident_services" : "operations";
  const queueRole = queue?.role ?? fallbackQueueRole;
  const queueTitle = queueRole === "finance_approver" ? "Finance workspace" : queueRole === "finance_encoder" ? "Finance preparation" : queueRole === "resident_services" ? "Resident Services workspace" : queueRole === "security_desk" ? "Security desk workspace" : "Today’s work";
  const queueDescription = queueRole === "finance_approver" ? "Review, reconcile, and publish financial work from one focused queue." : queueRole === "finance_encoder" ? "Prepare accurate financial records from one focused queue." : queueRole === "resident_services" ? "Keep resident requests, approvals, and communications moving from one focused queue." : queueRole === "security_desk" ? "Review arrivals, passes, and package custody from one focused queue." : "Your assigned tasks and job-authorized tools in one place.";
  const queueMoreHref = queueRole === "finance_approver"
    ? permissions.has("billing.view") ? "/maintenance/billing-documents" : "/maintenance/home"
    : queueRole === "finance_encoder"
      ? permissions.has("billing.view") ? "/maintenance/billing-operations" : "/maintenance/home"
      : queueRole === "resident_services"
        ? permissions.has("registrations.view") ? "/maintenance/registrations" : (permissions.has("permits.view_all") || permissions.has("permits.view_approved")) ? "/maintenance/permits" : permissions.has("reservations.manage") ? "/maintenance/reservations" : permissions.has("visitors.view") ? "/maintenance/visitors" : permissions.has("pool.view") ? "/maintenance/pool" : permissions.has("packages.view") ? "/maintenance/packages" : permissions.has("marketplace.review") ? "/maintenance/marketplace" : "/maintenance/home"
        : queueRole === "security_desk"
          ? permissions.has("visitors.view") ? "/maintenance/visitors" : permissions.has("pool.view") ? "/maintenance/pool" : permissions.has("packages.view") ? "/maintenance/packages" : "/maintenance/home"
          : permissions.has("tickets.view_assigned") ? "/maintenance/tickets" : "/maintenance/home";
  return <>
    <PageHeader eyebrow="MY WORKSPACE" title={queueTitle} description={queueDescription} action={<button className="button secondary" type="button" onClick={() => void load()} disabled={loading}>{loading ? "Refreshing…" : "Refresh queue"}</button>} />
    {error && <p className="form-error" role="alert">{error}</p>}
    <section className="metric-grid employee-metrics">
      <Metric label="Assigned tickets" value={String(assigned.length || tickets.length)} detail="Currently active" tone="blue" />
      <Metric label="Available tools" value={String(modules.length)} detail="Based on your access profile" tone="violet" />
      <Metric label="Awaiting action" value={loading ? "…" : queue?.available ? String(queue.awaitingAction) : "—"} detail="Prioritized work items" tone="amber" />
    </section>
    <section className="section-block"><div className="section-heading"><div><small>QUICK ACCESS</small><h2>Job tools</h2></div></div>
      <div className="employee-tool-grid">{modules.map((module) => <button className="card employee-tool" key={module.href} onClick={() => navigate(module.href)}><span aria-hidden="true">{module.glyph}</span><div><h3>{module.title}</h3><p>{module.detail}</p></div><b aria-hidden="true">→</b></button>)}{!modules.length && !error && <Empty title="No operational tools assigned" body="Ask a property administrator to review your access profile." />}</div>
    </section>
    <section className="card employee-queue-card">
      <CardTitle title="Priority queue" />
      {!loading && queue && !queue.available && <p className="callout" role="status">The priority queue is temporarily unavailable. Use the job tools above and refresh when ready.</p>}
      {loading && <div className="queue-loading" role="status">Loading your prioritized work…</div>}
      {!loading && queue?.available && <div className="work-queue-list">
        {queueItems.map((item) => <button key={item.key} className={`queue-item priority-${item.priority}`} onClick={() => allowedQueueHref(item.href) && navigate(item.href)}>
          <span className="work-queue-type">{item.kind.replaceAll("_", " ")}</span>
          <span><b>{item.title}</b><small>{item.detail ?? item.status.replaceAll("_", " ")}</small></span>
          <span className={`status ${item.status}`}>{item.nextAction}</span>
          <span aria-hidden="true">→</span>
        </button>)}
        {!queueItems.length && <Empty title="Nothing is waiting" body="Your queue is clear. Open a job tool when you need to review a different area." />}
      </div>}
      {!loading && queue?.available && queue.hasMore && <div className="queue-more"><button className="button secondary" type="button" onClick={() => navigate(queueMoreHref)}>Open full queue</button></div>}
      {!loading && !queue && <div className="work-queue-list"><Empty title="Queue unavailable" body="Refresh this workspace or open a job tool directly." /></div>}
    </section>
  </>;
}

function AdminOverview({ tickets }: { tickets: Ticket[] }) {
  const navigate = useNavigate();
  const [workType, setWorkType] = useState("all");
  const [workAge, setWorkAge] = useState("all");
  const [workQuery, setWorkQuery] = useState("");
  const [dashboard, setDashboard] = useState<AdminDashboardData | null>(
    demoMode
      ? {
          profileName: "Alexis Santos",
          pendingRegistrations: 3,
          readyRegistrations: 2,
          ticketCounts: [
            { status: "open", count: 9 },
            { status: "in_progress", count: 4 },
          ],
          portfolioBalanceCentavos: 46250000,
          activeUnits: 37,
          buildingCount: 3,
          unassignedTickets: 3,
          billedThisMonthCentavos: 141700000,
          collectedThisMonthCentavos: 128400000,
          recentRegistrations: registrationsSeed.map((row) => ({
            id: row.id,
            name: row.name,
            unitLabel: row.unit,
            relationshipType: row.relationship.toLowerCase(),
            documentCount: 2,
            cleanDocumentCount: row.scan === "Clean" ? 2 : 0,
          })),
          emailQueue: { pending: 0, failed: 0 },
          scannerEnabled: true,
          lastBackupAt: Math.floor(Date.now() / 1000),
          workItems: [],
        }
      : null,
  );
  const [error, setError] = useState("");
  useEffect(() => {
    if (demoMode) return;
    apiJson<AdminDashboardData>("/api/dashboard")
      .then(setDashboard)
      .catch(() => setError("The operational overview could not be loaded."));
  }, []);
  if (!dashboard)
    return (
      <>
        <PageHeader
          eyebrow="PROPERTY OPERATIONS"
          title="Overview"
          description="Loading current tenant data."
        />
        {error && <p className="form-error">{error}</p>}
      </>
    );
  const open = dashboard.ticketCounts
    .filter((row) => !["closed", "resolved"].includes(row.status))
    .reduce((total, row) => total + Number(row.count), 0);
  const maximumCollection = Math.max(
    dashboard.billedThisMonthCentavos,
    dashboard.collectedThisMonthCentavos,
    1,
  );
  const emailPending = Number(dashboard.emailQueue.pending);
  const emailFailed = Number(dashboard.emailQueue.failed);
  const today = new Intl.DateTimeFormat("en-PH", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "Asia/Manila",
  })
    .format(new Date())
    .toUpperCase();
  const groupedWorkItems = (() => {
    const groups = new Map<string, AdminDashboardData["workItems"][number] & { occurrences: number }>();
    for (const item of dashboard.workItems) {
      const key = `${item.type}:${item.title}:${item.status}:${item.href}`;
      const existing = groups.get(key);
      if (existing) {
        existing.occurrences += 1;
        existing.createdAt = Math.min(existing.createdAt, item.createdAt);
      } else groups.set(key, { ...item, occurrences: 1 });
    }
    return [...groups.values()];
  })();
  const filteredWorkItems = groupedWorkItems.filter((item) => {
    const ageDays = (Date.now() / 1000 - item.createdAt) / 86400;
    const matchesAge = workAge === "all" || (workAge === "overdue" ? ageDays >= 3 : ageDays < Number(workAge));
    const query = workQuery.trim().toLowerCase();
    return (workType === "all" || item.type === workType) && matchesAge
      && (!query || `${item.title} ${item.detail} ${item.status}`.toLowerCase().includes(query));
  });
  return (
    <>
      <PageHeader
        eyebrow={today}
        title={`Good day, ${dashboard.profileName.split(" ")[0]}`}
        description="Here’s the current operational picture for Two Carats."
      />
      <section className="metric-grid">
        <Metric
          label="Buildings"
          value={String(dashboard.buildingCount)}
          detail={`${dashboard.activeUnits} active units`}
          tone="blue"
        />
        <Metric
          label="Outstanding balance"
          value={formatPeso(dashboard.portfolioBalanceCentavos)}
          detail={`Across ${dashboard.activeUnits} active units`}
          tone="amber"
        />
        <Metric
          label="Open tickets"
          value={String(open)}
          detail={`${dashboard.unassignedTickets} need assignment`}
          tone="red"
        />
        <Metric
          label="Pending registrations"
          value={String(dashboard.pendingRegistrations)}
          detail={`${dashboard.readyRegistrations} ready for approval`}
          tone="violet"
        />
      </section>
      <section className="card admin-work-queue">
        <CardTitle
          title="Action center"
          badge={`${groupedWorkItems.length} requiring attention`}
        />
        {groupedWorkItems.length ? (
          <>
          <div className="work-queue-filters" aria-label="Action center filters">
            <label>Find task<input value={workQuery} onChange={(event) => setWorkQuery(event.target.value)} placeholder="Name, number, or status" /></label>
            <label>Module<select value={workType} onChange={(event) => setWorkType(event.target.value)}><option value="all">All modules</option>{[...new Set(groupedWorkItems.map((item) => item.type))].sort().map((type) => <option key={type} value={type}>{humanStatus(type)}</option>)}</select></label>
            <label>Age<select value={workAge} onChange={(event) => setWorkAge(event.target.value)}><option value="all">Any age</option><option value="1">Last 24 hours</option><option value="3">Last 3 days</option><option value="overdue">Over 3 days</option></select></label>
            {(workQuery || workType !== "all" || workAge !== "all") && <button className="button secondary small" type="button" onClick={() => { setWorkQuery(""); setWorkType("all"); setWorkAge("all"); }}>Clear</button>}
          </div>
          <div className="work-queue-list">
            {filteredWorkItems.map((item) => (
              <button
                key={`${item.type}-${item.id}`}
                onClick={() => navigate(item.href)}
              >
                <span className="work-queue-type">
                  {humanStatus(item.type)}
                </span>
                <span>
                  <b>{item.title}</b>
                  <small>
                    {item.detail} · {formatDateTime(item.createdAt)}
                    {item.occurrences > 1 ? ` · ${item.occurrences} related alerts` : ""}
                  </small>
                </span>
                <Status value={item.status} />
                <span aria-hidden="true">→</span>
              </button>
            ))}
            {!filteredWorkItems.length && <Empty title="No matching tasks" body="Adjust or clear the filters to see the rest of the queue." />}
          </div>
          </>
        ) : (
          <Empty
            title="No work waiting"
            body="Registration, assignment, payment, permit, Marketplace, and scanner exceptions will appear here."
          />
        )}
      </section>
      <section className="dashboard-grid">
        <div className="card span-2">
          <CardTitle title="Active maintenance tickets" link="View all" />
          {tickets.length ? (
            <TicketTable
              tickets={tickets
                .filter(
                  (ticket) => !["closed", "resolved"].includes(ticket.status),
                )
                .slice(0, 4)}
            />
          ) : (
            <Empty
              title="No active tickets"
              body="New maintenance requests will appear here."
            />
          )}
        </div>
        <div
          className="card dashboard-link-card"
          role="link"
          tabIndex={0}
          aria-label={`Open registration approvals, ${dashboard.pendingRegistrations} pending`}
          onClick={() => navigate("/admin/registrations")}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate("/admin/registrations");
            }
          }}
        >
          <CardTitle
            title="Registration queue"
            badge={`${dashboard.pendingRegistrations} pending`}
          />
          {dashboard.recentRegistrations.map((registration) => (
            <div className="person-row" key={registration.id}>
              <span className="avatar small">
                {initials(registration.name)}
              </span>
              <span>
                <b>{registration.name}</b>
                <small>
                  {registration.unitLabel ?? "Unit not linked"} ·{" "}
                  {humanStatus(registration.relationshipType)}
                </small>
              </span>
              <Status
                value={
                  Number(registration.documentCount) >= 2 &&
                  Number(registration.cleanDocumentCount) >= 2
                    ? "Clean"
                    : "Processing"
                }
              />
            </div>
          ))}
          {!dashboard.recentRegistrations.length && (
            <p className="empty-inline">No registrations awaiting review.</p>
          )}
        </div>
        <div className="card span-2">
          <CardTitle title="This month’s collections" link="Open ledgers" />
          <div
            className="bar-chart collection-bars"
            aria-label="This month billed and collected"
          >
            <i
              className="billed"
              style={{
                height: `${Math.max(4, (dashboard.billedThisMonthCentavos / maximumCollection) * 100)}%`,
              }}
            />
            <i
              className="collected"
              style={{
                height: `${Math.max(4, (dashboard.collectedThisMonthCentavos / maximumCollection) * 100)}%`,
              }}
            />
          </div>
          <div className="chart-legend">
            <span>
              <i className="dot gray" />
              Billed {formatPeso(dashboard.billedThisMonthCentavos)}
            </span>
            <span>
              <i className="dot teal" />
              Collected {formatPeso(dashboard.collectedThisMonthCentavos)}
            </span>
          </div>
        </div>
        <div className="card">
          <CardTitle title="System status" />
          <Health
            label="Email outbox"
            value={
              emailFailed
                ? `${emailFailed} failed`
                : emailPending
                  ? `${emailPending} pending`
                  : "Clear"
            }
          />
          <Health
            label="Document scanner"
            value={dashboard.scannerEnabled ? "Enabled" : "Disabled on staging"}
          />
          <Health
            label="Last backup"
            value={
              dashboard.lastBackupAt
                ? formatDateTime(dashboard.lastBackupAt)
                : "Not recorded"
            }
          />
          <Health label="Database" value="Connected" />
        </div>
      </section>
    </>
  );
}

function ResidentHome({ canViewFinance = true }: { canViewFinance?: boolean }) {
  const navigate = useNavigate();
  const currentUnitId = useCurrentUnitId();
  const [dashboard, setDashboard] = useState<ResidentDashboardData | null>(
    demoMode
      ? {
          profileName: "Alex Rivera",
          units: [
            {
              id: "unit-t3-3012b",
              code: "3012B",
              floor: "30",
              buildingName: "Tower 3",
              relationshipType: "owner",
              balanceCentavos: 462500,
            },
          ],
          tickets: ticketsSeed
            .filter((ticket) => ticket.location.includes("3012B"))
            .map((ticket) => ({
              id: ticket.id,
              publicNumber: ticket.number,
              title: ticket.title,
              status: ticket.status,
              locationLabel: ticket.location,
              updatedAt: Math.floor(Date.now() / 1000),
              category: ticket.category,
            })),
          announcements: announcementsSeed.map((notice) => ({
            id: notice.id,
            title: notice.title,
            body: notice.body,
            publishedAt: Math.floor(Date.now() / 1000),
          })),
          unreadNotifications: 2,
        }
      : null,
  );
  const [error, setError] = useState("");
  useEffect(() => {
    if (demoMode) return;
    let active = true;
    apiJson<ResidentDashboardData>(withCurrentUnit("/api/dashboard", currentUnitId))
      .then((result) => { if (active) setDashboard(result); })
      .catch(() => { if (active) setError("Your home overview could not be loaded."); });
    return () => { active = false; };
  }, [currentUnitId]);
  if (!dashboard)
    return (
      <>
        <PageHeader
          eyebrow="RESIDENT PORTAL"
          title="Home"
          description="Loading your current account information."
        />
        {error && <p className="form-error">{error}</p>}
      </>
    );
  const tickets = dashboard.tickets.map((ticket) => ({
    id: ticket.id,
    number: ticket.publicNumber,
    title: ticket.title,
    category: ticket.category,
    status: ticket.status,
    location: ticket.locationLabel ?? "Property",
    updated: formatDateTime(ticket.updatedAt),
  }));
  const openTickets = tickets.filter(
    (ticket) => !["closed", "resolved"].includes(ticket.status),
  );
  const totalBalance = dashboard.units.reduce(
    (total, unit) => total + Number(unit.balanceCentavos),
    0,
  );
  const primaryUnit = dashboard.units[0];
  return (
    <>
      <PageHeader
        eyebrow="RESIDENT PORTAL"
        title={`Welcome home, ${dashboard.profileName.split(" ")[0]}`}
        description={
          primaryUnit
            ? `Your current information for ${primaryUnit.buildingName} · Unit ${primaryUnit.code}.`
            : "Your resident account is active, but no unit is linked yet."
        }
        action={
          <button
            className="button"
            onClick={() => navigate("/resident/tickets")}
          >
            + New ticket
          </button>
        }
      />
      <section className="metric-grid resident-metrics">
        {canViewFinance && <Metric
          label="Current balance"
          value={formatPeso(totalBalance)}
          detail={`Across ${dashboard.units.length} linked ${dashboard.units.length === 1 ? "unit" : "units"}`}
          tone="amber"
        />}
        <Metric
          label="Open requests"
          value={String(openTickets.length)}
          detail={
            openTickets[0]
              ? `${openTickets[0].category} · ${humanStatus(openTickets[0].status)}`
              : "No open maintenance requests"
          }
          tone="blue"
        />
        <Metric
          label="Unread notifications"
          value={String(dashboard.unreadNotifications)}
          detail={
            dashboard.unreadNotifications
              ? "Open notifications to review"
              : "You’re all caught up"
          }
          tone="violet"
        />
      </section>
      <section className="dashboard-grid">
        <div className="card span-2">
          <CardTitle
            title={dashboard.units.length === 1 ? "My unit" : "My units"}
            badge={`${dashboard.units.length} linked`}
          />
          {dashboard.units.map((unit) => (
            <div className="unit-card" key={unit.id}>
              <span className="building-icon" aria-hidden="true">▦</span>
              <div>
                <h2>Unit {unit.code}</h2>
                <p>
                  {unit.buildingName}
                  {unit.floor ? ` · ${unit.floor}th floor` : ""} ·{" "}
                  {humanStatus(unit.relationshipType)}
                </p>
              </div>
              <span className="verified">✓ Active</span>
            </div>
          ))}
          {!dashboard.units.length && (
            <p className="empty-inline">
              Contact the management office to link your unit.
            </p>
          )}
        </div>
        <div className="card">
          <CardTitle title="Quick actions" />
          <div className="quick-grid">
            <button onClick={() => navigate("/resident/tickets")}>
              <span aria-hidden="true">⌑</span><span>New ticket</span>
            </button>
            {canViewFinance && <button onClick={() => navigate("/resident/billing")}>
              <span aria-hidden="true">₱</span><span>View ledger</span>
            </button>}
            <button onClick={() => navigate("/resident/announcements")}>
              <span aria-hidden="true">◖</span><span>Notices</span>
            </button>
            {canViewFinance && <button onClick={() => navigate("/resident/statements")}>
              <span aria-hidden="true">▤</span><span>Statements</span>
            </button>}
          </div>
        </div>
        <div className="card span-2">
          <CardTitle title="Recent support tickets" link="View all" />
          {tickets.length ? (
            <TicketTable tickets={tickets.slice(0, 3)} />
          ) : (
            <Empty
              title="No support tickets"
              body="Your submitted maintenance requests will appear here."
            />
          )}
        </div>
        <div className="card">
          <CardTitle title="Announcements" />
          {dashboard.announcements.slice(0, 2).map((notice) => (
            <article className="notice-mini" key={notice.id}>
              <small>{formatDateTime(notice.publishedAt)}</small>
              <b>{notice.title}</b>
              <p>{notice.body}</p>
            </article>
          ))}
          {!dashboard.announcements.length && (
            <p className="empty-inline">No announcements for your buildings.</p>
          )}
        </div>
      </section>
      <PropertyGuide />
    </>
  );
}

function PropertyGuide() {
  const [facilities, setFacilities] = useState<PropertyFacility[]>(
    demoMode ? propertyFacilitiesSeed : [],
  );
  useEffect(() => {
    if (demoMode) return;
    apiJson<{ facilities: PropertyFacility[] }>("/api/property/guide")
      .then((result) => setFacilities(result.facilities))
      .catch(() => undefined);
  }, []);
  return (
    <section className="card property-guide">
      <CardTitle title="Property guide" badge="Resident access" />
      <div className="facility-grid">
        {facilities.map((facility) => (
          <article key={facility.id}>
            <span aria-hidden="true">
              {facility.facilityType === "swimming_pool"
                ? "≈"
                : facility.facilityType === "gym"
                  ? "◆"
                  : facility.facilityType === "admin_office"
                    ? "◎"
                    : "▦"}
            </span>
            <div>
              <b>{facility.name}</b>
              <p>{facility.locationDescription}</p>
              {facility.accessInstructions && (
                <small>{facility.accessInstructions}</small>
              )}
              {facility.requiresAccessLog === 1 && (
                <em>Name, unit, and signature required</em>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: string;
}) {
  return (
    <div className={`metric ${tone}`}>
      <span className="metric-icon" aria-hidden="true">◆</span>
      <small>{label}</small>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}
function CardTitle({
  title,
  link,
  to,
  badge,
}: {
  title: string;
  link?: string;
  to?: string;
  badge?: string;
}) {
  const navigate = useNavigate();
  const destination =
    to ??
    (title === "Active maintenance tickets"
      ? "/admin/tickets"
      : title === "This month’s collections"
        ? "/admin/ledger"
        : title === "Recent support tickets"
          ? "/resident/tickets"
          : undefined);
  return (
    <div className="card-title">
      <h2>{title}</h2>
      {link && (
        <button
          className="card-title-action"
          onClick={() => destination && navigate(destination)}
          disabled={!destination}
        >
          {link} →
        </button>
      )}
      {badge && <span>{badge}</span>}
    </div>
  );
}
function Health({ label, value }: { label: string; value: string }) {
  return (
    <div className="health">
      <span>{label}</span>
      <b>
        <i />
        {value}
      </b>
    </div>
  );
}

function Tickets({
  tickets,
  setTickets,
  admin = false,
  maintenance = false,
  maintenanceEnabled = true,
  canCreate = false,
}: {
  tickets: Ticket[];
  setTickets: React.Dispatch<React.SetStateAction<Ticket[]>>;
  admin?: boolean;
  maintenance?: boolean;
  maintenanceEnabled?: boolean;
  canCreate?: boolean;
}) {
  const dialogs = useDialogs();
  const { has } = usePermissions();
  const location = useLocation();
  const canTransition = admin || !maintenance || demoMode || has("tickets.transition");
  const canTriage = admin || demoMode || has("maintenance.triage");
  const [selectedId, setSelectedId] = useState(() => new URLSearchParams(window.location.search).get("ticket") ?? tickets[0]?.id);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [options, setOptions] = useState<TicketOptions>({
    categories: [],
    units: [],
    buildings: [],
    assignees: [],
  });
  const [statusFilter, setStatusFilter] = useState("active");
  const [statusFilters, setStatusFilters] = useState<TicketStatusFilter[]>(["active"]);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const ticketListRef = useRef<HTMLDivElement>(null);
  const ticketDetailRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const statusFilterButtonRef = useRef<HTMLButtonElement>(null);
  const [action, setAction] = useState<{
    nextStatus: string;
    title: string;
    prompt: string;
  } | null>(null);
  const [schedulingVisit, setSchedulingVisit] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("ticket");
    setSelectedId((current) => requested && tickets.some((ticket) => ticket.id === requested) ? requested : current ?? tickets[0]?.id);
  }, [location.search, tickets]);
  const [stagingReviewAvailable, setStagingReviewAvailable] = useState(
    demoMode,
  );
  const isQueueFilter = admin || maintenance;
  const visibleTickets = tickets.filter((ticket) => {
    if (isQueueFilter) {
      if (!statusFilters.length) return true;
      if (statusFilters.includes("active")) return !["closed", "cancelled"].includes(ticket.status);
      return statusFilters.includes(ticket.status as TicketStatusFilter);
    }
    return (
      statusFilter === "all" ||
      (statusFilter === "active"
        ? !["closed", "cancelled"].includes(ticket.status)
        : ticket.status === statusFilter)
    );
  });
  const selected =
    visibleTickets.find((ticket) => ticket.id === selectedId) ?? visibleTickets[0];
  const statusFilterLabel = isQueueFilter
    ? statusFilters.length === 0
      ? "All"
      : statusFilters.includes("active")
        ? "Active"
        : statusFilters.length > 2
          ? `${statusFilters.length} statuses`
          : statusFilters
            .map((value) => ticketStatusOptions.find((option) => option.value === value)?.label ?? value)
            .join(" + ")
    : residentTicketStatusOptions.find((option) => option.value === statusFilter)?.label ?? statusFilter;

  const scrollToMobileTicketSurface = useCallback((surface: "list" | "detail") => {
    if (!window.matchMedia("(max-width: 1024px)").matches) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.requestAnimationFrame(() => {
      (surface === "list" ? ticketListRef.current : ticketDetailRef.current)?.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
    });
  }, []);

  const selectTicket = (ticketId: string) => {
    setSelectedId(ticketId);
    scrollToMobileTicketSurface("detail");
  };

  useEffect(() => {
    if (!statusMenuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!statusFilterRef.current?.contains(event.target as Node)) setStatusMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setStatusMenuOpen(false);
        statusFilterButtonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [statusMenuOpen]);

  useEffect(() => {
    const nextSelectedId = visibleTickets[0]?.id;
    if (selectedId && visibleTickets.some((ticket) => ticket.id === selectedId)) return;
    if (selectedId !== nextSelectedId) setSelectedId(nextSelectedId);
    if (!nextSelectedId) setDetail(null);
  }, [selectedId, visibleTickets]);

  const toggleStatusFilter = (value: TicketStatusFilter) => {
    if (value === "active") {
      setStatusFilters(["active"]);
      return;
    }
    setStatusFilters((current) => {
      const explicit = current.filter((item) => item !== "active");
      return explicit.includes(value)
        ? explicit.filter((item) => item !== value)
        : [...explicit, value];
    });
  };

  const loadDetail = async (ticket: Ticket) => {
    if (demoMode) {
      setDetail({
        id: ticket.id,
        publicNumber: ticket.number,
        title: ticket.title,
        description:
          "Water is dripping continuously. Please inspect the affected area.",
        status: ticket.status,
        category: ticket.category,
        locationLabel: ticket.location,
        locationType: "unit",
        assignedTo: ticket.assignee,
        createdBy: "Alex Rivera",
        createdAt: Math.floor(Date.now() / 1000) - 7200,
        updatedAt: Math.floor(Date.now() / 1000),
        reopenCount: ticket.status === "reopened" ? 1 : 0,
        events: [
          {
            id: `${ticket.id}-created`,
            toStatus: "open",
            comment: "Ticket submitted",
            createdAt: Math.floor(Date.now() / 1000) - 7200,
            actorName: "Alex Rivera",
          },
        ],
        attachments: [],
      });
      return;
    }
    try {
      const result = await apiJson<{
        ticket: TicketDetail;
        stagingReviewAvailable: boolean;
      }>(`/api/tickets/${ticket.id}`);
      setDetail(result.ticket);
      setStagingReviewAvailable(result.stagingReviewAvailable);
    } catch {
      setDetail(null);
      setError("The ticket details could not be loaded.");
    }
  };

  useEffect(() => {
    if (!selectedId && tickets[0]) setSelectedId(tickets[0].id);
  }, [tickets, selectedId]);
  useEffect(() => {
    if (demoMode) {
      setOptions({
        categories: [
          { id: "category-plumbing", name: "Plumbing" },
          { id: "category-electrical", name: "Electrical" },
          { id: "category-climate", name: "Climate" },
          { id: "category-general", name: "General" },
        ],
        units: [
          { id: "unit-t3-3012b", code: "3012B", buildingId: "building-t3", buildingName: "Tower 3" },
        ],
        buildings: [{ id: "building-t3", name: "Tower 3" }],
        assignees: [{ userId: "maintenance-marcus", name: "Marcus Rivera" }],
      });
      return;
    }
    apiJson<TicketOptions>("/api/tickets/options")
      .then(setOptions)
      .catch(() => setError("Ticket options could not be loaded."));
  }, []);
  useEffect(() => {
    if (selected) void loadDetail(selected);
  }, [selected?.id]);
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("ticket");
    if (requested && detail?.id === requested) scrollToMobileTicketSurface("detail");
  }, [detail?.id, location.search, scrollToMobileTicketSurface]);

  const triage = async () => {
    if (!selected || !detail) return;
    const nextAction = (await dialogs.input({ title: "Triage maintenance request", label: "Next action", initialValue: detail.nextAction ?? "Inspect and schedule service", minLength: 2, confirmLabel: "Save triage" }))?.trim();
    if (!nextAction) return;
    const body = { priority: detail.priority ?? "routine", requestType: detail.requestType ?? "other", nextAction };
    if (!demoMode) {
      const response = await fetch(`/api/tickets/${selected.id}/triage`, { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify(body) });
      if (!response.ok) { setError("The ticket could not be triaged."); return; }
    }
    setTickets((all) => all.map((ticket) => ticket.id === selected.id ? { ...ticket, status: "triaged", nextAction, updated: "Just now" } : ticket));
    await loadDetail({ ...selected, status: "triaged", nextAction });
  };

  const transition = async (comment: string) => {
    if (!selected || !action) return;
    setError("");
    if (!demoMode) {
      const isConfirmation = action.nextStatus === "closed" && !admin && !maintenance;
      const isClosureOverride = action.nextStatus === "closed" && admin;
      const resolutionEventId = detail?.resolutionEventId ?? detail?.events.find((event) => event.toStatus === "resolved")?.id;
      const response = await fetch(`/api/tickets/${selected.id}/${isConfirmation ? "confirm" : isClosureOverride ? "closure-override" : "transition"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(isConfirmation || isClosureOverride ? { resolutionEventId, ...(isClosureOverride ? { reason: comment } : { comment: comment || undefined }), expectedVersion: detail?.version } : { nextStatus: action.nextStatus, comment: comment || undefined, expectedVersion: detail?.version }),
      });
      if (!response.ok) {
        setError("The status change could not be saved.");
        return;
      }
    }
    setTickets((all) =>
      all.map((ticket) =>
        ticket.id === selected.id
          ? { ...ticket, status: action.nextStatus, updated: "Just now" }
          : ticket,
      ),
    );
    setAction(null);
    await loadDetail({ ...selected, status: action.nextStatus });
  };
  const submitFeedback = async () => {
    if (!selected || !detail) return;
    const score = Number(await dialogs.input({ title: "Rate the resolution", label: "Score from 1 to 5", initialValue: "5", inputType: "number", confirmLabel: "Continue" }));
    if (!Number.isInteger(score) || score < 1 || score > 5) return;
    const comment = (await dialogs.reason({ title: "Share feedback", label: "Optional comment", confirmLabel: "Submit feedback" }))?.trim();
    const resolutionEventId = detail.resolutionEventId ?? detail.events.find((event) => event.toStatus === "resolved")?.id;
    if (!resolutionEventId) { setError("The resolution event is not available for feedback yet."); return; }
    if (!demoMode) {
      const response = await fetch(`/api/tickets/${selected.id}/feedback`, { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify({ resolutionEventId, score, comment: comment || undefined, expectedVersion: detail.version }) });
      if (!response.ok) { setError("Feedback could not be saved."); return; }
    }
  };
  const confirmAppointment = async () => {
    if (!selected || !detail?.currentAppointmentId || demoMode) return;
    const response = await fetch(`/api/tickets/${selected.id}/appointments/${detail.currentAppointmentId}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The appointment confirmation could not be saved."); return; }
    await loadDetail(selected);
  };
  const changePause = async (nextStatus: "awaiting_parts" | "awaiting_resident" | "awaiting_vendor" | "in_progress") => {
    if (!selected || !detail || demoMode) return;
    const reason = (await dialogs.reason({ title: nextStatus === "in_progress" ? "Resume maintenance work" : "Pause maintenance work", label: "Reason", confirmLabel: nextStatus === "in_progress" ? "Resume" : "Pause" }))?.trim();
    if (!reason) return;
    const endpoint = nextStatus === "in_progress" ? "resume" : "pause";
    const response = await fetch(`/api/tickets/${selected.id}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ status: nextStatus, reason, expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The maintenance pause change could not be saved."); return; }
    setTickets((all) => all.map((ticket) => ticket.id === selected.id ? { ...ticket, status: nextStatus, updated: "Just now" } : ticket));
    await loadDetail({ ...selected, status: nextStatus });
  };
  const addEstimate = async () => {
    if (!selected || !detail || demoMode) return;
    const amountText = (await dialogs.input({ title: "Add maintenance estimate", label: "Amount in PHP", inputType: "number", confirmLabel: "Continue" }))?.trim();
    const amountCentavos = amountText ? Math.round(Number(amountText) * 100) : NaN;
    if (!Number.isSafeInteger(amountCentavos) || amountCentavos < 0) { setError("Enter a valid estimate amount."); return; }
    const description = (await dialogs.input({ title: "Estimate description", label: "What does this estimate cover?", minLength: 3, confirmLabel: "Submit estimate" }))?.trim();
    if (!description) return;
    const response = await fetch(`/api/tickets/${selected.id}/estimates`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ amountCentavos, description, expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The estimate could not be saved."); return; }
    await loadDetail(selected);
  };
  const approveEstimate = async (estimateId: string) => {
    if (!selected || !detail || demoMode) return;
    const comment = (await dialogs.reason({ title: "Approve estimate", label: "Approval note", confirmLabel: "Approve" }))?.trim();
    if (!comment) return;
    const response = await fetch(`/api/maintenance/estimates/${estimateId}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ comment, expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The estimate could not be approved."); return; }
    await loadDetail(selected);
  };
  const createIncident = async () => {
    if (!selected || !detail || demoMode) return;
    const safetyMessage = (await dialogs.reason({ title: "Open emergency incident", label: "Safety message", confirmLabel: "Open incident" }))?.trim();
    if (!safetyMessage) return;
    const response = await fetch(`/api/tickets/${selected.id}/incident`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ incidentType: "other", severity: "critical", safetyMessage, expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The emergency incident could not be opened."); return; }
    await loadDetail(selected);
  };
  const transitionIncident = async (incidentId: string, status: "acknowledged" | "mitigated" | "closed") => {
    if (!selected || !detail || demoMode) return;
    const reason = (await dialogs.reason({ title: `Mark incident ${status}`, label: "Reason", confirmLabel: "Save" }))?.trim();
    if (!reason) return;
    const response = await fetch(`/api/maintenance/incidents/${incidentId}/transition`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ status, reason, expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The emergency incident could not be updated."); return; }
    await loadDetail(selected);
  };
  const createWarranty = async () => {
    if (!selected || !detail || demoMode) return;
    const dateText = (await dialogs.input({ title: "Record warranty case", label: "Warranty end date (YYYY-MM-DD)", confirmLabel: "Continue" }))?.trim();
    const warrantyUntil = dateText ? Math.floor(Date.parse(`${dateText}T23:59:59+08:00`) / 1000) : NaN;
    if (!Number.isSafeInteger(warrantyUntil) || warrantyUntil <= Math.floor(Date.now() / 1000)) { setError("Enter a future warranty end date."); return; }
    const note = (await dialogs.input({ title: "Warranty note", label: "Optional note", confirmLabel: "Save warranty" }))?.trim();
    const response = await fetch(`/api/tickets/${selected.id}/warranty`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ warrantyUntil, note: note || undefined, expectedVersion: detail.version }),
    });
    if (!response.ok) { setError("The warranty case could not be saved."); return; }
    await loadDetail(selected);
  };
  const assign = async (userId: string) => {
    if (!selected || !userId) return;
    const assignee = options.assignees.find((item) => item.userId === userId);
    if (!demoMode) {
      const response = await fetch(`/api/tickets/${selected.id}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ userId }),
      });
      if (!response.ok) {
        setError("The ticket could not be assigned.");
        return;
      }
    }
    setTickets((all) =>
      all.map((ticket) =>
        ticket.id === selected.id
          ? { ...ticket, assignee: assignee?.name, updated: "Just now" }
          : ticket,
      ),
    );
    await loadDetail({ ...selected, assignee: assignee?.name });
  };
  const scheduleVisit = async (schedule: {
    workerUserId: string;
    scheduledVisitStart: number;
    scheduledVisitEnd: number;
    note?: string;
  }) => {
    if (!selected) return;
    setError("");
    if (!demoMode) {
      const response = await fetch(detail?.currentAssignmentId ? `/api/tickets/${selected.id}/appointments` : `/api/tickets/${selected.id}/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(detail?.currentAssignmentId ? { assignmentId: detail.currentAssignmentId, appointmentId: detail.currentAppointmentId, startsAt: schedule.scheduledVisitStart, endsAt: schedule.scheduledVisitEnd, note: schedule.note } : schedule),
      });
      if (!response.ok) {
        setError("The service visit could not be scheduled.");
        return;
      }
    }
    const assignee = options.assignees.find((item) => item.userId === schedule.workerUserId);
    setTickets((all) => all.map((ticket) =>
      ticket.id === selected.id ? { ...ticket, assignee: assignee?.name, updated: "Just now" } : ticket,
    ));
    setSchedulingVisit(false);
    await loadDetail({ ...selected, assignee: assignee?.name });
  };
  const reviewAttachment = async (documentId: string) => {
    if (!selected || !(await dialogs.confirm({ title: "Review staging attachment", description: "Mark this synthetic attachment as manually reviewed for scanner-disabled staging?", confirmLabel: "Mark reviewed" })))
      return;
    const response = await fetch(
      `/api/documents/${documentId}/staging-review`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason:
            "Manual administrator review of synthetic ticket attachment in scanner-disabled staging",
        }),
      },
    );
    if (!response.ok) {
      setError("The staging attachment review could not be recorded.");
      return;
    }
    await loadDetail(selected);
  };
  return (
    <>
      <PageHeader
        eyebrow={
          maintenance
            ? "MAINTENANCE WORKSPACE"
            : admin
              ? "OPERATIONS"
              : "SUPPORT"
        }
        title={
          maintenance
            ? "Assigned tickets"
            : admin
              ? "Tickets & requests"
              : "My tickets"
        }
        description={
          maintenance
            ? "Your active work queue—nothing outside assigned maintenance."
            : "Track requests from report through resolution."
        }
        action={
          (!maintenance || canCreate) && (
            <button className="button" onClick={() => setShowForm(true)}>
              + New ticket
            </button>
          )
        }
      />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {showForm && (
        <TicketForm
          options={options}
          onClose={() => setShowForm(false)}
          onCreate={(ticket) => {
            setTickets((all) => [ticket, ...all]);
            selectTicket(ticket.id);
            setShowForm(false);
          }}
        />
      )}
      {action && canTransition && (
        <TicketTransitionDialog
          action={action}
          onClose={() => setAction(null)}
          onSubmit={transition}
        />
      )}
      {schedulingVisit && canTransition && (
        <TicketVisitDialog
          assignees={options.assignees}
          currentWorkerUserId={detail?.scheduledWorkerUserId ?? detail?.assignedToUserId}
          onClose={() => setSchedulingVisit(false)}
          onSubmit={scheduleVisit}
        />
      )}
      <section className="split-panel">
        <div className="ticket-list" ref={ticketListRef}>
          <div className={isQueueFilter ? "panel-tools ticket-filter-tools" : "panel-tools"}>
            <b>REQUESTS ({visibleTickets.length})</b>
            {isQueueFilter ? (
              <div className="ticket-status-filter" ref={statusFilterRef}>
                <button
                  ref={statusFilterButtonRef}
                  type="button"
                  className="ticket-status-filter-trigger"
                  aria-expanded={statusMenuOpen}
                  aria-controls="ticket-status-filter-options"
                  aria-haspopup="true"
                  onClick={() => setStatusMenuOpen((open) => !open)}
                >
                  <span>Status: {statusFilterLabel}</span>
                  <span aria-hidden="true">⌄</span>
                </button>
                {statusMenuOpen && (
                  <div
                    id="ticket-status-filter-options"
                    className="ticket-status-filter-menu"
                    role="group"
                    aria-label="Ticket status filters"
                  >
                    <label className="ticket-status-option">
                      <input
                        type="checkbox"
                        checked={statusFilters.includes("active")}
                        onChange={() => toggleStatusFilter("active")}
                      />
                      <span>
                        <b>Active</b>
                        <small>All except closed</small>
                      </span>
                    </label>
                    {ticketStatusOptions.map((option) => (
                      <label className="ticket-status-option" key={option.value}>
                        <input
                          type="checkbox"
                          checked={statusFilters.includes(option.value)}
                          onChange={() => toggleStatusFilter(option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                    <div className="ticket-status-filter-footer">
                      <span>{statusFilters.length ? `${statusFilters.length} selected` : "All statuses"}</span>
                      <button
                        type="button"
                        className="table-action"
                        disabled={!statusFilters.length}
                        onClick={() => setStatusFilters([])}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="ticket-status-filter" ref={statusFilterRef}>
                <button
                  ref={statusFilterButtonRef}
                  type="button"
                  className="ticket-status-filter-trigger"
                  aria-expanded={statusMenuOpen}
                  aria-controls="resident-ticket-status-filter-options"
                  aria-haspopup="true"
                  onClick={() => setStatusMenuOpen((open) => !open)}
                >
                  <span>Status: {statusFilterLabel}</span>
                  <span aria-hidden="true">⌄</span>
                </button>
                {statusMenuOpen && (
                  <div
                    id="resident-ticket-status-filter-options"
                    className="ticket-status-filter-menu"
                    role="group"
                    aria-label="Ticket status filters"
                  >
                    {residentTicketStatusOptions.map((option) => (
                      <label className="ticket-status-option" key={option.value}>
                        <input
                          type="radio"
                          name="resident-ticket-status-filter"
                          value={option.value}
                          checked={statusFilter === option.value}
                          onChange={() => {
                            setStatusFilter(option.value);
                            setStatusMenuOpen(false);
                          }}
                        />
                        <span>
                          <b>{option.label}</b>
                          {option.description && <small>{option.description}</small>}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="status-hint"><b>Resolved</b> means work is complete and may still be accepted or reopened. <b>Closed</b> is final; a new issue is required.</p>
          {visibleTickets.map((ticket) => (
            <button
              key={ticket.id}
              className={
                ticket.id === selected?.id
                  ? "ticket-row selected"
                  : "ticket-row"
              }
              onClick={() => selectTicket(ticket.id)}
            >
              <span>
                <Status value={ticket.status} label={ticket.status === "open" ? "Submitted" : undefined} />
                <small>{ticket.updated}</small>
              </span>
              <b>{ticket.title}</b>
              <p>
                {ticket.location} · {ticket.category}
              </p>
              {ticket.priority && <small>{humanStatus(ticket.priority)} priority{ticket.nextAction ? ` · ${ticket.nextAction}` : ""}</small>}
              {ticket.assignee && <small>Assigned to {ticket.assignee}</small>}
            </button>
          ))}
          {!visibleTickets.length && (
            <Empty
              title="No matching tickets"
              body="There are no requests in this status."
            />
          )}
        </div>
        <div className="ticket-detail" ref={ticketDetailRef}>
          <button
            type="button"
            className="button secondary mobile-master-back"
            onClick={() => scrollToMobileTicketSurface("list")}
          >
            Back to requests
          </button>
          {selected && detail ? (
            <>
              <div className="detail-head">
                <div>
                  <span className="eyebrow">{detail.publicNumber}</span>
                  <h2>{detail.title}</h2>
                  <p>
                    {detail.locationLabel} · {detail.category}
                  </p>
                  <div className="ticket-meta-line">
                    {detail.priority && <Status value={detail.priority} />}
                    {detail.breachRisk && <span>SLA risk: {humanStatus(detail.breachRisk)}</span>}
                    {detail.nextAction && <span>Next: {detail.nextAction}</span>}
                  </div>
                </div>
                <div className="ticket-actions">
                  {(admin || (maintenance && canTransition)) && detail.status !== "closed" && (
                    <button className="button secondary" onClick={() => setSchedulingVisit(true)}>
                      {detail.scheduledVisitStart ? "Reschedule visit" : "Schedule visit"}
                    </button>
                  )}
                  {canTriage && detail.status === "open" && (
                    <button className="button" onClick={() => void triage()}>
                      Triage request
                    </button>
                  )}
                  {(admin || (maintenance && canTransition)) &&
                    ["triaged", "reopened"].includes(detail.status) && (
                      <button
                        className="button"
                        onClick={() =>
                          setAction({
                            nextStatus: "in_progress",
                            title:
                              detail.status === "reopened"
                                ? "Resume work"
                                : "Start work",
                            prompt: "Add an optional work note.",
                          })
                        }
                      >
                          {detail.status === "reopened"
                            ? "Resume work"
                            : "Start work"}
                      </button>
                    )}
                  {(admin || (maintenance && canTransition)) &&
                    detail.status === "in_progress" && (
                      <button
                        className="button"
                        onClick={() =>
                          setAction({
                            nextStatus: "resolved",
                            title: "Resolve ticket",
                            prompt:
                              "Describe the work completed. This is required.",
                          })
                        }
                      >
                        Resolve
                      </button>
                    )}
                  {maintenanceEnabled && (admin || (maintenance && canTransition)) && ["scheduled", "in_progress"].includes(detail.status) && (
                    <>
                      <button className="button secondary" onClick={() => void changePause("awaiting_parts")}>Awaiting parts</button>
                      <button className="button secondary" onClick={() => void changePause("awaiting_resident")}>Awaiting resident</button>
                      <button className="button secondary" onClick={() => void changePause("awaiting_vendor")}>Awaiting vendor</button>
                    </>
                  )}
                  {maintenanceEnabled && (admin || (maintenance && canTransition)) && ["awaiting_parts", "awaiting_resident", "awaiting_vendor"].includes(detail.status) && (
                    <button className="button secondary" onClick={() => void changePause("in_progress")}>Resume work</button>
                  )}
                  {admin && detail.status === "resolved" && (
                    <button
                      className="button secondary"
                      onClick={() =>
                        setAction({
                          nextStatus: "closed",
                          title: "Close ticket",
                          prompt:
                            "Explain why this request is being closed. This is required.",
                        })
                      }
                    >
                      Close ticket
                    </button>
                  )}
                  {!admin && !maintenance && detail.status === "resolved" && (
                    <>
                      <button
                        className="button"
                        onClick={() =>
                          setAction({
                            nextStatus: "closed",
                            title: "Accept resolution",
                            prompt:
                              "Confirm that the work is complete. You may add a note.",
                          })
                        }
                      >
                        Accept & close
                      </button>
                      {detail.reopenCount < 1 && (
                        <button
                          className="button secondary"
                          onClick={() =>
                            setAction({
                              nextStatus: "reopened",
                              title: "Reopen ticket",
                              prompt:
                                "Explain what remains unresolved. This is required.",
                            })
                          }
                        >
                          Reopen
                        </button>
                      )}
                    </>
                  )}
                  {!admin && !maintenance && ["resolved", "closed"].includes(detail.status) && (
                    <button className="button secondary" onClick={() => void submitFeedback()}>Leave feedback</button>
                  )}
                </div>
              </div>
              {admin && detail.status !== "closed" && (
                <label className="ticket-assignee">
                  Assigned maintenance
                  <select
                    value={detail.assignedToUserId ?? ""}
                    onChange={(event) => void assign(event.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {options.assignees.map((item) => (
                      <option key={item.userId} value={item.userId}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {detail.scheduledVisitStart && detail.scheduledVisitEnd && (
                <section className="card ticket-visit-card" aria-label="Scheduled service visit">
                  <div>
                    <span className="eyebrow">SCHEDULED SERVICE VISIT</span>
                    <h3>{formatDateTime(detail.scheduledVisitStart)}</h3>
                    <p>
                      Until {formatDateTime(detail.scheduledVisitEnd)}
                      {detail.scheduledWorkerName ? ` · ${detail.scheduledWorkerName}` : ""}
                    </p>
                    {detail.scheduledVisitNote && <small>{detail.scheduledVisitNote}</small>}
                    {detail.scheduledVisitEnd < Math.floor(Date.now() / 1000) && !["resolved", "closed"].includes(detail.status) && <p className="form-error">This service visit is overdue. Update the ticket or reschedule it.</p>}
                    {!admin && !maintenance && detail.currentAppointmentId && !detail.appointmentResidentConfirmedAt && <button className="button secondary" onClick={() => void confirmAppointment()}>Confirm appointment</button>}
                  </div>
                  <Status value="confirmed" />
                </section>
              )}
              {maintenanceEnabled && (admin || maintenance) && (
                <section className="card maintenance-control-card" aria-label="Maintenance controls">
                  <CardTitle title="Maintenance controls" />
                  {(detail.estimates?.length ?? 0) > 0 && (
                    <div className="maintenance-control-section">
                      <b>Estimates</b>
                      {detail.estimates?.map((estimate) => (
                        <div className="maintenance-control-row" key={estimate.id}>
                          <span>
                            Revision {estimate.revision} · {formatPeso(estimate.amountCentavos)} · {humanStatus(estimate.status)}
                            <small>{estimate.description}</small>
                          </span>
                          {estimate.status === "submitted" && (admin || has("maintenance.estimate.approve")) && (
                            <button className="table-action" type="button" onClick={() => void approveEstimate(estimate.id)}>Approve</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {(admin || has("maintenance.cost.record") || has("maintenance.triage")) && !["closed", "cancelled"].includes(detail.status) && (
                    <button className="button secondary" type="button" onClick={() => void addEstimate()}>Add estimate</button>
                  )}
                  {detail.priority === "emergency" && (admin || has("maintenance.emergency.manage")) && (detail.incidents?.length ?? 0) === 0 && (
                    <button className="button secondary" type="button" onClick={() => void createIncident()}>Open emergency incident</button>
                  )}
                  {(detail.incidents?.length ?? 0) > 0 && (
                    <div className="maintenance-control-section">
                      <b>Emergency incidents</b>
                      {detail.incidents?.map((incident) => (
                        <div className="maintenance-control-row" key={incident.id}>
                          <span>
                            {humanStatus(incident.severity)} · {humanStatus(incident.status)}
                            <small>{incident.safetyMessage}</small>
                          </span>
                          {incident.status !== "closed" && (admin || has("maintenance.emergency.manage")) && (
                            <button className="table-action" type="button" onClick={() => void transitionIncident(incident.id, incident.status === "open" ? "acknowledged" : incident.status === "acknowledged" ? "mitigated" : "closed")}>
                              {incident.status === "open" ? "Acknowledge" : incident.status === "acknowledged" ? "Mark mitigated" : "Close incident"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {(detail.warrantyCases?.length ?? 0) > 0 && (
                    <div className="maintenance-control-section">
                      <b>Warranty</b>
                      {detail.warrantyCases?.map((warranty) => (
                        <div className="maintenance-control-row" key={warranty.id}>
                          <span>{humanStatus(warranty.status)} until {formatDateTime(warranty.warrantyUntil)}<small>{warranty.note ?? "No note"}</small></span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(admin || has("maintenance.triage")) && !["closed", "cancelled"].includes(detail.status) && (
                    <button className="button secondary" type="button" onClick={() => void createWarranty()}>Record warranty case</button>
                  )}
                </section>
              )}
              <div className="status-track">
                {["Submitted", "Triaged", "Scheduled", "In progress", "Resolved", "Closed"].map(
                  (step, index) => (
                    <div
                      key={step}
                      className={
                        index <=
                        ["open", "triaged", "scheduled", "in_progress", "resolved", "closed"].indexOf(detail.status === "reopened" ? "in_progress" : detail.status)
                          ? "done"
                          : ""
                      }
                    >
                      <i>{index <= ["open", "triaged", "scheduled", "in_progress", "resolved", "closed"].indexOf(detail.status === "reopened" ? "in_progress" : detail.status) ? "✓" : "○"}</i>
                      <span>{step}</span>
                    </div>
                  ),
                )}
              </div>
              <div className="detail-grid">
                <div className="card">
                  <CardTitle title="Request details" />
                  <dl>
                    <dt>Reported</dt>
                    <dd>
                      {formatDateTime(detail.createdAt)} by {detail.createdBy}
                    </dd>
                    <dt>Description</dt>
                    <dd>{detail.description}</dd>
                    <dt>Attachments</dt>
                    <dd>
                      {detail.attachments.length
                        ? detail.attachments.map((attachment) =>
                            attachment.fileUrl ? (
                              <a
                                className="attachment"
                                href={attachment.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                key={attachment.id}
                              >
                                {attachment.contentType.startsWith("image/") ? (
                                  <img className="ticket-attachment-preview" src={attachment.fileUrl} alt={`Preview of ${attachment.originalName}`} width="4" height="3" loading="lazy" decoding="async" />
                                ) : "▧ "}
                                <span>{attachment.originalName}</span>
                              </a>
                            ) : (
                              <span className="attachment" key={attachment.id}>
                                ▧ {attachment.originalName} ·{" "}
                                {humanStatus(attachment.status)}
                              </span>
                            ),
                          )
                        : "No attachments"}
                    </dd>
                  </dl>
                </div>
                <div className="card">
                  <CardTitle title="Activity" />
                  <div className="timeline">
                    {detail.events.map((event) => (
                      <p key={event.id}>
                        <i />
                        {event.toStatus === "open" ? "Submitted" : humanStatus(event.toStatus)}
                        {event.comment ? ` — ${event.comment}` : ""}
                        <small>
                          {event.actorName ?? "System"} ·{" "}
                          {formatDateTime(event.createdAt)}
                        </small>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Empty
              title="Select a ticket"
              body="Choose a request to see its history."
            />
          )}
        </div>
      </section>
    </>
  );
}

function TicketForm({
  options,
  onClose,
  onCreate,
}: {
  options: TicketOptions;
  onClose: () => void;
  onCreate: (ticket: Ticket) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [previews, setPreviews] = useState<Array<{ name: string; url?: string; image: boolean }>>([]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const categoryId = String(data.get("categoryId"));
    const category =
      options.categories.find((item) => item.id === categoryId)?.name ??
      "General";
    const priority = String(data.get("priority") || "routine");
    const emergencyCallback = String(data.get("emergencyCallback") || "").trim();
    if (priority === "emergency" && !emergencyCallback) {
      setError("Add an emergency callback number for emergency requests.");
      setSubmitting(false);
      return;
    }
    const preferredAccessStartValue = String(data.get("preferredAccessStart") || "");
    const preferredAccessEndValue = String(data.get("preferredAccessEnd") || "");
    const parsePreferredAccess = (value: string) => {
      if (!value) return undefined;
      const parsed = Date.parse(`${value}:00+08:00`);
      return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : undefined;
    };
    const unitId = String(data.get("unitId") || "");
    const unit = options.units.find((item) => item.id === unitId);
    const locationLabel = unit
      ? `${unit.buildingName} · Unit ${unit.code}`
      : String(data.get("locationLabel") ?? "").trim();
    if (!locationLabel || locationLabel.length < 2) {
      setError("Add a common-area location, or select a unit.");
      setSubmitting(false);
      return;
    }
    const draft = {
      id: crypto.randomUUID(),
      number: `TK-${Math.floor(5000 + Math.random() * 4000)}`,
      title: String(data.get("title")),
      category,
      status: "open",
      location: locationLabel,
      updated: "Just now",
    } satisfies Ticket;
    if (demoMode) return onCreate(draft);
    try {
      const files = data
        .getAll("attachments")
        .filter(
          (value): value is File => value instanceof File && value.size > 0,
        );
      const attachmentDocumentIds: string[] = [];
      for (const file of files) {
        const upload = new FormData();
        upload.set("type", "ticket_attachment");
        upload.set("file", file);
        const response = await fetch("/api/documents", {
          method: "POST",
          body: upload,
        });
        const result = (await response.json()) as { document?: { id: string } };
        if (!response.ok || !result.document)
          throw new Error("ATTACHMENT_UPLOAD_FAILED");
        attachmentDocumentIds.push(result.document.id);
      }
      const response = await fetch("/api/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          unitId: unitId || undefined,
          buildingId: unit?.buildingId || String(data.get("buildingId") || "") || undefined,
          categoryId,
          locationType: unit ? "unit" : "common_area",
          locationLabel,
          title: draft.title,
          description: String(data.get("description")),
          requestType: String(data.get("requestType") || "other"),
          priority,
          permissionToEnter: data.get("permissionToEnter") === "on",
          preferredAccessStart: parsePreferredAccess(preferredAccessStartValue),
          preferredAccessEnd: parsePreferredAccess(preferredAccessEndValue),
          emergencyCallback: emergencyCallback || undefined,
          equipmentContext: String(data.get("equipmentContext") || "").trim() || undefined,
          attachmentDocumentIds,
        }),
      });
      const result = (await response.json()) as {
        ticket?: { id: string; publicNumber: string; status: string };
      };
      if (!response.ok || !result.ticket)
        throw new Error("TICKET_CREATE_FAILED");
      onCreate({
        ...draft,
        id: result.ticket.id,
        number: result.ticket.publicNumber,
        status: result.ticket.status,
      });
    } catch {
      setError(
        "The ticket could not be submitted. Check the attachments and try again.",
      );
      setSubmitting(false);
    }
  };
  return (
    <ModalSurface title="Report a maintenance issue" onClose={onClose} busy={submitting}>
      <form onSubmit={submit}>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <label>
          Issue title
          <input
            name="title"
            required
            minLength={5}
            placeholder="e.g. Kitchen sink is leaking"
          />
        </label>
        <label>
          Category
          <select name="categoryId" required>
            <option value="">Select a category</option>
            {options.categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Request type
          <select name="requestType" defaultValue="other">
            <option value="other">Other</option>
            <option value="plumbing">Plumbing</option>
            <option value="electrical">Electrical</option>
            <option value="air_conditioning">Air conditioning</option>
            <option value="elevator">Elevator</option>
            <option value="generator_pump">Generator / pump</option>
            <option value="fire_life_safety">Fire / life safety</option>
            <option value="cleaning_pest_control">Cleaning / pest control</option>
            <option value="common_area_damage">Common-area damage</option>
            <option value="security_access">Security / access</option>
          </select>
        </label>
        <label>
          Priority
          <select name="priority" defaultValue="routine">
            <option value="routine">Routine</option>
            <option value="urgent">Urgent</option>
            <option value="emergency">Emergency</option>
            <option value="planned">Planned</option>
          </select>
        </label>
        <label className="checkbox-row">
          <input name="permissionToEnter" type="checkbox" />
          <span>Permission to enter if I am unavailable</span>
        </label>
        <label>
          Emergency callback (for urgent safety issues)
          <input name="emergencyCallback" type="tel" placeholder="+63 9xx xxx xxxx" />
        </label>
        <div className="form-grid two-column">
          <label>
            Preferred entry start
            <input name="preferredAccessStart" type="datetime-local" />
          </label>
          <label>
            Preferred entry end
            <input name="preferredAccessEnd" type="datetime-local" />
          </label>
        </div>
        <label>
          Equipment or asset context (optional)
          <input name="equipmentContext" maxLength={240} placeholder="e.g. Split-type AC in bedroom" />
        </label>
        <label>
          Location
          <select name="unitId" value={selectedUnitId} onChange={(event) => setSelectedUnitId(event.target.value)}>
            <option value="">Common area or shared facility</option>
            {options.units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.buildingName} · Unit {unit.code}
              </option>
            ))}
          </select>
        </label>
        {!selectedUnitId && (
          <label>
            Building
            <select name="buildingId" required>
              <option value="">Select a building</option>
              {options.buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Common-area location
          <input
            name="locationLabel"
            placeholder="e.g. Tower 2 hallway, 12th floor"
            required={!selectedUnitId}
            minLength={selectedUnitId ? undefined : 2}
          />
        </label>
        <label>
          Description
          <textarea
            name="description"
            required
            minLength={10}
            rows={4}
            placeholder="Describe what is happening and where…"
          />
        </label>
        <label className="upload-zone">
          ⇧<b>Add photos or documents</b>
          <span>Up to 5 PDF, JPEG, PNG, HEIC, or HEIF files · 10 MB each</span>
          <input
            name="attachments"
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
            multiple
            hidden
            onChange={(event) => {
              previews.forEach((preview) => preview.url && URL.revokeObjectURL(preview.url));
              setPreviews(Array.from(event.target.files ?? []).map((file) => ({
                name: file.name,
                url: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
                image: file.type.startsWith("image/"),
              })));
            }}
          />
        </label>
        {previews.length > 0 && <div className="upload-previews" aria-label="Selected attachments">{previews.map((preview) => preview.image && preview.url ? <img key={preview.name} src={preview.url} alt={preview.name} /> : <span key={preview.name}>▧ {preview.name}</span>)}</div>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="button" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit ticket"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

function TicketVisitDialog({
  assignees,
  currentWorkerUserId,
  onClose,
  onSubmit,
}: {
  assignees: TicketOptions["assignees"];
  currentWorkerUserId?: string;
  onClose: () => void;
  onSubmit: (schedule: {
    workerUserId: string;
    scheduledVisitStart: number;
    scheduledVisitEnd: number;
    note?: string;
  }) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const localValue = (date: Date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  const start = new Date(Date.now() + 24 * 60 * 60 * 1_000);
  start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1_000);
  return (
    <ModalSurface title="Schedule service visit" onClose={onClose} busy={busy} size="compact">
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          setBusy(true);
          try {
            await onSubmit({
              workerUserId: String(data.get("workerUserId")),
              scheduledVisitStart: Math.floor(new Date(String(data.get("scheduledVisitStart"))).getTime() / 1_000),
              scheduledVisitEnd: Math.floor(new Date(String(data.get("scheduledVisitEnd"))).getTime() / 1_000),
              note: String(data.get("note") || "") || undefined,
            });
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="eyebrow">RESIDENT NOTIFICATION</p>
        <p>The resident and assigned staff member will be notified when you confirm.</p>
        <label>
          Assigned maintenance or housekeeper
          <select name="workerUserId" defaultValue={currentWorkerUserId ?? assignees[0]?.userId ?? ""} required>
            <option value="">Select staff member</option>
            {assignees.map((assignee) => <option key={assignee.userId} value={assignee.userId}>{assignee.name}</option>)}
          </select>
        </label>
        <div className="form-grid two">
          <label>
            Arrival
            <input name="scheduledVisitStart" type="datetime-local" defaultValue={localValue(start)} required />
          </label>
          <label>
            Expected completion
            <input name="scheduledVisitEnd" type="datetime-local" defaultValue={localValue(end)} required />
          </label>
        </div>
        <label>
          Instructions for the resident (optional)
          <textarea name="note" rows={3} maxLength={500} placeholder="e.g. Please ensure an adult is present and secure pets." />
        </label>
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button" disabled={busy || !assignees.length}>{busy ? "Scheduling…" : "Confirm & notify"}</button>
        </div>
      </form>
    </ModalSurface>
  );
}

function TicketTransitionDialog({
  action,
  onClose,
  onSubmit,
}: {
  action: { nextStatus: string; title: string; prompt: string };
  onClose: () => void;
  onSubmit: (comment: string) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const required =
    action.nextStatus === "resolved" ||
    action.nextStatus === "reopened" ||
    (action.nextStatus === "closed" &&
      action.nextStatus === "closed");
  return (
    <ModalSurface title={action.title} onClose={onClose} busy={busy} size="compact">
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          try {
            await onSubmit(String(new FormData(event.currentTarget).get("comment") ?? ""));
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          {action.prompt}
          <textarea
            name="comment"
            rows={4}
            required={required}
            minLength={required ? 3 : undefined}
          />
        </label>
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>{busy ? "Saving…" : "Confirm"}</button>
        </div>
      </form>
    </ModalSurface>
  );
}

function TicketTable({ tickets }: { tickets: Ticket[] }) {
  return (
    <div className="table-wrap responsive-table">
      <table>
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Request</th>
            <th>Status</th>
            <th>Assigned</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => (
            <tr key={ticket.id}>
              <td className="mono" data-label="Ticket">{ticket.number}</td>
              <td data-label="Request">
                <b>{ticket.title}</b>
                <small>
                  {ticket.location} · {ticket.category}
                </small>
              </td>
              <td data-label="Status">
                <Status value={ticket.status} label={ticket.status === "open" ? "Submitted" : undefined} />
              </td>
              <td data-label="Assigned">{ticket.assignee ?? "Unassigned"}</td>
              <td data-label="Updated">{ticket.updated}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Registrations() {
  const dialogs = useDialogs();
  const { has, loaded } = usePermissions();
  const canView = demoMode || has("registrations.view");
  const canReview = demoMode || has("registrations.review");
  const canApprove = demoMode || has("registrations.approve");
  const canInvite = demoMode || has("registrations.invite");
  const [rows, setRows] = useState<RegistrationRow[]>(
    demoMode ? registrationsSeed : [],
  );
  const [selected, setSelected] = useState<RegistrationRow | undefined>(
    rows[0],
  );
  const [documents, setDocuments] = useState<RegistrationReviewDocument[]>([]);
  const [stagingReviewAvailable, setStagingReviewAvailable] = useState(false);
  const [reviewing, setReviewing] = useState("");
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  useEffect(() => {
    if (demoMode || !loaded || !canView) return;
    apiJson<{ registrations?: Record<string, unknown>[] }>("/api/registrations")
      .then((result) => {
        const loaded = (result.registrations ?? [])
          .filter((row) => row.status === "pending")
          .map((row) => {
            const relationships = String(
              row.relationshipTypes ?? row.relationshipType,
            ).split(",");
            const preVerified = row.verificationMode === "pre_verified";
            const requiredDocuments = preVerified
              ? []
              : [
                  "government_id",
                  ...(relationships.some(
                    (value) => value === "owner" || value === "co_owner",
                  )
                    ? ["ownership_proof"]
                    : []),
                  ...(relationships.includes("tenant")
                    ? ["lease_document"]
                    : []),
                ];
            const requiredCount = Number(
              row.requiredDocumentCount ?? requiredDocuments.length,
            );
            return {
              id: String(row.id),
              name: String(row.name),
              email: String(row.email),
              unit: String(row.unitLabel ?? "Unit not linked"),
              relationship: relationships.map(humanStatus).join(", "),
              scan:
                Number(row.cleanDocumentCount) >= requiredCount
                  ? "Clean"
                  : "Processing",
              submitted: formatDateTime(Number(row.createdAt)),
              emailVerified: Boolean(row.emailVerified),
              preVerified,
              requiredDocuments,
            };
          });
        setRows(loaded);
        const requested = new URLSearchParams(window.location.search).get("registration");
        setSelected(loaded.find((row) => row.id === requested) ?? loaded[0]);
      })
      .catch(() => undefined);
  }, [canView, loaded]);
  useEffect(() => {
    if (!selected || (!demoMode && !canReview)) {
      setDocuments([]);
      return;
    }
    if (demoMode) {
      const status = selected.scan === "Clean" ? "clean" : "scan_delayed";
      setDocuments(
        (
          selected.requiredDocuments ?? [
            "government_id",
            selected.relationship === "Tenant"
              ? "lease_document"
              : "ownership_proof",
          ]
        ).map((type) => ({
          id: `${selected.id}-${type}`,
          type,
          originalName: `${type}.pdf`,
          status,
          createdAt: Date.now(),
        })),
      );
      setStagingReviewAvailable(true);
      return;
    }
    setDocuments([]);
    apiJson<{
      documents: RegistrationReviewDocument[];
      stagingReviewAvailable: boolean;
    }>(`/api/registrations/${selected.id}/documents`)
      .then((result) => {
        setDocuments(
          result.documents.filter(
            (document, index, all) =>
              all.findIndex((candidate) => candidate.type === document.type) ===
              index,
          ),
        );
        setStagingReviewAvailable(result.stagingReviewAvailable);
      })
      .catch(() => setError("Registration documents could not be loaded."));
  }, [canReview, selected?.id]);
  const reviewForStaging = async (document: RegistrationReviewDocument) => {
    if (!selected || !(await dialogs.confirm({ title: "Review staging document", description: `Mark ${document.originalName} as manually reviewed for staging? This bypasses malware scanning and must only be used with synthetic test files.`, confirmLabel: "Mark reviewed" })))
      return;
    setReviewing(document.id);
    setError("");
    if (!demoMode) {
      const response = await fetch(
        `/api/registrations/${selected.id}/documents/${document.id}/staging-review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reason: "Manual administrator review in scanner-disabled staging",
          }),
        },
      );
      if (!response.ok) {
        setError("The staging review could not be recorded.");
        setReviewing("");
        return;
      }
    }
    const updated = documents.map((item) =>
      item.id === document.id
        ? { ...item, status: "clean", failureReason: undefined }
        : item,
    );
    setDocuments(updated);
    const requiredDocuments = selected.requiredDocuments ?? [
      "government_id",
      selected.relationship === "Tenant" ? "lease_document" : "ownership_proof",
    ];
    const allClean = requiredDocuments.every((type) =>
      updated.some((item) => item.type === type && item.status === "clean"),
    );
    if (allClean) {
      const cleanSelected = { ...selected, scan: "Clean" };
      setSelected(cleanSelected);
      setRows((all) =>
        all.map((row) => (row.id === selected.id ? cleanSelected : row)),
      );
    }
    setReviewing("");
  };
  const decide = async (approved: boolean) => {
    if (!selected || !canApprove) return;
    if (!demoMode) {
      const response = await fetch(
        `/api/registrations/${selected.id}/decision`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decision: approved ? "approved" : "rejected",
            reason: approved
              ? undefined
              : "Registration documents did not meet requirements",
          }),
        },
      );
      if (!response.ok) {
        setError("The registration decision could not be saved.");
        return;
      }
    }
    const remaining = rows.filter((row) => row.id !== selected.id);
    setRows(remaining);
    setSelected(remaining[0]);
  };
  if (loaded && !demoMode && !canView)
    return <><header className="page-header"><div><small>RESIDENT ONBOARDING</small><h1>Registration approvals</h1><p>This workspace is not included in your assigned permissions.</p></div></header><p className="callout">Ask a property administrator to assign registration-view access if you need this workspace.</p></>;
  return (
    <>
      <PageHeader
        eyebrow="RESIDENT ONBOARDING"
        title="Registration approvals"
        description="Review verified identities and unit relationships before granting access."
        action={
          canInvite && <button className="button" onClick={() => setShowInvite(true)}>
            + Invite resident
          </button>
        }
      />
      {showInvite && canInvite && (
        <InviteResidentModal onClose={() => setShowInvite(false)} />
      )}
      <section className="split-panel registrations">
        <label className="mobile-master-selector">
          Pending registration
          <select
            value={selected?.id ?? ""}
            onChange={(event) => setSelected(rows.find((row) => row.id === event.target.value))}
            disabled={!rows.length}
          >
            {!rows.length && <option value="">No pending registrations</option>}
            {rows.map((row) => (
              <option value={row.id} key={row.id}>
                {row.name} · {row.unit}
              </option>
            ))}
          </select>
        </label>
        <div className="ticket-list">
          <div className="panel-tools">
            <b>PENDING ({rows.length})</b>
            <button>Oldest first</button>
          </div>
          {rows.map((row) => (
            <button
              key={row.id}
              className={
                row.id === selected?.id ? "ticket-row selected" : "ticket-row"
              }
              onClick={() => setSelected(row)}
            >
              <span>
                <Status value={row.scan} />
                <small>{row.submitted}</small>
              </span>
              <b>{row.name}</b>
              <p>
                {row.unit} · {row.relationship}
              </p>
              <small>{row.email}</small>
            </button>
          ))}
        </div>
        <div className="ticket-detail">
          {selected ? (
            <>
              <div className="detail-head">
                <div>
                  <span className="eyebrow">REGISTRATION REVIEW</span>
                  <h2>{selected.name}</h2>
                  <p>{selected.email}</p>
                </div>
                <Status value={selected.scan} />
              </div>
              {stagingReviewAvailable && !selected.preVerified && (
                <p className="callout staging-review-warning">
                  <b>Staging-only review</b> Malware scanning is unavailable.
                  Use this override only with synthetic test documents.
                </p>
              )}
              <div className="review-grid">
                <div className="card">
                  <CardTitle title="Requested relationship" />
                  <dl>
                    <dt>Property</dt>
                    <dd>{selected.unit}</dd>
                    <dt>Relationship</dt>
                    <dd>{selected.relationship}</dd>
                    <dt>Email</dt>
                    <dd>
                      {selected.emailVerified
                        ? "✓ Verified"
                        : "Verification pending"}
                    </dd>
                    <dt>Verification</dt>
                    <dd>
                      {selected.preVerified
                        ? "Verified offline by management"
                        : "Resident documents required"}
                    </dd>
                  </dl>
                </div>
                <div className="card">
                  <CardTitle
                    title={
                      selected.preVerified
                        ? "Management verification"
                        : "Required documents"
                    }
                  />
                  {selected.preVerified ? (
                    <p className="empty-inline">
                      No uploads are required. Property Management verified this
                      resident before sending the invitation.
                    </p>
                  ) : (
                    <>
                      {documents.map((document) => (
                        <div className="document-row" key={document.id}>
                          <span>▧</span>
                          <b>
                            {humanStatus(document.type)}
                            <small>{document.originalName}</small>
                          </b>
                          <div className="document-actions">
                            {document.fileUrl && (
                              <a className="table-action" href={document.fileUrl} target="_blank" rel="noreferrer">
                                Open document
                              </a>
                            )}
                            {!document.fileUrl && <small className="muted-text">Available after clean scan</small>}
                            <Status value={document.status} />
                            {canReview && stagingReviewAvailable &&
                              document.status !== "clean" && (
                                <button
                                  className="table-action"
                                  disabled={Boolean(reviewing)}
                                  onClick={() => reviewForStaging(document)}
                                >
                                  {reviewing === document.id
                                    ? "Recording…"
                                    : "Mark reviewed"}
                                </button>
                              )}
                          </div>
                        </div>
                      ))}
                      {!documents.length && (
                        <p className="empty-inline">
                          No documents uploaded yet.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              {canApprove ? <div className="decision-bar">
                <p>
                  {selected.scan === "Clean" && selected.emailVerified
                    ? selected.preVerified
                      ? "Management verification and email verification are complete. The resident can be approved."
                      : "Email and required documents are verified. This registration can be approved."
                    : selected.preVerified
                      ? "Activation is waiting only for the resident to verify their email."
                      : "Approval is disabled until email and required documents are verified clean."}
                </p>
                <button className="button danger" onClick={() => decide(false)}>
                  Reject
                </button>
                <button
                  className="button"
                  disabled={
                    selected.scan !== "Clean" || !selected.emailVerified
                  }
                  onClick={() => decide(true)}
                >
                  Approve resident
                </button>
              </div> : <p className="callout">Review-only access. Registration approval permission is required to activate or reject residents.</p>}
            </>
          ) : (
            <Empty
              title="Queue complete"
              body="There are no registrations awaiting review."
            />
          )}
        </div>
      </section>
    </>
  );
}

function InviteResidentModal({ onClose }: { onClose: () => void }) {
  const [units, setUnits] = useState<
    Array<{ id: string; code: string; buildingName: string }>
  >([]);
  const [links, setLinks] = useState<
    Array<{
      key: string;
      unitId: string;
      relationshipType: "owner" | "co_owner" | "tenant";
    }>
  >([{ key: crypto.randomUUID(), unitId: "", relationshipType: "owner" }]);
  const [inviteUrl, setInviteUrl] = useState("");
  const [preVerified, setPreVerified] = useState(true);
  const [verificationNote, setVerificationNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (demoMode)
      return setUnits([
        { id: "unit-tower-1-1606", code: "1606", buildingName: "Tower 1" },
        { id: "unit-north-0305", code: "1205", buildingName: "Tower 2" },
        { id: "unit-east-1204", code: "3012B", buildingName: "Tower 3" },
      ]);
    apiJson<{
      units: Array<{ id: string; code: string; buildingName: string }>;
    }>("/api/units")
      .then((result) => setUnits(result.units))
      .catch(() => setError("Units could not be loaded."));
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    if (links.some((link) => !link.unitId)) {
      setError("Select every unit or remove the unfinished row.");
      setBusy(false);
      return;
    }
    if (new Set(links.map((link) => link.unitId)).size !== links.length) {
      setError("The same unit cannot be added twice.");
      setBusy(false);
      return;
    }
    if (demoMode) {
      setInviteUrl(
        "https://two-carats-staging.example/accept-invite#token=one-time-demo-link",
      );
      setBusy(false);
      return;
    }
    const data = new FormData(event.currentTarget);
    const role = links.some((link) => link.relationshipType !== "tenant")
      ? "resident_owner"
      : "resident_tenant";
    const response = await fetch("/api/invitations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: String(data.get("email")),
        role,
        preVerified,
        verificationNote,
        unitLinks: links.map(({ unitId, relationshipType }) => ({
          unitId,
          relationshipType,
        })),
      }),
    });
    const result = (await response.json()) as {
      invitation?: { inviteUrl?: string };
      error?: string;
    };
    if (!response.ok || !result.invitation?.inviteUrl)
      setError(
        result.error === "INVITATION_RESEND_COOLDOWN"
          ? "An invitation was already sent to this email in the last 24 hours."
          : "The invitation could not be created.",
      );
    else setInviteUrl(result.invitation.inviteUrl);
    setBusy(false);
  };

  return (
    <ModalSurface title="Invite resident" onClose={onClose} busy={busy} className="invite-resident-modal">
      <form onSubmit={submit}>
        {inviteUrl ? (
          <div className="invite-result">
            <b>Invitation sent</b>
            <p>
              {preVerified
                ? "Management verification was recorded. After verifying their email, the resident can sign in without uploading documents."
                : "The resident must verify their email and submit the required documents for review."}
            </p>
            <p>
              The link is single-use and expires in seven days. Copy it now for
              SMS or Messenger.
            </p>
            <input readOnly value={inviteUrl} />
            <button
              type="button"
              className="button full"
              onClick={() => navigator.clipboard.writeText(inviteUrl)}
            >
              Copy secure link
            </button>
          </div>
        ) : (
          <>
            <label>
              Email address
              <input name="email" type="email" autoComplete="email" required />
            </label>
            <fieldset className="registration-units">
              <legend>Unit relationships</legend>
              {links.map((link, index) => (
                <div className="invite-unit-row" key={link.key}>
                  <span>Unit {index + 1}</span>
                  <label>
                    Property
                    <select
                      value={link.unitId}
                      onChange={(event) =>
                        setLinks((current) =>
                          current.map((item) =>
                            item.key === link.key
                              ? { ...item, unitId: event.target.value }
                              : item,
                          ),
                        )
                      }
                      required
                    >
                      <option value="">Select a unit</option>
                      {units.map((unit) => (
                        <option value={unit.id} key={unit.id}>
                          {unit.buildingName} · {unit.code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Relationship
                    <select
                      value={link.relationshipType}
                      onChange={(event) =>
                        setLinks((current) =>
                          current.map((item) =>
                            item.key === link.key
                              ? {
                                  ...item,
                                  relationshipType: event.target
                                    .value as typeof link.relationshipType,
                                }
                              : item,
                          ),
                        )
                      }
                    >
                      <option value="owner">Owner</option>
                      <option value="co_owner">Co-owner</option>
                      <option value="tenant">Tenant</option>
                    </select>
                  </label>
                  {links.length > 1 && (
                    <button
                      type="button"
                      className="table-action danger-text"
                      onClick={() =>
                        setLinks((current) =>
                          current.filter((item) => item.key !== link.key),
                        )
                      }
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="button secondary add-unit"
                disabled={links.length >= 10}
                onClick={() =>
                  setLinks((current) => [
                    ...current,
                    {
                      key: crypto.randomUUID(),
                      unitId: "",
                      relationshipType: "owner",
                    },
                  ])
                }
              >
                + Add another unit
              </button>
            </fieldset>
            <label className="checkbox-line invite-verification">
              <input
                type="checkbox"
                checked={preVerified}
                onChange={(event) => setPreVerified(event.target.checked)}
              />
              <span>
                <b>Identity and residency documents verified offline</b>
                <small>
                  Recommended for residents already validated by Property
                  Management. They will not be asked to upload files.
                </small>
              </span>
            </label>
            {preVerified && (
              <label>
                Internal verification note{" "}
                <span className="optional">Optional</span>
                <textarea
                  value={verificationNote}
                  onChange={(event) => setVerificationNote(event.target.value)}
                  maxLength={300}
                  rows={2}
                  placeholder="e.g. Existing owner records checked by the Admin Office"
                />
              </label>
            )}
            {!preVerified && (
              <p className="callout">
                The resident will need to upload a government ID and the
                applicable ownership or lease document.
              </p>
            )}
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <div className="form-actions">
              <button
                type="button"
                className="button secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button className="button" disabled={busy || !units.length}>
                {busy ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </>
        )}
      </form>
    </ModalSurface>
  );
}

function Ledger({ admin = false }: { admin?: boolean }) {
  const location = useLocation();
  const [entries, setEntries] = useState<typeof ledgerSeed>(
    demoMode ? ledgerSeed : [],
  );
  const [units, setUnits] = useState<
    Array<{ id: string; code: string; buildingName: string }>
  >(
    demoMode
      ? [{ id: "unit-east-1204", code: "3012B", buildingName: "Tower 3" }]
      : [],
  );
  const [unitId, setUnitId] = useSelectedUnitId(
    units.map((unit) => unit.id),
  );
  const [unitSearch, setUnitSearch] = useState("");
  const [showEntry, setShowEntry] = useState(false);
  const balance = entries.at(-1)?.balance ?? 0;
  const selectedUnit = units.find((unit) => unit.id === unitId);
  const filteredUnits = units.filter((unit) =>
    `${unit.buildingName} ${unit.code}`.toLowerCase().includes(unitSearch.trim().toLowerCase()),
  );
  const loadLedger = async (selectedUnitId: string) => {
    setUnitId(selectedUnitId);
    if (demoMode) return;
    const ledger = await apiJson<{ entries?: Record<string, unknown>[] }>(
      `/api/ledger/${selectedUnitId}`,
    );
    setEntries(
      (ledger.entries ?? [])
        .reverse()
        .map((entry) => ({
          id: String(entry.id),
          date: String(entry.effectiveDate),
          type: humanStatus(String(entry.type)),
          category: humanStatus(String(entry.category)),
          reference: String(entry.description),
          amount: Number(entry.amountCentavos),
          balance: Number(entry.balanceAfterCentavos),
        })),
    );
  };
  useEffect(() => {
    if (demoMode) return;
    apiJson<{
      units?: Array<{ id: string; code: string; buildingName: string }>;
    }>("/api/units")
      .then((result) => setUnits(result.units ?? []))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (demoMode || units.length === 0) return;
    const requested = new URLSearchParams(location.search).get("unit");
    const stored = localStorage.getItem("two-carats-selected-unit");
    const initial =
      units.find((unit) => unit.id === requested)?.id ??
      units.find((unit) => unit.id === unitId)?.id ??
      units.find((unit) => unit.id === stored)?.id ??
      units[0].id;
    void loadLedger(initial);
  }, [location.search, unitId, units]);
  const latestDate = entries.at(-1)?.date;
  const accountLabel = selectedUnit
    ? `U-${selectedUnit.buildingName.replace(/[^A-Za-z0-9]/g, "").toUpperCase()}-${selectedUnit.code}`
    : "No unit selected";
  return (
    <>
      <PageHeader
        eyebrow="IMMUTABLE UNIT LEDGER"
        title={admin ? "Unit ledgers" : "My ledger"}
        description={
          admin
            ? "Append-only financial records with centavo-accurate running balances."
            : selectedUnit
              ? `Charges and recorded payments for Unit ${selectedUnit.code}.`
              : "No linked unit is available."
        }
        action={
          admin && (
            <button
              className="button"
              disabled={!unitId}
              onClick={() => setShowEntry(true)}
            >
              + Add entry
            </button>
          )
        }
      />
      {units.length > 1 && (
        <section className="card ledger-unit-picker">
          <div className="unit-search">
            <label htmlFor="ledger-unit-search">Find a unit</label>
            <div className="unit-search-row">
              <input id="ledger-unit-search" type="search" placeholder="Search by tower or unit number" value={unitSearch} onChange={(event) => setUnitSearch(event.target.value)} />
              <select aria-label="Select unit" value={unitId} onChange={(event) => void loadLedger(event.target.value)}>
                {filteredUnits.slice(0, 100).map((unit) => <option key={unit.id} value={unit.id}>{unit.buildingName} · {unit.code}</option>)}
              </select>
            </div>
            <small>{filteredUnits.length > 100 ? "Showing the first 100 matches. Refine your search." : `${filteredUnits.length} matching unit${filteredUnits.length === 1 ? "" : "s"}.`}</small>
          </div>
        </section>
      )}
      {showEntry && (
        <LedgerForm
          onClose={() => setShowEntry(false)}
          onCreate={async (amount, type, reference) => {
            let id = crypto.randomUUID();
            let nextBalance = balance + amount;
            if (!demoMode) {
              if (!unitId) throw new Error("UNIT_NOT_SELECTED");
              const response = await fetch(`/api/ledger/${unitId}/entries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  mutationId: crypto.randomUUID(),
                  type: type.toLowerCase(),
                  category: "association_dues",
                  amountCentavos: amount,
                  description: reference,
                  effectiveDate: new Date().toISOString().slice(0, 10),
                }),
              });
              const result = (await response.json()) as {
                entry?: { id: string; balanceAfterCentavos: number };
              };
              if (!response.ok || !result.entry) throw new Error("LEDGER_ENTRY_FAILED");
              id = result.entry.id;
              nextBalance = result.entry.balanceAfterCentavos;
            }
            setEntries((all) => [
              ...all,
              {
                id,
                date: new Date().toISOString().slice(0, 10),
                type,
                category: "Association dues",
                reference,
                amount,
                balance: nextBalance,
              },
            ]);
            setShowEntry(false);
          }}
        />
      )}
      <section className="ledger-summary">
        <div>
          <small>Current balance</small>
          <strong>{formatPeso(balance)}</strong>
          <span>{latestDate ? `As of ${latestDate}` : "No entries yet"}</span>
        </div>
        <div>
          <small>Unit</small>
          <b>
            {selectedUnit
              ? `${selectedUnit.code} · ${selectedUnit.buildingName}`
              : "No linked unit"}
          </b>
          <span>{accountLabel}</span>
          {admin && selectedUnit && <a className="table-action ledger-profile-link" href={`/admin/billing-documents?unitCode=${encodeURIComponent(selectedUnit.code)}&building=${encodeURIComponent(selectedUnit.buildingName)}`}>Edit billing profile</a>}
        </div>
        <div>
          <small>Ledger integrity</small>
          <b className="healthy">● Append-only</b>
          <span>{entries.length} entries</span>
        </div>
      </section>
      <section className="card ledger-card">
        <div className="panel-tools">
          <b>TRANSACTION HISTORY</b>
        </div>
        <div className="table-wrap responsive-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Category</th>
                <th>Reference</th>
                <th>Amount</th>
                <th>Balance after</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td data-label="Date">{entry.date}</td>
                  <td data-label="Type">
                    <Status value={entry.type} />
                  </td>
                  <td data-label="Category">{entry.category}</td>
                  <td data-label="Reference">
                    <b>{entry.reference}</b>
                  </td>
                  <td data-label="Amount" className={entry.amount < 0 ? "credit" : "debit"}>
                    {entry.amount < 0 ? "−" : "+"}
                    {formatPeso(Math.abs(entry.amount))}
                  </td>
                  <td data-label="Balance after">
                    <b>{formatPeso(entry.balance)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

type UnitDirectoryPerson = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  registered: number;
};

type UnitDirectoryRow = {
  id: string;
  code: string;
  floor?: string;
  status: string;
  buildingId: string;
  buildingCode: string;
  buildingName: string;
  owners: UnitDirectoryPerson[];
  residents: UnitDirectoryPerson[];
  tenants: UnitDirectoryPerson[];
};

function UnitMasterlist() {
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const [units, setUnits] = useState<UnitDirectoryRow[]>(
    demoMode
      ? [{
          id: "unit-east-1204",
          code: "3012B",
          floor: "30",
          status: "active",
          buildingId: "tower-3",
          buildingCode: "T3",
          buildingName: "Tower 3",
          owners: [{ id: "owner-1", name: "Alex Johnson", email: "alex@example.com", registered: 1 }],
          residents: [{ id: "resident-1", name: "Jamie Johnson", phone: "+63 917 555 0101", registered: 0 }],
          tenants: [],
        }]
      : [],
  );
  const [query, setQuery] = useState("");
  const [building, setBuilding] = useState("all");
  const [occupancy, setOccupancy] = useState("all");
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState("");
  const [availableBuildings, setAvailableBuildings] = useState<Array<{ id: string; name: string }>>(
    demoMode ? [{ id: "tower-3", name: "Tower 3" }] : [],
  );
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [personForm, setPersonForm] = useState<{ person?: UnitDirectoryPerson; unitId: string; relationshipType: "resident" | "tenant" }>();
  const addUnitButtonRef = useRef<HTMLButtonElement>(null);
  const closeUnitForm = () => {
    setShowUnitForm(false);
    requestAnimationFrame(() => addUnitButtonRef.current?.focus());
  };

  const loadDirectory = async () => {
    if (demoMode) return;
    setError("");
    setLoading(true);
    try {
      const [directory, buildingResult] = await Promise.all([
        apiJson<{ units: UnitDirectoryRow[] }>("/api/unit-directory"),
        apiJson<{ buildings: Array<{ id: string; name: string; status: string }> }>("/api/buildings"),
      ]);
      setUnits(directory.units);
      setAvailableBuildings(buildingResult.buildings.filter((item) => item.status === "active"));
    } catch {
      setError("The unit masterlist could not be loaded.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void loadDirectory();
  }, []);

  const buildings = [...new Map(units.map((unit) => [unit.buildingId, { id: unit.buildingId, name: unit.buildingName }])).values()];
  const normalizedQuery = query.trim().toLowerCase();
  const visibleUnits = units.filter((unit) => {
    const people = [...unit.owners, ...unit.residents, ...unit.tenants];
    const searchable = [
      unit.buildingName,
      unit.buildingCode,
      unit.code,
      unit.floor ?? "",
      ...people.flatMap((person) => [person.name, person.email ?? "", person.phone ?? ""]),
    ].join(" ").toLowerCase();
    const occupancyMatches =
      occupancy === "all" ||
      (occupancy === "owner" && unit.owners.length > 0) ||
      (occupancy === "resident" && unit.residents.length > 0) ||
      (occupancy === "tenant" && unit.tenants.length > 0) ||
      (occupancy === "empty" && people.length === 0);
    return (!normalizedQuery || searchable.includes(normalizedQuery)) &&
      (building === "all" || unit.buildingId === building) &&
      occupancyMatches;
  });

  const removePerson = async (person: UnitDirectoryPerson) => {
    if (!(await dialogs.confirm({ title: "Remove person from unit", description: `Remove ${person.name} from this unit? Their unit access and related financial access will be revoked. The audit history will be preserved.`, confirmLabel: "Remove person", danger: true }))) return;
    const response = await fetch(`/api/unit-directory/people/${encodeURIComponent(person.id)}`, { method: "DELETE" });
    if (!response.ok) {
      setError("The person could not be removed from this unit.");
      return;
    }
    await loadDirectory();
  };

  const renderPeople = (people: UnitDirectoryPerson[], unitId: string, relationshipType: "owner" | "resident" | "tenant") =>
    people.length ? people.map((person) => (
      <div className="directory-person" key={person.id}>
        <div><b>{person.name}</b><span className={person.registered ? "directory-account registered" : "directory-account"}>{person.registered ? "Registered" : "Unregistered"}</span></div>
        {person.email && <small>{person.email}</small>}
        {person.phone && <small>{person.phone}</small>}
        {relationshipType !== "owner" && <div className="directory-person-actions">
          <button type="button" onClick={() => setPersonForm({ person, unitId, relationshipType })}>Edit</button>
          <button type="button" className="danger-link" onClick={() => void removePerson(person)}>Remove</button>
        </div>}
      </div>
    )) : <span className="directory-empty">None recorded</span>;

  return (
    <>
      <PageHeader
        eyebrow="PROPERTY DIRECTORY"
        title="Unit masterlist"
        description="Current legal owners, resident occupants, and tenants for every unit."
        action={<div className="page-header-actions">
          <button className="button secondary" onClick={() => navigate("/admin/imports")}>Import directory</button>
          <button ref={addUnitButtonRef} className="button secondary" disabled={!availableBuildings.length} onClick={() => setShowUnitForm(true)}>+ Add unit</button>
          <button className="button" disabled={!units.length} onClick={() => setPersonForm({ unitId: visibleUnits[0]?.id ?? units[0]?.id ?? "", relationshipType: "resident" })}>+ Add resident or tenant</button>
        </div>}
      />
      {showUnitForm && <UnitCreateModal
        buildings={availableBuildings}
        onClose={closeUnitForm}
        onCreated={async () => { closeUnitForm(); await loadDirectory(); }}
      />}
      {personForm && <DirectoryPersonModal
        units={units}
        initial={personForm}
        onClose={() => setPersonForm(undefined)}
        onSaved={async () => { setPersonForm(undefined); await loadDirectory(); }}
      />}
      <section className="card unit-directory">
        <div className="directory-tools">
          <label className="directory-search">
            <span>Search masterlist</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Unit, building, person, email, or phone" />
          </label>
          <label>
            <span>Building</span>
            <select value={building} onChange={(event) => setBuilding(event.target.value)}>
              <option value="all">All buildings</option>
              {buildings.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>
            <span>Occupancy</span>
            <select value={occupancy} onChange={(event) => setOccupancy(event.target.value)}>
              <option value="all">All units</option>
              <option value="owner">With owners</option>
              <option value="resident">With residents</option>
              <option value="tenant">With tenants</option>
              <option value="empty">No people recorded</option>
            </select>
          </label>
        </div>
        <div className="directory-count">{visibleUnits.length} of {units.length} units</div>
        {loading ? <p className="empty-inline">Loading unit directory…</p> : error ? <p className="callout">{error}</p> : (
          <div className="directory-table-wrap">
            <table className="directory-table responsive-table">
              <thead><tr><th>Unit</th><th>Unit owners</th><th>Residents</th><th>Tenants</th><th>Actions</th></tr></thead>
              <tbody>
                {visibleUnits.map((unit) => (
                  <tr key={unit.id}>
                    <td data-label="Unit"><b>{unit.buildingName} · {unit.code}</b><small>{unit.floor ? `Floor ${unit.floor} · ` : ""}{humanStatus(unit.status)}</small></td>
                    <td data-label="Unit owners">{renderPeople(unit.owners, unit.id, "owner")}</td>
                    <td data-label="Residents">{renderPeople(unit.residents, unit.id, "resident")}</td>
                    <td data-label="Tenants">{renderPeople(unit.tenants, unit.id, "tenant")}</td>
                    <td data-label="Actions"><button className="table-action" onClick={() => navigate(`/admin/ledger?unit=${encodeURIComponent(unit.id)}`)}>View ledger →</button></td>
                  </tr>
                ))}
                {!visibleUnits.length && <tr><td className="directory-no-results" colSpan={5}>No units match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function UnitCreateModal({
  buildings,
  onClose,
  onCreated,
}: {
  buildings: Array<{ id: string; name: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const response = await fetch("/api/units", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        buildingId: String(data.get("buildingId")),
        code: String(data.get("code")),
        floor: String(data.get("floor")),
        status: String(data.get("status")),
      }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error === "UNIT_CODE_EXISTS" ? "That unit code already exists in this building." : "The unit could not be created.");
      setBusy(false);
      return;
    }
    await onCreated();
  };
  return <ModalSurface title="Add unit" onClose={onClose} busy={busy} size="compact">
    <form onSubmit={submit}>
      <label>Building<select name="buildingId" required autoFocus>{buildings.map((building) => <option key={building.id} value={building.id}>{building.name}</option>)}</select></label>
      <label>Unit code<input name="code" maxLength={30} required placeholder="e.g. 3012B" /></label>
      <label>Floor<input name="floor" maxLength={20} placeholder="e.g. 30" /></label>
      <label>Status<select name="status" defaultValue="active"><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
      {error && <p className="form-error">{error}</p>}
      <div className="form-actions"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button><button className="button" disabled={busy}>{busy ? "Creating…" : "Create unit"}</button></div>
    </form>
  </ModalSurface>;
}

function DirectoryPersonModal({
  units,
  initial,
  onClose,
  onSaved,
}: {
  units: UnitDirectoryRow[];
  initial: { person?: UnitDirectoryPerson; unitId: string; relationshipType: "resident" | "tenant" };
  onClose: () => void;
  onSaved: () => void;
}) {
  const registered = Boolean(initial.person?.registered);
  const [email, setEmail] = useState(initial.person?.email ?? "");
  const [inviteToApp, setInviteToApp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const payload = {
      unitId: String(data.get("unitId")),
      relationshipType: String(data.get("relationshipType")),
      name: String(data.get("name")),
      email,
      phone: String(data.get("phone")),
      inviteToApp: initial.person ? undefined : inviteToApp,
      preVerified: initial.person ? undefined : inviteToApp && data.get("preVerified") === "on",
    };
    const response = await fetch(initial.person ? `/api/unit-directory/people/${encodeURIComponent(initial.person.id)}` : "/api/unit-directory/people", {
      method: initial.person ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      const messages: Record<string, string> = {
        DIRECTORY_ASSIGNMENT_EXISTS: "This person is already assigned to that unit.",
        UNIT_ACCESS_EXISTS: "This registered account already has access to the selected unit.",
        DIRECTORY_ACCOUNT_INCOMPATIBLE: "That email belongs to an account that cannot be assigned as a resident.",
        REGISTERED_IDENTITY_MANAGED_BY_ACCOUNT: "Registered names and emails must be changed in account management.",
      };
      setError(messages[result.error ?? ""] ?? "The unit assignment could not be saved.");
      setBusy(false);
      return;
    }
    await onSaved();
  };
  return <ModalSurface title={initial.person ? "Edit unit assignment" : "Add resident or tenant"} onClose={onClose} busy={busy} className="directory-person-modal">
    <form onSubmit={submit}>
      <label>Unit<select name="unitId" defaultValue={initial.unitId} required autoFocus>{units.filter((unit) => unit.status === "active").map((unit) => <option key={unit.id} value={unit.id}>{unit.buildingName} · {unit.code}</option>)}</select></label>
      <label>Relationship<select name="relationshipType" defaultValue={initial.relationshipType} required><option value="resident">Resident</option><option value="tenant">Tenant</option></select></label>
      <label>Name<input name="name" defaultValue={initial.person?.name} minLength={2} maxLength={120} required readOnly={registered} /></label>
      <label>Email<input name="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} readOnly={registered} /></label>
      {registered && <p className="field-note">Name and email are managed by this person’s registered account.</p>}
      <label>Phone<input name="phone" defaultValue={initial.person?.phone} maxLength={40} /></label>
      {!initial.person && email && <label className="check-row"><input type="checkbox" checked={inviteToApp} onChange={(event) => setInviteToApp(event.target.checked)} /> Invite to app</label>}
      {!initial.person && inviteToApp && <label className="check-row"><input type="checkbox" name="preVerified" /> Management verified this person</label>}
      {error && <p className="form-error">{error}</p>}
      <div className="form-actions"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button><button className="button" disabled={busy}>{busy ? "Saving…" : "Save assignment"}</button></div>
    </form>
  </ModalSurface>;
}

type BillingDocumentConfiguration = {
  accounts: Array<{
    id: string;
    customerReferenceNumber: string;
    provider: string;
    effectiveFrom: string;
    effectiveTo?: string;
    status: string;
    unitCode: string;
    buildingName: string;
    responsiblePartyName: string;
    responsiblePartyKind: string;
    taxpayerIdentificationNumber?: string;
    businessStyle?: string;
    billingAddress?: string;
  }>;
  invoiceSeries: Array<{
    id: string;
    code: string;
    invoiceType: string;
    prefix: string;
    nextNumber: number;
    numberWidth: number;
    status: string;
    authorityType?: string;
    authorityReference?: string;
    validFrom?: string;
    validTo?: string;
    templateVersion: string;
  }>;
  contacts: Array<{
    id: string;
    displayName: string;
    roleTitle?: string;
    email: string;
    phoneNumbersJson: string;
    status: string;
    effectiveFrom: string;
  }>;
  paymentInstructions: Array<{
    id: string;
    displayName: string;
    instructionsJson: string;
    status: string;
    effectiveFrom: string;
  }>;
  compliance: {
    officialIssuanceEnabled: boolean;
    approvedSeriesCount: number;
    supplementaryDocumentsEnabled: boolean;
  };
};

const demoBillingDocuments: BillingDocumentConfiguration = {
  accounts: [
    {
      id: "billing-account-1204-owner",
      customerReferenceNumber: "DEMO-300003012B",
      provider: "Two Carats Demo",
      effectiveFrom: "2026-01-01",
      status: "active",
      unitCode: "3012B",
      buildingName: "Tower 3",
      responsiblePartyName: "Alexis Dela Cruz",
      responsiblePartyKind: "owner",
    },
    {
      id: "billing-account-1008-owner",
      customerReferenceNumber: "DEMO-300003012A",
      provider: "Two Carats Demo",
      effectiveFrom: "2026-01-01",
      status: "active",
      unitCode: "3012A",
      buildingName: "Tower 3",
      responsiblePartyName: "Alexis Dela Cruz",
      responsiblePartyKind: "owner",
    },
    {
      id: "billing-account-0305-owner",
      customerReferenceNumber: "DEMO-3000001205",
      provider: "Two Carats Demo",
      effectiveFrom: "2026-01-01",
      status: "active",
      unitCode: "1205",
      buildingName: "Tower 2",
      responsiblePartyName: "Lucia Reyes",
      responsiblePartyKind: "owner",
    },
    {
      id: "billing-account-0305-tenant",
      customerReferenceNumber: "DEMO-UTILITY-1205",
      provider: "Two Carats Demo",
      effectiveFrom: "2026-01-01",
      status: "active",
      unitCode: "1205",
      buildingName: "Tower 2",
      responsiblePartyName: "Maria Reyes",
      responsiblePartyKind: "tenant",
    },
  ],
  invoiceSeries: [
    {
      id: "invoice-series-demo-draft",
      code: "DEMO-NONVAT-DRAFT",
      invoiceType: "non_vat",
      prefix: "DRAFT-",
      nextNumber: 1,
      numberWidth: 8,
      status: "draft",
      templateVersion: "billing-invoice-v1",
    },
  ],
  contacts: [
    {
      id: "billing-contact-demo-v1",
      displayName: "Two Carats Demo Management Office",
      roleTitle: "Resident Services",
      email: "billing@example.invalid",
      phoneNumbersJson: '["+63 2 8000 0000"]',
      status: "active",
      effectiveFrom: "2026-01-01",
    },
  ],
  paymentInstructions: [
    {
      id: "payment-instructions-demo-v1",
      displayName: "Synthetic staging payment options",
      instructionsJson:
        '{"disclaimer":"SYNTHETIC STAGING DATA - do not send payment","options":[{"channel":"Bank bill payment","instruction":"Use the CRN shown on the statement"},{"channel":"E-wallet bills payment","instruction":"Select the demo property and enter the CRN"},{"channel":"Check at admin office","instruction":"No cash payment accepted"}]}',
      status: "active",
      effectiveFrom: "2026-01-01",
    },
  ],
  compliance: {
    officialIssuanceEnabled: false,
    approvedSeriesCount: 0,
    supplementaryDocumentsEnabled: true,
  },
};

function BillingDocuments() {
  const location = useLocation();
  const { has, loaded } = usePermissions(); const canRecord = has("billing.record"); const canView = has("billing.view");
  const [configuration, setConfiguration] =
    useState<BillingDocumentConfiguration | null>(
      demoMode ? demoBillingDocuments : null,
    );
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    demoMode ? "ready" : "loading",
  );
  const [editingAccount, setEditingAccount] =
    useState<BillingDocumentConfiguration["accounts"][number]>();

  useEffect(() => {
    if (demoMode || !loaded || !canView) return;
    apiJson<BillingDocumentConfiguration>(
      "/api/billing/documents/configuration",
    )
      .then((result) => {
        setConfiguration(result);
        const params = new URLSearchParams(location.search);
        const requestedCode = params.get("unitCode");
        const requestedBuilding = params.get("building");
        if (requestedCode) {
          const account = result.accounts.find((item) => item.unitCode === requestedCode && (!requestedBuilding || item.buildingName === requestedBuilding));
          if (account) setEditingAccount(account);
        }
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [loaded, canView, location.search]);

  useEffect(() => {
    if (!configuration) return;
    const params = new URLSearchParams(location.search);
    const requestedCode = params.get("unitCode");
    const requestedBuilding = params.get("building");
    if (!requestedCode) return;
    const account = configuration.accounts.find((item) => item.unitCode === requestedCode && (!requestedBuilding || item.buildingName === requestedBuilding));
    if (account) setEditingAccount(account);
  }, [configuration, location.search]);

  if (!demoMode && loaded && !canView)
    return <><PageHeader eyebrow="FINANCIAL DOCUMENTS" title="Billing documents" description="Your account does not include billing-document access." /><section className="card"><Empty title="Access not available" body="Ask a property administrator to assign billing-document access if this workspace is part of your role." /></section></>;

  if (status === "loading")
    return (
      <>
        <PageHeader
          eyebrow="FINANCIAL DOCUMENTS"
          title="Billing documents"
          description="Loading tenant billing configuration."
        />
        <section className="card">
          <Empty
            title="Loading configuration"
            body="Checking billing accounts, document series, and delivery details."
          />
        </section>
      </>
    );
  if (status === "error" || !configuration)
    return (
      <>
        <PageHeader
          eyebrow="FINANCIAL DOCUMENTS"
          title="Billing documents"
          description="Statements, billing summaries, invoices, and delivery configuration."
        />
        <section className="card">
          <Empty
            title="Configuration unavailable"
            body="The billing configuration could not be loaded. Try again after refreshing the page."
          />
        </section>
      </>
    );

  const activeContact = configuration.contacts.find(
    (item) => item.status === "active",
  );
  const activeInstructions = configuration.paymentInstructions.find(
    (item) => item.status === "active",
  );
  let paymentOptions: Array<{ channel: string; instruction: string }> = [];
  try {
    paymentOptions =
      JSON.parse(activeInstructions?.instructionsJson ?? "{}").options ?? [];
  } catch {
    paymentOptions = [];
  }

  return (
    <>
      <PageHeader
        eyebrow="FINANCIAL DOCUMENTS"
        title="Billing documents"
        description="Configure account references and preview supplementary billing summaries before publication."
      />
      <section className="billing-document-health">
        <div className="card">
          <small>Supplementary documents</small>
          <b className="healthy">● Enabled</b>
          <span>Statements and billing summaries can be previewed.</span>
        </div>
        <div className="card">
          <small>Official invoice issuance</small>
          <b
            className={
              configuration.compliance.officialIssuanceEnabled
                ? "healthy"
                : "billing-blocked"
            }
          >
            {configuration.compliance.officialIssuanceEnabled
              ? "● Enabled"
              : "● Compliance gated"}
          </b>
          <span>
            {configuration.compliance.approvedSeriesCount} approved invoice
            series.
          </span>
        </div>
        <div className="card">
          <small>Billing accounts</small>
          <strong>{configuration.accounts.length}</strong>
          <span>Effective-dated CRNs across owner and tenant accounts.</span>
        </div>
      </section>
      <BillingRuns />
      <section className="card billing-accounts-card">
        <CardTitle
          title="Customer billing accounts"
          badge="Synthetic staging data"
        />
        <div className="table-wrap responsive-table">
          <table>
            <thead>
              <tr>
                <th>Unit</th>
                <th>Responsible party</th>
                <th>CRN</th>
                <th>Effective</th>
                <th>Status</th>
                <th>Document</th>
              </tr>
            </thead>
            <tbody>
              {configuration.accounts.map((account) => (
                <tr key={account.id}>
                  <td data-label="Unit">
                    <b>{account.unitCode}</b>
                    <small>{account.buildingName}</small>
                  </td>
                  <td data-label="Responsible party">
                    <b>{account.responsiblePartyName}</b>
                    <small>{humanStatus(account.responsiblePartyKind)}</small>
                  </td>
                  <td data-label="CRN">
                    <code>{account.customerReferenceNumber}</code>
                  </td>
                  <td data-label="Effective">{account.effectiveFrom}</td>
                  <td data-label="Status">
                    <Status value={account.status} />
                  </td>
                  <td data-label="Document">
                    {canRecord && <button
                      className="table-action"
                      disabled={demoMode}
                      onClick={() => setEditingAccount(account)}
                    >
                      Billing profile
                    </button>}
                    <button
                      className="table-action"
                      disabled={demoMode}
                      onClick={() =>
                        window.open(
                          `/api/billing/documents/preview.pdf?accountId=${encodeURIComponent(account.id)}`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {demoMode ? "Staging only" : "Preview PDF"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="billing-document-grid">
        <div className="card">
          <CardTitle title="Invoice series" />
          {configuration.invoiceSeries.map((series) => (
            <div className="billing-config-row" key={series.id}>
              <span>
                <b>{series.code}</b>
                <small>
                  {series.prefix}
                  {String(series.nextNumber).padStart(series.numberWidth, "0")}
                </small>
              </span>
              <Status value={series.status} />
              <p>
                {series.authorityReference
                  ? `Authority: ${series.authorityReference}`
                  : "No authority reference. Registered issuance is blocked."}
              </p>
            </div>
          ))}
        </div>
        <div className="card">
          <CardTitle title="Billing inquiries" />
          {activeContact ? (
            <dl>
              <dt>Contact</dt>
              <dd>{activeContact.displayName}</dd>
              <dt>Role</dt>
              <dd>{activeContact.roleTitle ?? "-"}</dd>
              <dt>Email</dt>
              <dd>{activeContact.email}</dd>
              <dt>Phone</dt>
              <dd>{JSON.parse(activeContact.phoneNumbersJson).join(", ")}</dd>
            </dl>
          ) : (
            <Empty
              title="No active contact"
              body="Activate a billing contact before publishing documents."
            />
          )}
        </div>
        <div className="card">
          <CardTitle title="Payment instructions" />
          {activeInstructions ? (
            <div className="payment-options">
              <b>{activeInstructions.displayName}</b>
              {paymentOptions.map((option, index) => (
                <p key={`${option.channel}-${index}`}>
                  <span>{index + 1}</span>
                  <strong>{option.channel}</strong>
                  <small>{option.instruction}</small>
                </p>
              ))}
            </div>
          ) : (
            <Empty
              title="No active instructions"
              body="Activate payment instructions before publishing documents."
            />
          )}
        </div>
      </section>
      {editingAccount && (
        <BillingAccountProfileForm
          account={editingAccount}
          onClose={() => setEditingAccount(undefined)}
          onSaved={(profile) => {
            setConfiguration((current) =>
              current
                ? {
                    ...current,
                    accounts: current.accounts.map((account) =>
                      account.id === editingAccount.id
                        ? { ...account, ...profile }
                        : account,
                    ),
                  }
                : current,
            );
            setEditingAccount(undefined);
          }}
        />
      )}
    </>
  );
}

function BillingAccountProfileForm({
  account,
  onClose,
  onSaved,
}: {
  account: BillingDocumentConfiguration["accounts"][number];
  onClose: () => void;
  onSaved: (profile: {
    taxpayerIdentificationNumber?: string;
    businessStyle?: string;
    billingAddress?: string;
  }) => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const profile = {
      taxpayerIdentificationNumber: String(
        values.get("taxpayerIdentificationNumber") ?? "",
      ),
      businessStyle: String(values.get("businessStyle") ?? ""),
      billingAddress: String(values.get("billingAddress") ?? ""),
    };
    setBusy(true);
    setError("");
    try {
      await apiJson(`/api/billing/accounts/${account.id}/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      onSaved(profile);
    } catch {
      setError("The billing profile could not be saved.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModalSurface title={`Billing profile · ${account.unitCode}`} onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        <p className="modal-copy">
          These details appear on billing summaries and are snapshotted when a
          statement is published.
        </p>
        <label>
          Taxpayer identification number
          <input
            name="taxpayerIdentificationNumber"
            defaultValue={account.taxpayerIdentificationNumber}
          />
        </label>
        <label>
          Business style
          <input name="businessStyle" defaultValue={account.businessStyle} />
        </label>
        <label>
          Billing address
          <textarea
            name="billingAddress"
            rows={3}
            defaultValue={account.billingAddress}
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>{busy ? "Saving…" : "Save profile"}</button>
        </div>
      </form>
    </ModalSurface>
  );
}

type BillingSchedule = {
  id: string;
  code: string;
  name: string;
  cadence: "monthly" | "quarterly";
  includeUnbilledUtilities: boolean;
};
type BillingPreview = {
  coverageStart: string;
  coverageEnd: string;
  cutoffDate: string;
  paymentCutoffDate: string;
  balanceAsOfDate: string;
  releaseDate: string;
  dueDate: string;
};
type BillingRun = {
  id: string;
  scheduleCode: string;
  scheduleName: string;
  coverageStart: string;
  coverageEnd: string;
  cutoffDate: string;
  paymentCutoffDate?: string;
  releaseDate: string;
  dueDate: string;
  status: string;
  exceptionCount: number;
  itemCount: number;
  statementCount: number;
  publishedStatementCount: number;
  revision?: number;
  sourceRunId?: string | null;
  approvedAt?: number | null;
  lockedAt?: number | null;
};

const manilaBillingPeriod = (() => {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Manila", year: "numeric", month: "numeric" }).formatToParts(new Date());
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  return { year, month };
})();

function BillingRuns() {
  const dialogs = useDialogs();
  const location = useLocation();
  const { has, loaded } = usePermissions(); const canRecord = has("billing.record"); const canPublish = has("billing.publish");
  const [schedules, setSchedules] = useState<BillingSchedule[]>([]);
  const [runs, setRuns] = useState<BillingRun[]>([]);
  const [scheduleId, setScheduleId] = useState("");
  const [year, setYear] = useState(manilaBillingPeriod.year);
  const [period, setPeriod] = useState(manilaBillingPeriod.month);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState<BillingPreview>();
  const [dates, setDates] = useState({
    billingCutoffDate: "",
    paymentCutoffDate: "",
    balanceAsOfDate: "",
    releaseDate: "",
    dueDate: "",
  });
  const [includeUnbilledUtilities, setIncludeUnbilledUtilities] =
    useState(false);
  const [focusRunId, setFocusRunId] = useState(() => new URLSearchParams(window.location.search).get("run") ?? new URLSearchParams(window.location.search).get("focus") ?? "");

  const load = async () => {
    const [scheduleResult, runResult] = await Promise.all([
      apiJson<{ schedules: BillingSchedule[] }>("/api/billing/schedules"),
      apiJson<{ runs: BillingRun[] }>("/api/billing/runs"),
    ]);
    setSchedules(scheduleResult.schedules);
    setRuns(runResult.runs);
    const first = scheduleResult.schedules[0];
    setScheduleId((current) => current || first?.id || "");
    if (first && !scheduleId) setPeriod(first.cadence === "quarterly" ? Math.ceil(manilaBillingPeriod.month / 3) : manilaBillingPeriod.month);
  };
  useEffect(() => {
    if (!loaded || !has("billing.view")) return;
    load().catch(() => setMessage("Billing runs could not be loaded."));
  }, [loaded]);
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("run") ?? new URLSearchParams(location.search).get("focus") ?? "";
    if (requested) setFocusRunId(requested);
  }, [location.search]);
  useEffect(() => {
    if (!focusRunId || !runs.length) return;
    const target = Array.from(document.querySelectorAll<HTMLElement>("[data-focus-id]")).find((element) => element.dataset.focusId === focusRunId);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.add("queue-focus");
    const timer = window.setTimeout(() => target.classList.remove("queue-focus"), 4000);
    return () => window.clearTimeout(timer);
  }, [focusRunId, runs]);
  useEffect(() => {
    if (!scheduleId || !loaded || !has("billing.view")) return;
    apiJson<{ preview: BillingPreview }>(
      `/api/billing/schedules/${scheduleId}/preview?year=${year}&period=${period}`,
    )
      .then((result) => {
        setPreview(result.preview);
        setDates({
          billingCutoffDate: result.preview.cutoffDate,
          paymentCutoffDate: result.preview.paymentCutoffDate,
          balanceAsOfDate: result.preview.balanceAsOfDate,
          releaseDate: result.preview.releaseDate,
          dueDate: result.preview.dueDate,
        });
        setIncludeUnbilledUtilities(
          Boolean(
            schedules.find((schedule) => schedule.id === scheduleId)
              ?.includeUnbilledUtilities,
          ),
        );
      })
      .catch(() => setPreview(undefined));
  }, [scheduleId, year, period, schedules]);

  const selectedSchedule = schedules.find(
    (schedule) => schedule.id === scheduleId,
  );
  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (!scheduleId) return;
    setBusy("create");
    setMessage("");
    try {
      const result = await apiJson<{
        itemCount: number;
        statementCount: number;
      }>("/api/billing/runs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scheduleId,
          year,
          period,
          ...dates,
          includeUnbilledUtilities,
        }),
      });
      setMessage(
        `Review run created with ${result.statementCount} statements and ${result.itemCount} items.`,
      );
      await load();
    } catch {
      setMessage(
        "The run could not be created. It may already exist or have no valid period.",
      );
    } finally {
      setBusy("");
    }
  };
  const publish = async (run: BillingRun) => {
    if (!(await dialogs.confirm({ title: "Publish billing run", description: `Publish ${run.statementCount} statements? This posts new utility charges to immutable unit ledgers.`, confirmLabel: "Publish run", danger: true })))
      return;
    setBusy(run.id);
    setMessage("");
    try {
      const result = await apiJson<{ publishedStatements: number }>(
        `/api/billing/runs/${run.id}/publish`,
        { method: "POST" },
      );
      setMessage(
        `${result.publishedStatements} statements published and secured in private storage.`,
      );
      await load();
    } catch {
      setMessage(
        "Publication failed. No run status was changed; review the configuration and try again.",
      );
    } finally {
      setBusy("");
    }
  };
  const requestRunReason = async (run: BillingRun, type: "approve" | "reverse") => {
    await dialogs.reason({
      title: type === "approve" ? "Approve billing run" : "Reverse billing run",
      description: `${run.scheduleName} · ${run.coverageStart}–${run.coverageEnd}`,
      label: type === "approve" ? "Approval reason" : "Reversal reason",
      confirmLabel: type === "approve" ? "Approve run" : "Reverse run",
      danger: type === "reverse",
      callout: type === "approve" ? "This records the review decision and makes the run eligible for publication." : "This creates append-only ledger adjustments for every posted charge and cannot be undone.",
      minLength: 3,
      errorFallback: type === "approve" ? "The run could not be approved. Resolve exceptions and try again." : "The billing run could not be reversed. Review the run and try again.",
      onSubmit: async (reason) => {
        setBusy(run.id); setMessage("");
        try {
          const path = type === "approve" ? "approve" : "reverse";
          await apiJson(`/api/billing/runs/${run.id}/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) });
          setMessage(type === "approve" ? "Billing run approved for publication." : "Billing run reversed with append-only ledger adjustments.");
          await load();
        } catch (caught) {
          throw new Error(caught instanceof Error ? caught.message : (type === "approve" ? "The run could not be approved. Resolve exceptions and try again." : "The billing run could not be reversed. Review the run and try again."));
        } finally {
          setBusy("");
        }
      },
    });
  };
  const lock = async (run: BillingRun) => {
    if (!(await dialogs.confirm({ title: "Lock billing run", description: "Lock this released billing run? It will become read-only.", confirmLabel: "Lock run", danger: true }))) return;
    setBusy(run.id); setMessage("");
    try { await apiJson(`/api/billing/runs/${run.id}/lock`, { method: "POST" }); setMessage("Billing run locked."); await load(); }
    catch { setMessage("The billing run could not be locked."); }
    finally { setBusy(""); }
  };
  const reconcile = async (run: BillingRun) => {
    setBusy(run.id); setMessage("");
    try { const result = await apiJson<{ balanced: boolean }>(`/api/billing/runs/${run.id}/reconcile`, { method: "POST" }); setMessage(result.balanced ? "Billing run reconciliation passed." : "Reconciliation found a difference requiring review."); }
    catch { setMessage("Reconciliation could not be completed."); }
    finally { setBusy(""); }
  };

  return (
    <section className="card billing-accounts-card">
      <CardTitle title="Billing runs" badge="Review before publication" />
      {loaded && <p className="callout finance-access-summary">{canRecord ? "Preparation access: create review runs for an independent approver." : canPublish ? "Approval access: validate exceptions and publish completed runs." : "Billing runs are read-only for your account."}</p>}
      {canRecord && <form className="billing-run-form" onSubmit={create}>
        <label>
          Schedule
          <select
            value={scheduleId}
            onChange={(event) => {
              const next = schedules.find(
                (item) => item.id === event.target.value,
              );
              setScheduleId(event.target.value);
              setPeriod(next?.cadence === "quarterly" ? 3 : 7);
            }}
          >
            {schedules.map((schedule) => (
              <option value={schedule.id} key={schedule.id}>
                {schedule.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year
          <input
            type="number"
            min="2000"
            max="2100"
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
          />
        </label>
        <label>
          {selectedSchedule?.cadence === "quarterly"
            ? "Quarter"
            : "Release month"}
          <input
            type="number"
            min="1"
            max={selectedSchedule?.cadence === "quarterly" ? 4 : 12}
            value={period}
            onChange={(event) => setPeriod(Number(event.target.value))}
          />
        </label>
        <label>
          Billing cutoff
          <input
            type="date"
            value={dates.billingCutoffDate}
            onChange={(event) =>
              setDates((current) => ({
                ...current,
                billingCutoffDate: event.target.value,
              }))
            }
            required
          />
        </label>
        <label>
          Payments reflected through
          <input
            type="date"
            value={dates.paymentCutoffDate}
            onChange={(event) =>
              setDates((current) => ({
                ...current,
                paymentCutoffDate: event.target.value,
              }))
            }
            required
          />
        </label>
        <label>
          Opening balance date
          <input
            type="date"
            value={dates.balanceAsOfDate}
            onChange={(event) =>
              setDates((current) => ({
                ...current,
                balanceAsOfDate: event.target.value,
              }))
            }
            required
          />
        </label>
        <label>
          Release date
          <input
            type="date"
            value={dates.releaseDate}
            onChange={(event) =>
              setDates((current) => ({
                ...current,
                releaseDate: event.target.value,
              }))
            }
            required
          />
        </label>
        <label>
          Due date
          <input
            type="date"
            value={dates.dueDate}
            onChange={(event) =>
              setDates((current) => ({
                ...current,
                dueDate: event.target.value,
              }))
            }
            required
          />
        </label>
        {selectedSchedule?.cadence === "quarterly" && (
          <label className="billing-run-check">
            <input
              type="checkbox"
              checked={includeUnbilledUtilities}
              onChange={(event) =>
                setIncludeUnbilledUtilities(event.target.checked)
              }
            />
            Include unbilled utility readings in this consolidated statement
          </label>
        )}
        <button className="button" disabled={Boolean(busy) || !scheduleId}>
          {busy === "create" ? "Creating…" : "Create review run"}
        </button>
      </form>}
      {preview && (
        <p className="billing-run-note">
          Coverage {preview.coverageStart}–{preview.coverageEnd}. Defaults follow
          the approved cadence; property managers may adjust them for this run,
          and every override is recorded in its immutable snapshot.
        </p>
      )}
      {message && <p className="callout">{message}</p>}
      <div className="table-wrap responsive-table">
        <table>
          <thead>
            <tr>
              <th>Schedule</th>
              <th>Coverage</th>
              <th>Cutoffs</th>
              <th>Release / due</th>
              <th>Items</th>
              <th>Statements</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id} data-focus-id={run.id}>
                <td data-label="Schedule">
                  <b>{run.scheduleName}</b>
                  <small>{run.scheduleCode}</small>
                </td>
                <td data-label="Coverage">
                  {run.coverageStart}
                  <small>to {run.coverageEnd}</small>
                </td>
                <td data-label="Cutoffs">
                  {run.cutoffDate}
                  <small>
                    Payments through {run.paymentCutoffDate ?? run.cutoffDate}
                  </small>
                </td>
                <td data-label="Release / due">
                  {run.releaseDate}
                  <small>Due {run.dueDate}</small>
                </td>
                <td data-label="Items">
                  {run.itemCount}
                  <small
                    className={run.exceptionCount ? "billing-exception" : ""}
                  >
                    {run.exceptionCount
                      ? `${run.exceptionCount} exceptions`
                      : "Validated"}
                  </small>
                </td>
                <td data-label="Statements">
                  {run.publishedStatementCount}/{run.statementCount} published
                </td>
                <td data-label="Status">
                  <Status value={run.status} />
                </td>
                <td data-label="Action">
                  {run.status === "review" && canPublish ? (
                    <button
                      className="table-action"
                      title={
                        run.exceptionCount
                          ? "Resolve all billing exceptions before publication"
                          : undefined
                      }
                      disabled={
                        Boolean(busy) ||
                        run.statementCount === 0 ||
                        run.exceptionCount > 0
                      }
                      onClick={() => void requestRunReason(run, "approve")}
                    >
                      {busy === run.id
                        ? "Approving…"
                        : run.exceptionCount
                          ? "Resolve exceptions"
                          : "Approve"}
                    </button>
                  ) : run.status === "approved" && canPublish ? (
                    <button className="table-action" disabled={Boolean(busy)} onClick={() => publish(run)}>{busy === run.id ? "Publishing…" : "Publish"}</button>
                  ) : run.status === "released" && canPublish ? (
                  <span className="table-actions"><button className="table-action" disabled={Boolean(busy)} onClick={() => lock(run)}>{busy === run.id ? "Locking…" : "Lock"}</button><button className="table-action" disabled={Boolean(busy)} onClick={() => void requestRunReason(run, "reverse")}>Reverse</button><button className="table-action" disabled={Boolean(busy)} onClick={() => reconcile(run)}>Reconcile</button></span>
                  ) : run.status === "locked" && canPublish ? (
                    <span className="table-actions"><button className="table-action" disabled={Boolean(busy)} onClick={() => void requestRunReason(run, "reverse")}>Reverse</button><button className="table-action" disabled={Boolean(busy)} onClick={() => reconcile(run)}>Reconcile</button></span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {!runs.length && (
              <tr>
                <td colSpan={8}>
                  No billing runs yet. Create one to generate draft statements
                  for review.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type ResidentStatement = {
  id: string;
  statementNumber: string;
  periodStart: string;
  periodEnd: string;
  issueDate: string;
  dueDate: string;
  amountDueCentavos: number;
  status: string;
  unitCode: string;
  buildingName: string;
  customerReferenceNumber: string;
  balanceAsOfDate: string;
  paymentCutoffDate: string;
  amountOverdueCentavos: number;
  pdfAvailable: number;
  csvAvailable: number;
};

function ResidentStatements() {
  const currentUnitId = useCurrentUnitId();
  const [statements, setStatements] = useState<ResidentStatement[]>(
    demoMode
      ? [
          {
            id: "demo",
            statementNumber: "SOA-UTIL-2026-07-3012B",
            periodStart: "2026-06-01",
            periodEnd: "2026-06-30",
            issueDate: "2026-07-20",
            dueDate: "2026-07-31",
            balanceAsOfDate: "2026-06-30",
            paymentCutoffDate: "2026-06-30",
            amountDueCentavos: 284650,
            amountOverdueCentavos: 0,
            status: "published",
            unitCode: "3012B",
            buildingName: "Tower 3",
            customerReferenceNumber: "DEMO-300003012B",
            pdfAvailable: 1,
            csvAvailable: 1,
          },
        ]
      : [],
  );
  const [loading, setLoading] = useState(!demoMode);
  const [selected, setSelected] = useState<ResidentStatementDetail>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (demoMode) return;
    let active = true;
    apiJson<{ statements: ResidentStatement[] }>(withCurrentUnit("/api/billing/statements", currentUnitId))
      .then((result) => { if (active) setStatements(result.statements); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [currentUnitId]);
  const openDetails = async (statement: ResidentStatement) => {
    if (demoMode) return setSelected(demoStatementDetail);
    setDetailLoading(true);
    setError("");
    try {
      const result = await apiJson<{ statement: ResidentStatementDetail }>(
        `/api/billing/statements/${statement.id}`,
      );
      setSelected(result.statement);
    } catch {
      setError("This statement could not be opened.");
    } finally {
      setDetailLoading(false);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="ONLINE STATEMENTS"
        title="Statements of account"
        description="Published statements for the units you are authorized to view."
      />
      {error && <p className="form-error">{error}</p>}
      <section className="card billing-accounts-card">
        <div className="table-wrap responsive-table">
          <table>
            <thead>
              <tr>
                <th>Statement</th>
                <th>Unit</th>
                <th>Coverage</th>
                <th>Issue / due</th>
                <th>Amount due</th>
                <th>Document</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((statement) => (
                <tr key={statement.id}>
                  <td data-label="Statement">
                    <b>{statement.statementNumber}</b>
                    <small>CRN {statement.customerReferenceNumber}</small>
                  </td>
                  <td data-label="Unit">
                    <b>{statement.unitCode}</b>
                    <small>{statement.buildingName}</small>
                  </td>
                  <td data-label="Coverage">
                    {statement.periodStart}
                    <small>to {statement.periodEnd}</small>
                  </td>
                  <td data-label="Issue / due">
                    {statement.issueDate}
                    <small>Due {statement.dueDate}</small>
                  </td>
                  <td data-label="Amount due">
                    <b>{formatPeso(statement.amountDueCentavos)}</b>
                    {statement.amountOverdueCentavos > 0 && (
                      <small className="overdue-text">
                        {formatPeso(statement.amountOverdueCentavos)} overdue
                      </small>
                    )}
                  </td>
                  <td data-label="Document">
                    <button
                      className="table-action"
                      disabled={detailLoading}
                      onClick={() => void openDetails(statement)}
                    >
                      Details
                    </button>
                    {statement.pdfAvailable ? (
                      <button
                        className="table-action"
                        disabled={demoMode}
                        onClick={() =>
                          window.open(
                            `/api/billing/statements/${statement.id}/pdf`,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        PDF
                      </button>
                    ) : null}
                    {statement.csvAvailable ? (
                      <button
                        className="table-action"
                        disabled={demoMode}
                        onClick={() =>
                          window.open(
                            `/api/billing/statements/${statement.id}/csv`,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        CSV
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!loading && !statements.length && (
                <tr>
                  <td colSpan={6}>
                    No published statements are available yet.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={6}>Loading statements…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      {selected && (
        <StatementDetailModal
          statement={selected}
          onClose={() => setSelected(undefined)}
        />
      )}
    </>
  );
}

type ResidentStatementDetail = ResidentStatement & {
  version: number;
  documentGrouping: string;
  openingBalanceCentavos: number;
  closingBalanceCentavos: number;
  agingCurrentCentavos: number;
  aging1To30Centavos: number;
  aging31To60Centavos: number;
  aging61To90Centavos: number;
  agingOver90Centavos: number;
  billedTo: string;
  publishedAt: number;
  items: Array<{
    id: string;
    itemDate: string;
    category: string;
    description: string;
    amountCentavos: number;
    runningBalanceCentavos: number;
    externalDocumentNumber?: string;
  }>;
  accountActivity: Array<{
    id: string;
    itemDate: string;
    activityType: string;
    category: string;
    description: string;
    amountCentavos: number;
    runningBalanceCentavos: number;
    externalDocumentNumber?: string;
  }>;
  artifacts: Array<{
    format: "pdf" | "csv";
    contentType: string;
    sizeBytes: number;
    sha256: string;
  }>;
};

const demoStatementDetail: ResidentStatementDetail = {
  id: "demo",
  statementNumber: "SOA-UTIL-2026-07-3012B",
  version: 1,
  documentGrouping: "combined",
  periodStart: "2026-06-01",
  periodEnd: "2026-06-30",
  issueDate: "2026-07-20",
  dueDate: "2026-07-31",
  balanceAsOfDate: "2026-06-30",
  paymentCutoffDate: "2026-06-30",
  openingBalanceCentavos: 0,
  closingBalanceCentavos: 284650,
  amountDueCentavos: 284650,
  amountOverdueCentavos: 0,
  agingCurrentCentavos: 284650,
  aging1To30Centavos: 0,
  aging31To60Centavos: 0,
  aging61To90Centavos: 0,
  agingOver90Centavos: 0,
  status: "published",
  unitCode: "3012B",
  buildingName: "Tower 3",
  billedTo: "Jayjay Pena",
  customerReferenceNumber: "DEMO-300003012B",
  pdfAvailable: 1,
  csvAvailable: 1,
  publishedAt: 1784505600,
  items: [
    {
      id: "electric",
      itemDate: "2026-07-20",
      category: "electricity",
      description: "June electricity consumption",
      amountCentavos: 231250,
      runningBalanceCentavos: 231250,
    },
    {
      id: "water",
      itemDate: "2026-07-20",
      category: "water",
      description: "June water consumption",
      amountCentavos: 53400,
      runningBalanceCentavos: 284650,
    },
  ],
  accountActivity: [],
  artifacts: [
    {
      format: "csv",
      contentType: "text/csv",
      sizeBytes: 820,
      sha256: "synthetic",
    },
    {
      format: "pdf",
      contentType: "application/pdf",
      sizeBytes: 48200,
      sha256: "synthetic",
    },
  ],
};

function StatementDetailModal({
  statement,
  onClose,
}: {
  statement: ResidentStatementDetail;
  onClose: () => void;
}) {
  const hasArtifact = (format: "pdf" | "csv") =>
    statement.artifacts.some((artifact) => artifact.format === format);
  return (
    <ModalSurface title={`Statement ${statement.statementNumber}`} onClose={onClose} className="statement-detail-modal">
        <div className="statement-identity">
          <div>
            <small>UNIT</small>
            <b>
              {statement.buildingName} · {statement.unitCode}
            </b>
          </div>
          <div>
            <small>BILLED TO</small>
            <b>{statement.billedTo}</b>
          </div>
          <div>
            <small>CRN</small>
            <b>{statement.customerReferenceNumber}</b>
          </div>
          <div>
            <small>COVERAGE</small>
            <b>
              {statement.periodStart} to {statement.periodEnd}
            </b>
          </div>
          <div>
            <small>PAYMENTS THROUGH</small>
            <b>{statement.paymentCutoffDate}</b>
          </div>
          <div>
            <small>DUE DATE</small>
            <b>{statement.dueDate}</b>
          </div>
        </div>
        <div className="statement-totals">
          <div>
            <small>Opening balance</small>
            <b>{formatPeso(statement.openingBalanceCentavos)}</b>
          </div>
          <div>
            <small>Current</small>
            <b>{formatPeso(statement.agingCurrentCentavos)}</b>
          </div>
          <div>
            <small>Overdue</small>
            <b>{formatPeso(statement.amountOverdueCentavos)}</b>
          </div>
          <div className="statement-total-due">
            <small>Total amount due</small>
            <b>{formatPeso(statement.amountDueCentavos)}</b>
          </div>
        </div>
        <div className="table-wrap responsive-table">
          <table>
            <thead>
              <tr>
                <th>Document / date</th>
                <th>Description</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="statement-section-row">
                <th colSpan={5}>Current charges</th>
              </tr>
              {statement.items.map((item) => (
                <tr key={item.id}>
                  <td data-label="Document / date">
                    {item.externalDocumentNumber ?? item.itemDate}
                    <small>{item.itemDate}</small>
                  </td>
                  <td data-label="Description">{item.description}</td>
                  <td data-label="Category">{humanStatus(item.category)}</td>
                  <td data-label="Amount">{formatPeso(item.amountCentavos)}</td>
                  <td data-label="Balance">
                    <b>{formatPeso(item.runningBalanceCentavos)}</b>
                  </td>
                </tr>
              ))}
              <tr className="statement-section-row">
                <th colSpan={5}>
                  Payments / adjustments reflected through{" "}
                  {statement.paymentCutoffDate}
                </th>
              </tr>
              {statement.accountActivity.map((item) => (
                <tr key={item.id}>
                  <td data-label="Document / date">
                    {item.externalDocumentNumber ?? item.itemDate}
                    <small>{item.itemDate}</small>
                  </td>
                  <td data-label="Description">{item.description}</td>
                  <td data-label="Category">{humanStatus(item.activityType)}</td>
                  <td data-label="Amount">{formatPeso(item.amountCentavos)}</td>
                  <td data-label="Balance">
                    <b>{formatPeso(item.runningBalanceCentavos)}</b>
                  </td>
                </tr>
              ))}
              {!statement.accountActivity.length && (
                <tr>
                  <td colSpan={5}>
                    No payments or adjustments in this statement window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="statement-aging">
          <span>
            1–30 days <b>{formatPeso(statement.aging1To30Centavos)}</b>
          </span>
          <span>
            31–60 days <b>{formatPeso(statement.aging31To60Centavos)}</b>
          </span>
          <span>
            61–90 days <b>{formatPeso(statement.aging61To90Centavos)}</b>
          </span>
          <span>
            Over 90 days <b>{formatPeso(statement.agingOver90Centavos)}</b>
          </span>
        </div>
        <div className="form-actions">
          <button className="button secondary" onClick={onClose}>
            Close
          </button>
          {hasArtifact("csv") && (
            <button
              className="button secondary"
              disabled={demoMode}
              onClick={() =>
                window.open(
                  `/api/billing/statements/${statement.id}/csv`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              Download CSV
            </button>
          )}
          {hasArtifact("pdf") && (
            <button
              className="button"
              disabled={demoMode}
              onClick={() =>
                window.open(
                  `/api/billing/statements/${statement.id}/pdf`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              Open PDF
            </button>
          )}
        </div>
    </ModalSurface>
  );
}

function LedgerForm({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (amount: number, type: string, reference: string) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const type = String(data.get("type"));
    const amount =
      Math.round(Number(data.get("amount")) * 100) *
      (type === "Payment" ? -1 : 1);
    setBusy(true);
    setError("");
    try {
      await onCreate(amount, type, String(data.get("reference")));
    } catch {
      setError("The ledger entry could not be posted. Check the unit and accounting period, then try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModalSurface title="Add ledger entry" onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        <label>
          Entry type
          <select name="type">
            <option>Charge</option>
            <option>Payment</option>
            <option>Adjustment</option>
            <option>Penalty</option>
          </select>
        </label>
        <label>
          Amount (PHP)
          <input type="number" name="amount" min="0.01" step="0.01" required />
        </label>
        <label>
          Reference / reason
          <input name="reference" required />
        </label>
        <p className="callout">
          This creates an immutable entry. It cannot be edited or deleted.
        </p>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>{busy ? "Posting…" : "Post entry"}</button>
        </div>
      </form>
    </ModalSurface>
  );
}

type HubBuilding = { id: string; name: string };
type AnnouncementItem = {
  id: string;
  title: string;
  body: string;
  status?: string;
  priority?: string;
  audienceRelationship?: string;
  audienceBuildings?: string;
  coverDocumentId?: string;
  coverAltText?: string;
  publishedAt?: number;
  createdAt?: number;
};

async function uploadHubImage(
  file: File,
  type:
    | "announcement_image"
    | "community_image"
    | "marketplace_image"
    | "event_image",
  stagingReview: boolean,
) {
  const data = new FormData();
  data.set("file", file);
  data.set("type", type);
  const response = await fetch("/api/documents", {
    method: "POST",
    body: data,
  });
  const result = (await response.json()) as { document?: { id: string } };
  if (!response.ok || !result.document)
    throw new Error("The image could not be uploaded.");
  if (stagingReview) {
    const review = await fetch(
      `/api/documents/${result.document.id}/staging-review`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason:
            "Manual administrator review of synthetic Community Hub media",
        }),
      },
    );
    if (!review.ok)
      throw new Error("The image is still awaiting security review.");
  }
  return result.document.id;
}

function Announcements({ admin = false, canManage = admin }: { admin?: boolean; canManage?: boolean }) {
  const [notices, setNotices] = useState<AnnouncementItem[]>([]);
  const [buildings, setBuildings] = useState<HubBuilding[]>([]);
  const [compose, setCompose] = useState(false);
  const [error, setError] = useState("");
  const load = () =>
    apiJson<{ announcements: AnnouncementItem[] }>("/api/announcements").then(
      (result) => setNotices(result.announcements),
    );
  useEffect(() => {
    if (admin && !canManage) return;
    if (demoMode) {
      setNotices(
        announcementsSeed.map((item) => ({
          ...item,
          audienceBuildings: item.scope,
          publishedAt: Math.floor(Date.now() / 1000),
        })),
      );
      setBuildings([
        { id: "tower-1", name: "Tower 1" },
        { id: "tower-2", name: "Tower 2" },
        { id: "tower-3", name: "Tower 3" },
      ]);
      return;
    }
    Promise.all([
      load(),
      apiJson<{ buildings: HubBuilding[] }>("/api/buildings").then((result) =>
        setBuildings(result.buildings),
      ),
    ]).catch(() => setError("Announcements could not be loaded."));
  }, [admin, canManage]);
  return (
    <>
      <PageHeader
        eyebrow="PROPERTY UPDATES"
        title="Announcements"
        description={
          admin
            ? "Publish image-enabled updates to selected buildings and resident groups."
            : "Official notices selected for your buildings and resident relationship."
        }
        action={
          canManage && (
            <button className="button" onClick={() => setCompose(true)}>
              + New announcement
            </button>
          )
        }
      />
      {compose && canManage && (
        <AnnouncementForm
          buildings={buildings}
          onClose={() => setCompose(false)}
          onCreated={() => {
            setCompose(false);
            void load();
          }}
        />
      )}
      {error && <p className="form-error">{error}</p>}
      <section className="notice-grid">
        {notices.map((notice) => (
          <article
            className={`notice-card priority-${notice.priority ?? "normal"}`}
            key={notice.id}
          >
            {notice.coverDocumentId && (
              <img
                className="notice-cover"
                src={`/files/${notice.coverDocumentId}`}
                alt={notice.coverAltText ?? "Announcement image"}
                width="16"
                height="9"
                loading="lazy"
                decoding="async"
              />
            )}
            <span className="notice-date">
              {formatDateTime(
                Number(
                  notice.publishedAt ?? notice.createdAt ?? Date.now() / 1000,
                ),
              )}{" "}
              · {humanStatus(notice.priority ?? "normal")}
            </span>
            <h2>{notice.title}</h2>
            <p>{notice.body}</p>
            <footer>
              <span>
                {notice.audienceBuildings ?? "Selected buildings"} ·{" "}
                {notice.audienceRelationship === "owner"
                  ? "Owners"
                  : notice.audienceRelationship === "tenant"
                    ? "Tenants"
                    : "All residents"}
              </span>
              {canManage && notice.status && <Status value={notice.status} />}
            </footer>
          </article>
        ))}
        {!notices.length && admin && !canManage ? (
          <Empty title="Announcements access is not assigned" body="Ask a property administrator for the announcements publishing permission." />
        ) : !notices.length && (
          <Empty
            title="No announcements"
            body="Published announcements for this audience will appear here."
          />
        )}
      </section>
    </>
  );
}

function AnnouncementForm({
  buildings,
  onClose,
  onCreated,
}: {
  buildings: HubBuilding[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const dialogs = useDialogs();
  const [selected, setSelected] = useState(
    buildings.map((building) => building.id),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected.length) return setError("Select at least one building.");
    const data = new FormData(event.currentTarget);
    const image = data.get("image");
    if (image instanceof File && image.size && String(data.get("altText") ?? "").trim().length < 3) return setError("Describe the announcement image for residents using screen readers.");
    setBusy(true);
    setError("");
    try {
      let media: Array<{ documentId: string; altText: string }> = [];
      if (image instanceof File && image.size) {
        if (!(await dialogs.confirm({ title: "Review staging image", description: "Staging media review bypasses malware scanning. Continue only with a synthetic test image?", confirmLabel: "Continue" }))) {
          setBusy(false);
          return;
        }
        media = [
          {
            documentId: await uploadHubImage(image, "announcement_image", true),
            altText: String(data.get("altText")),
          },
        ];
      }
      const expiry = String(data.get("expiresAt"));
      await apiJson("/api/announcements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: String(data.get("title")),
          body: String(data.get("body")),
          buildingIds: selected,
          audienceRelationship: String(data.get("audienceRelationship")),
          priority: String(data.get("priority")),
          expiresAt: expiry
            ? Math.floor(new Date(`${expiry}T23:59:59+08:00`).getTime() / 1000)
            : undefined,
          media,
          publishNow: true,
        }),
      });
      onCreated();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The announcement could not be published.",
      );
      setBusy(false);
    }
  };
  return (
    <ModalSurface title="New announcement" onClose={onClose} busy={busy} className="hub-form">
      <form onSubmit={submit}>
        <label>
          Title
          <input name="title" required minLength={3} />
        </label>
        <label>
          Message
          <textarea name="body" rows={5} required minLength={10} />
        </label>
        <div className="form-row">
          <label>
            Resident group
            <select name="audienceRelationship">
              <option value="all">Owners and tenants</option>
              <option value="owner">Owners only</option>
              <option value="tenant">Tenants only</option>
            </select>
          </label>
          <label>
            Priority
            <select name="priority">
              <option value="normal">Normal</option>
              <option value="important">Important</option>
              <option value="emergency">Emergency</option>
            </select>
          </label>
        </div>
        <fieldset>
          <legend>Buildings</legend>
          <div className="check-grid">
            {buildings.map((building) => (
              <label key={building.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(building.id)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, building.id]
                        : current.filter((id) => id !== building.id),
                    )
                  }
                />
                {building.name}
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Expiration date (optional)
          <input name="expiresAt" type="date" />
        </label>
        <label>
          Cover image (optional)
          <input
            name="image"
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
          />
        </label>
        <label>
          Image description
          <input
            name="altText"
            placeholder="Describe the image for accessibility"
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>
            {busy ? "Publishing…" : "Publish now"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

type EventItem = {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  status: string;
  visibility: string;
  audienceRelationship?: string;
  registrationMode: "individual" | "group" | "team";
  venueName: string;
  facilityId?: string;
  buildingIds?: string;
  startsAt: number;
  endsAt: number;
  registrationOpensAt: number;
  registrationClosesAt: number;
  capacityAttendees?: number;
  maxPartySize: number;
  waitlistEnabled: number;
  externalRegistrationEnabled: number;
  confirmedAttendees: number;
  myRegistrationStatus?: string;
  audienceBuildings?: string;
  imageDocumentId?: string;
  imageAltText?: string;
};
type EventRegistration = {
  id: string;
  registrantName: string;
  registrantEmail: string;
  teamName?: string;
  attendeeCount: number;
  status: string;
  claimedBuildingName?: string;
  claimedUnitCode?: string;
  createdAt: number;
};

function Events({ admin = false, canManage = admin }: { admin?: boolean; canManage?: boolean }) {
  const dialogs = useDialogs();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [buildings, setBuildings] = useState<HubBuilding[]>([]);
  const [facilities, setFacilities] = useState<
    Array<{ id: string; name: string; locationDescription: string }>
  >([]);
  const [compose, setCompose] = useState(false);
  const [editEvent, setEditEvent] = useState<EventItem>();
  const [registerEvent, setRegisterEvent] = useState<EventItem>();
  const [manageEvent, setManageEvent] = useState<EventItem>();
  const [error, setError] = useState("");
  const load = () =>
    apiJson<{ events: EventItem[] }>("/api/events").then((result) =>
      setEvents(result.events),
    );
  useEffect(() => {
    if (admin && !canManage) return;
    if (demoMode) {
      const now = Math.floor(Date.now() / 1000);
      setBuildings([
        { id: "building-tower-1", name: "Tower 1" },
        { id: "building-north", name: "Tower 2" },
        { id: "building-east", name: "Tower 3" },
      ]);
      setFacilities([
        {
          id: "facility-basketball-court",
          name: "Basketball court",
          locationDescription: "Community court",
        },
        {
          id: "facility-common-area",
          name: "Common area",
          locationDescription: "Between Tower 2 and Tower 3",
        },
      ]);
      setEvents([
        {
          id: "event-demo-movie",
          slug: "movie-night-demo",
          title: "Community movie night",
          description:
            "An outdoor family movie showing with limited seating. Bring a small cushion and arrive 20 minutes before screening.",
          category: "movie",
          status: "published",
          visibility: "public",
          registrationMode: "group",
          venueName: "Common area",
          startsAt: now + 345600,
          endsAt: now + 352800,
          registrationOpensAt: now - 86400,
          registrationClosesAt: now + 338400,
          capacityAttendees: 80,
          maxPartySize: 6,
          waitlistEnabled: 1,
          externalRegistrationEnabled: 1,
          confirmedAttendees: 36,
          audienceBuildings: "Tower 1, Tower 2, Tower 3",
        },
      ]);
      return;
    }
    Promise.all([
      load(),
      apiJson<{ buildings: HubBuilding[] }>("/api/buildings").then((result) =>
        setBuildings(result.buildings),
      ),
      apiJson<{
        facilities: Array<{
          id: string;
          name: string;
          locationDescription: string;
        }>;
      }>("/api/property/guide").then((result) =>
        setFacilities(result.facilities),
      ),
    ]).catch(() => setError("Events could not be loaded."));
  }, [admin, canManage]);
  const saveStatus = async (
    event: EventItem,
    status: "published" | "cancelled" | "completed",
    reason?: string,
  ) => {
    if (!demoMode)
      await apiJson(`/api/events/${event.id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, reason }),
      });
    setEvents((current) =>
      current.map((item) =>
        item.id === event.id ? { ...item, status } : item,
      ),
    );
  };
  const changeStatus = async (event: EventItem, status: "published" | "cancelled" | "completed", reason?: string) => {
    try {
      await saveStatus(event, status, reason);
    } catch {
      setError("The event status could not be updated.");
    }
  };
  const requestEventCancellation = async (event: EventItem) => {
    await dialogs.reason({
      title: "Cancel event",
      description: event.title,
      label: "Cancellation reason",
      confirmLabel: "Cancel event",
      danger: true,
      minLength: 3,
      errorFallback: "The event status could not be updated.",
      onSubmit: async (reason) => {
        try {
          await saveStatus(event, "cancelled", reason);
        } catch (caught) {
          throw new Error(caught instanceof Error ? caught.message : "The event status could not be updated.");
        }
      },
    });
  };
  const cancelMyRegistration = async (event: EventItem) => {
    if (!(await dialogs.confirm({ title: "Cancel event registration", description: `Cancel your registration for ${event.title}?`, confirmLabel: "Cancel registration", danger: true }))) return;
    try {
      if (!demoMode)
        await apiJson(`/api/events/${event.id}/registrations/me`, {
          method: "DELETE",
        });
      setEvents((current) =>
        current.map((item) =>
          item.id === event.id
            ? { ...item, myRegistrationStatus: "cancelled" }
            : item,
        ),
      );
    } catch {
      setError("Your registration could not be cancelled.");
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="COMMUNITY CALENDAR"
        title="Events"
        description={
          admin
            ? "Publish activities and manage resident, guest, household, and team registrations."
            : "Discover property events and manage your registrations."
        }
        action={
          canManage && (
            <button className="button" onClick={() => setCompose(true)}>
              + New event
            </button>
          )
        }
      />
      {compose && canManage && (
        <EventForm
          buildings={buildings}
          facilities={facilities}
          onClose={() => setCompose(false)}
          onCreated={() => {
            setCompose(false);
            void load();
          }}
        />
      )}
      {editEvent && canManage && (
        <EventForm
          event={editEvent}
          buildings={buildings}
          facilities={facilities}
          onClose={() => setEditEvent(undefined)}
          onCreated={() => {
            setEditEvent(undefined);
            void load();
          }}
        />
      )}
      {registerEvent && (
        <EventRegistrationForm
          event={registerEvent}
          onClose={() => setRegisterEvent(undefined)}
          onCreated={() => {
            setRegisterEvent(undefined);
            void load();
          }}
        />
      )}
      {manageEvent && canManage && (
        <EventRegistrationManager
          event={manageEvent}
          onClose={() => setManageEvent(undefined)}
        />
      )}
      {error && <p className="form-error">{error}</p>}
      <section className="event-grid">
        {events.map((event) => (
          <article className="event-card card" key={event.id}>
            {event.imageDocumentId && (
              <img
                src={`/files/${event.imageDocumentId}`}
                alt={event.imageAltText ?? "Event image"}
                width="16"
                height="9"
                loading="lazy"
                decoding="async"
              />
            )}
            <div className="event-card-body">
              <span className="community-category">
                {humanStatus(event.category)}
              </span>
              <Status value={event.status} />
              <h2>{event.title}</h2>
              <p>{event.description}</p>
              <dl>
                <dt>Schedule</dt>
                <dd>{formatEventSchedule(event.startsAt, event.endsAt)}</dd>
                <dt>Venue</dt>
                <dd>{event.venueName}</dd>
                <dt>Registration</dt>
                <dd>
                  {humanStatus(event.registrationMode)} ·{" "}
                  {event.capacityAttendees
                    ? `${event.confirmedAttendees}/${event.capacityAttendees} attending`
                    : "No capacity limit"}
                </dd>
                {event.audienceBuildings && (
                  <>
                    <dt>Audience</dt>
                    <dd>{event.audienceBuildings}</dd>
                  </>
                )}
              </dl>
            </div>
            <footer>
              {!admin && !event.myRegistrationStatus && (
                <button
                  className="button"
                  onClick={() => setRegisterEvent(event)}
                >
                  Register
                </button>
              )}
              {!admin && event.myRegistrationStatus && (
                <Status value={event.myRegistrationStatus} />
              )}
              {!admin &&
                ["confirmed", "waitlisted"].includes(
                  event.myRegistrationStatus ?? "",
                ) && (
                  <button
                    className="table-action danger-text"
                    onClick={() => void cancelMyRegistration(event)}
                  >
                    Cancel registration
                  </button>
                )}
              {canManage && (
                <button
                  className="table-action"
                  onClick={() => setEditEvent(event)}
                >
                  Edit
                </button>
              )}
              {canManage && (
                <button
                  className="table-action"
                  onClick={() => setManageEvent(event)}
                >
                  Registrations
                </button>
              )}
              {canManage && event.status === "draft" && (
                <button
                  className="table-action"
                  onClick={() => void changeStatus(event, "published")}
                >
                  Publish
                </button>
              )}
              {canManage &&
                event.status === "published" &&
                Boolean(event.externalRegistrationEnabled) && (
                  <button
                    className="table-action"
                    onClick={() =>
                      void navigator.clipboard.writeText(
                        `${window.location.origin}/events/${event.slug}`,
                      )
                    }
                  >
                    Copy public link
                  </button>
                )}
              {canManage && event.status === "published" && (
                <button
                  className="table-action danger-text"
                  onClick={() => void requestEventCancellation(event)}
                >
                  Cancel
                </button>
              )}
            </footer>
          </article>
        ))}
        {!events.length && admin && !canManage ? (
          <Empty title="Events access is not assigned" body="Ask a property administrator for the events management permission." />
        ) : !events.length && (
          <Empty
            title="No events scheduled"
            body={
              admin
                ? "Create the first community event."
                : "New property events will appear here."
            }
          />
        )}
      </section>
    </>
  );
}

function EventForm({
  event: existing,
  buildings,
  facilities,
  onClose,
  onCreated,
}: {
  event?: EventItem;
  buildings: HubBuilding[];
  facilities: Array<{ id: string; name: string; locationDescription: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const dialogs = useDialogs();
  const [selectedBuildings, setSelectedBuildings] = useState(
    existing?.buildingIds?.split(",").filter(Boolean) ??
      buildings.map((building) => building.id),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const localInput = (hours: number, timestamp?: number) => {
    const date = new Date(
      timestamp ? timestamp * 1000 : Date.now() + hours * 3600000,
    );
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedBuildings.length)
      return setError("Select at least one tower.");
    const data = new FormData(event.currentTarget);
    const image = data.get("image");
    setBusy(true);
    setError("");
    try {
      let media: Array<{ documentId: string; altText: string }> =
        existing?.imageDocumentId
          ? [
              {
                documentId: existing.imageDocumentId,
                altText: existing.imageAltText ?? "Event image",
              },
            ]
          : [];
      if (image instanceof File && image.size) {
        if (!(await dialogs.confirm({ title: "Review staging image", description: "Continue with a synthetic staging image and manual security-review override?", confirmLabel: "Continue" }))) {
          setBusy(false);
          return;
        }
        media = [
          {
            documentId: await uploadHubImage(image, "event_image", true),
            altText: String(data.get("altText")),
          },
        ];
      }
      const unix = (name: string) =>
        Math.floor(new Date(String(data.get(name))).getTime() / 1000);
      const capacity = String(data.get("capacityAttendees"));
      if (!demoMode)
        await apiJson(existing ? `/api/events/${existing.id}` : "/api/events", {
          method: existing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: String(data.get("title")),
            description: String(data.get("description")),
            category: String(data.get("category")),
            visibility: String(data.get("visibility")),
            audienceRelationship: String(data.get("audienceRelationship")),
            registrationMode: String(data.get("registrationMode")),
            venueName: String(data.get("venueName")),
            facilityId: String(data.get("facilityId")) || undefined,
            startsAt: unix("startsAt"),
            endsAt: unix("endsAt"),
            registrationOpensAt: unix("registrationOpensAt"),
            registrationClosesAt: unix("registrationClosesAt"),
            capacityAttendees: capacity ? Number(capacity) : undefined,
            maxPartySize: Number(data.get("maxPartySize")),
            waitlistEnabled: data.get("waitlistEnabled") === "on",
            externalRegistrationEnabled:
              data.get("externalRegistrationEnabled") === "on",
            buildingIds: selectedBuildings,
            media,
            publishNow: data.get("publishNow") === "on",
          }),
        });
      onCreated();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The event could not be created.",
      );
      setBusy(false);
    }
  };
  return (
    <ModalSurface title={existing ? "Edit event" : "Create event"} onClose={onClose} busy={busy} className="event-form">
      <form onSubmit={submit}>
        <label>
          Event title
          <input
            name="title"
            required
            minLength={4}
            defaultValue={existing?.title}
          />
        </label>
        <label>
          Description
          <textarea
            name="description"
            rows={5}
            required
            minLength={20}
            defaultValue={existing?.description}
          />
        </label>
        <div className="form-row">
          <label>
            Category
            <select name="category" defaultValue={existing?.category}>
              <option value="movie">Movie showing</option>
              <option value="sports">Sports or tournament</option>
              <option value="community">Community activity</option>
              <option value="meeting">Meeting</option>
              <option value="workshop">Workshop</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label>
            Registration type
            <select
              name="registrationMode"
              defaultValue={existing?.registrationMode}
            >
              <option value="individual">Individual</option>
              <option value="group">Household or group</option>
              <option value="team">Team</option>
            </select>
          </label>
        </div>
        <div className="form-row">
          <label>
            Starts
            <input
              name="startsAt"
              type="datetime-local"
              defaultValue={localInput(168, existing?.startsAt)}
              required
            />
          </label>
          <label>
            Ends
            <input
              name="endsAt"
              type="datetime-local"
              defaultValue={localInput(170, existing?.endsAt)}
              required
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            Registration opens
            <input
              name="registrationOpensAt"
              type="datetime-local"
              defaultValue={localInput(1, existing?.registrationOpensAt)}
              required
            />
          </label>
          <label>
            Registration closes
            <input
              name="registrationClosesAt"
              type="datetime-local"
              defaultValue={localInput(160, existing?.registrationClosesAt)}
              required
            />
          </label>
        </div>
        <label>
          Venue
          <input name="venueName" required defaultValue={existing?.venueName} />
        </label>
        <label>
          Property facility
          <select name="facilityId" defaultValue={existing?.facilityId ?? ""}>
            <option value="">Custom venue</option>
            {facilities.map((facility) => (
              <option value={facility.id} key={facility.id}>
                {facility.name} · {facility.locationDescription}
              </option>
            ))}
          </select>
        </label>
        <div className="form-row">
          <label>
            Capacity
            <input
              name="capacityAttendees"
              type="number"
              min={1}
              placeholder="Unlimited"
              defaultValue={existing?.capacityAttendees}
            />
          </label>
          <label>
            Maximum people per registration
            <input
              name="maxPartySize"
              type="number"
              min={1}
              max={50}
              defaultValue={existing?.maxPartySize ?? 1}
              required
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            Visibility
            <select name="visibility" defaultValue={existing?.visibility}>
              <option value="residents_only">Residents only</option>
              <option value="public">Public link</option>
            </select>
          </label>
          <label>
            Resident audience
            <select
              name="audienceRelationship"
              defaultValue={existing?.audienceRelationship}
            >
              <option value="all">Owners and tenants</option>
              <option value="owner">Owners only</option>
              <option value="tenant">Tenants only</option>
            </select>
          </label>
        </div>
        <fieldset>
          <legend>Towers</legend>
          <div className="check-grid">
            {buildings.map((building) => (
              <label key={building.id}>
                <input
                  type="checkbox"
                  checked={selectedBuildings.includes(building.id)}
                  onChange={(change) =>
                    setSelectedBuildings((current) =>
                      change.target.checked
                        ? [...current, building.id]
                        : current.filter((id) => id !== building.id),
                    )
                  }
                />
                {building.name}
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Event image
          <input
            name="image"
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
          />
        </label>
        <label>
          Image description
          <input
            name="altText"
            placeholder="Describe the image for accessibility"
            defaultValue={existing?.imageAltText}
          />
        </label>
        <label className="checkbox-line">
          <input
            name="waitlistEnabled"
            type="checkbox"
            defaultChecked={existing ? Boolean(existing.waitlistEnabled) : true}
          />{" "}
          Enable automatic waitlist
        </label>
        <label className="checkbox-line">
          <input
            name="externalRegistrationEnabled"
            type="checkbox"
            defaultChecked={Boolean(existing?.externalRegistrationEnabled)}
          />{" "}
          Allow people without Two Carats accounts
        </label>
        <label className="checkbox-line">
          <input
            name="publishNow"
            type="checkbox"
            defaultChecked={existing ? existing.status === "published" : true}
          />{" "}
          Publish immediately
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>
            {busy ? "Saving…" : existing ? "Save changes" : "Create event"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

function EventRegistrationForm({
  event,
  onClose,
  onCreated,
}: {
  event: EventItem;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [names, setNames] = useState([""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const data = new FormData(formEvent.currentTarget);
    setBusy(true);
    setError("");
    try {
      if (!demoMode)
        await apiJson(`/api/events/${event.id}/registrations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attendeeNames: names,
            teamName: String(data.get("teamName")) || undefined,
          }),
        });
      onCreated();
    } catch (caught) {
      setError(userFacingApiError(caught, "Registration could not be completed."));
      setBusy(false);
    }
  };
  return (
    <ModalSurface title={`Register for ${event.title}`} onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        {event.registrationMode === "team" && (
          <label>
            Team name
            <input name="teamName" required />
          </label>
        )}
        <fieldset className="attendee-list">
          <legend>
            {event.registrationMode === "individual" ? "Attendee" : "Attendees"}
          </legend>
          {names.map((name, index) => (
            <div key={index}>
              <input
                value={name}
                onChange={(change) =>
                  setNames((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? change.target.value : item,
                    ),
                  )
                }
                required
                placeholder={`Person ${index + 1} full name`}
              />
              {names.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setNames((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {event.registrationMode !== "individual" && (
            <button
              type="button"
              className="table-action"
              disabled={names.length >= event.maxPartySize}
              onClick={() => setNames((current) => [...current, ""])}
            >
              + Add person
            </button>
          )}
        </fieldset>
        <p className="callout">
          Registration uses your verified Two Carats profile. If capacity is
          full, you may be placed on the waitlist.
        </p>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>
            {busy ? "Registering…" : "Confirm registration"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

function EventRegistrationManager({
  event,
  onClose,
}: {
  event: EventItem;
  onClose: () => void;
}) {
  const dialogs = useDialogs();
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [error, setError] = useState("");
  const load = () =>
    demoMode
      ? setRegistrations([
          {
            id: "registration-demo",
            registrantName: "Sample Guest",
            registrantEmail: "guest@example.invalid",
            attendeeCount: 2,
            status: "pending_review",
            claimedBuildingName: "Tower 3",
            claimedUnitCode: "3012B",
            createdAt: Math.floor(Date.now() / 1000),
          },
        ])
      : apiJson<{ registrations: EventRegistration[] }>(
          `/api/events/${event.id}/registrations`,
        ).then((result) => setRegistrations(result.registrations));
  useEffect(() => {
    void load();
  }, [event.id]);
  const saveReview = async (
    registration: EventRegistration,
    decision: "approved" | "rejected",
    note: string,
  ) => {
    if (!demoMode)
      await apiJson(`/api/event-registrations/${registration.id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, note }),
      });
    await load();
  };
  const requestReview = async (registration: EventRegistration, decision: "approved" | "rejected") => {
    await dialogs.reason({
      title: decision === "approved" ? "Approve registration" : "Reject registration",
      description: `${registration.registrantName} · ${registration.attendeeCount} attendee(s)`,
      label: decision === "approved" ? "Approval note" : "Rejection reason",
      confirmLabel: decision === "approved" ? "Approve" : "Reject",
      danger: decision === "rejected",
      callout: "Affected registrants will be notified with this reason.",
      minLength: 3,
      errorFallback: "The registration could not be reviewed.",
      onSubmit: async (note) => {
        try {
          await saveReview(registration, decision, note);
        } catch (caught) {
          throw new Error(caught instanceof Error ? caught.message : "The registration could not be reviewed.");
        }
      },
    });
  };
  const exportCsv = () => {
    const quote = (value: unknown) =>
      `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = [
      [
        "name",
        "email",
        "team",
        "attendees",
        "status",
        "claimed_building",
        "claimed_unit",
      ],
      ...registrations.map((registration) => [
        registration.registrantName,
        registration.registrantEmail,
        registration.teamName,
        registration.attendeeCount,
        registration.status,
        registration.claimedBuildingName,
        registration.claimedUnitCode,
      ]),
    ]
      .map((row) => row.map(quote).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${event.slug}-registrations.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <ModalSurface title={`${event.title} registrations`} onClose={onClose} className="event-registration-manager">
        <div className="panel-tools">
          <b>{registrations.length} registrations</b>
          <button onClick={exportCsv}>Export CSV</button>
        </div>
        {registrations.map((registration) => (
          <div className="event-registration-row" key={registration.id}>
            <span>
              <b>{registration.registrantName}</b>
              <small>
                {registration.registrantEmail} · {registration.attendeeCount}{" "}
                attendee(s)
                {registration.teamName ? ` · ${registration.teamName}` : ""}
              </small>
              <small>
                {registration.claimedBuildingName
                  ? `${registration.claimedBuildingName} · ${registration.claimedUnitCode}`
                  : "Verified app account"}
              </small>
            </span>
            <Status value={registration.status} />
            {registration.status === "pending_review" && (
              <div>
                <button
                  className="table-action"
                  onClick={() => void requestReview(registration, "approved")}
                >
                  Approve
                </button>
                <button
                  className="table-action danger-text"
                  onClick={() => void requestReview(registration, "rejected")}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
        {!registrations.length && (
          <Empty
            title="No registrations"
            body="Confirmed and pending attendees will appear here."
          />
        )}
        {error && <p className="form-error">{error}</p>}
    </ModalSurface>
  );
}

function PublicEvents() {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);
  const slug = parts[1];
  const confirming = parts[2] === "confirm";
  const cancelling = parts[2] === "cancel";
  const [events, setEvents] = useState<EventItem[]>([]);
  const [event, setEvent] = useState<EventItem>();
  const [verification, setVerification] = useState<{
    status: string;
    eventTitle: string;
  }>();
  const [error, setError] = useState("");
  useEffect(() => {
    if (confirming || cancelling) {
      const token = new URLSearchParams(window.location.hash.slice(1)).get(
        "token",
      );
      if (!token)
        return setError(
          `This ${cancelling ? "cancellation" : "verification"} link is incomplete.`,
        );
      apiJson<{ registration: { status: string; eventTitle: string } }>(
        cancelling
          ? "/api/public/event-registrations/cancel"
          : "/api/public/event-registrations/verify",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        },
      )
        .then((result) => {
          setVerification(result.registration);
          history.replaceState(null, "", location.pathname);
        })
        .catch(() =>
          setError(
            `This ${cancelling ? "cancellation" : "verification"} link is invalid or expired.`,
          ),
        );
      return;
    }
    if (slug)
      apiJson<{ event: EventItem }>(`/api/public/events/${slug}`)
        .then((result) => setEvent(result.event))
        .catch(() => setError("This event is unavailable."));
    else
      apiJson<{ events: EventItem[] }>("/api/public/events")
        .then((result) => setEvents(result.events))
        .catch(() => setError("Public events could not be loaded."));
  }, [location.pathname]);

  useEffect(() => {
    if (!event || confirming || cancelling) return;
    document.title = `${event.title} | Two Carats`;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) description.content = event.description;

    const structuredData = document.createElement("script");
    structuredData.type = "application/ld+json";
    structuredData.dataset.twoCaratsEvent = "true";
    structuredData.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Event",
      name: event.title,
      description: event.description,
      startDate: new Date(event.startsAt * 1000).toISOString(),
      endDate: new Date(event.endsAt * 1000).toISOString(),
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      location: { "@type": "Place", name: event.venueName },
      organizer: { "@type": "Organization", name: "Two Carats" },
      url: new URL(`/events/${event.slug}`, window.location.origin).href,
    });
    document.head.appendChild(structuredData);
    return () => {
      structuredData.remove();
    };
  }, [cancelling, confirming, event]);

  if (confirming || cancelling)
    return (
      <div className="public-events">
        <header>
          <span className="brand-mark" aria-hidden="true">2C</span>
          <b>Two Carats Events</b>
        </header>
        <main>
          {verification ? (
            <section className="auth-card registration-confirmation">
              <span className="confirmation-icon" aria-hidden="true">✓</span>
              <h1>
                {cancelling ? "Registration cancelled" : "Email verified"}
              </h1>
              <p>
                Your registration for <b>{verification.eventTitle}</b> is now:
              </p>
              <Status value={verification.status} />
              <a className="button full" href="/events">
                View public events
              </a>
            </section>
          ) : error ? (
            <Empty
              title={`${cancelling ? "Cancellation" : "Verification"} unsuccessful`}
              body={error}
            />
          ) : (
            <Empty
              title={
                cancelling
                  ? "Cancelling registration"
                  : "Verifying registration"
              }
              body="Please wait…"
            />
          )}
        </main>
      </div>
    );
  if (slug)
    return (
      <div className="public-events">
        <header>
          <span className="brand-mark" aria-hidden="true">2C</span>
          <b>Two Carats Events</b>
          <a href="/events">All public events</a>
        </header>
        <main>
          {event ? (
            <>
              <PageHeader
                eyebrow={humanStatus(event.category)}
                title={event.title}
                description={event.description}
              />
              <section className="public-event-detail card">
                <dl>
                  <dt>Schedule</dt>
                  <dd>{formatDateTime(event.startsAt)}</dd>
                  <dt>Venue</dt>
                  <dd>{event.venueName}</dd>
                  <dt>Registration</dt>
                  <dd>
                    {humanStatus(event.registrationMode)} · maximum{" "}
                    {event.maxPartySize} people
                  </dd>
                </dl>
                <ExternalEventRegistrationForm event={event} />
              </section>
            </>
          ) : error ? (
            <Empty title="Event unavailable" body={error} />
          ) : (
            <Empty title="Loading event" body="Retrieving event details…" />
          )}
        </main>
      </div>
    );
  return (
    <div className="public-events">
      <header>
        <span className="brand-mark" aria-hidden="true">2C</span>
        <b>Two Carats Events</b>
        <a href="/login">Resident sign in</a>
      </header>
      <main>
        <PageHeader
          eyebrow="PUBLIC CALENDAR"
          title="Community events"
          description="Public activities hosted by property management."
        />
        {error && <p className="form-error" role="alert">{error}</p>}
        <section className="event-grid">
          {events.map((item) => (
            <article className="event-card card" key={item.id}>
              <div className="event-card-body">
                <span className="community-category">
                  {humanStatus(item.category)}
                </span>
                <h2>{item.title}</h2>
                <p>{item.description}</p>
                <b>
                  {formatDateTime(item.startsAt)} · {item.venueName}
                </b>
              </div>
              <footer>
                <a className="button" href={`/events/${item.slug}`}>
                  View and register
                </a>
              </footer>
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}

function ExternalEventRegistrationForm({ event }: { event: EventItem }) {
  const [names, setNames] = useState([""]);
  const [buildings, setBuildings] = useState<HubBuilding[]>([]);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (event.visibility === "residents_only")
      apiJson<{ buildings: HubBuilding[] }>(
        "/api/resident-registrations/options",
      )
        .then((result) => setBuildings(result.buildings))
        .catch(() => undefined);
  }, [event.visibility]);
  const submit = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const data = new FormData(formEvent.currentTarget);
    setBusy(true);
    setError("");
    try {
      await apiJson(`/api/public/events/${event.slug}/registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(data.get("name")),
          email: String(data.get("email")),
          attendeeNames: names,
          teamName: String(data.get("teamName")) || undefined,
          claimedBuildingId: String(data.get("claimedBuildingId")) || undefined,
          claimedUnitCode: String(data.get("claimedUnitCode")) || undefined,
          turnstileToken,
          consent: data.get("consent") === "on",
        }),
      });
      setSent(true);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Registration could not be submitted.",
      );
      setBusy(false);
    }
  };
  if (sent)
    return (
      <div className="registration-confirmation">
        <span className="confirmation-icon" aria-hidden="true">✉</span>
        <h2>Check your email</h2>
        <p>
          Your place is assigned only after you verify the email within 30
          minutes. Resident eligibility may still require administrator
          approval.
        </p>
      </div>
    );
  return (
    <form className="external-event-form" onSubmit={submit}>
      <h2>Register without an account</h2>
      <div className="form-row">
        <label>
          Your full name
          <input name="name" required />
        </label>
        <label>
          Email
          <input name="email" type="email" required />
        </label>
      </div>
      {event.registrationMode === "team" && (
        <label>
          Team name
          <input name="teamName" required />
        </label>
      )}
      <fieldset className="attendee-list">
        <legend>Attendees</legend>
        {names.map((name, index) => (
          <div key={index}>
            <input
              value={name}
              onChange={(change) =>
                setNames((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? change.target.value : item,
                  ),
                )
              }
              required
              placeholder={`Person ${index + 1} full name`}
            />
            {names.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setNames((current) =>
                    current.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {event.registrationMode !== "individual" && (
          <button
            type="button"
            className="table-action"
            disabled={names.length >= event.maxPartySize}
            onClick={() => setNames((current) => [...current, ""])}
          >
            + Add person
          </button>
        )}
      </fieldset>
      {event.visibility === "residents_only" && (
        <div className="form-row">
          <label>
            Claimed tower
            <select name="claimedBuildingId" required>
              <option value="">Select tower</option>
              {buildings.map((building) => (
                <option value={building.id} key={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Claimed unit
            <input name="claimedUnitCode" required />
          </label>
        </div>
      )}
      <Turnstile onToken={setTurnstileToken} />
      <label className="checkbox-line">
        <input name="consent" type="checkbox" required /> I agree that my
        registration details may be used to administer this event.
      </label>
      {error && <p className="form-error">{error}</p>}
      <button className="button full" disabled={busy || !turnstileToken}>
        {busy ? "Submitting…" : "Email my verification link"}
      </button>
    </form>
  );
}

type CommunitySettings = {
  communityEnabled: number;
  postModerationMode: "pre_approval" | "post_publication";
  commentsEnabled: number;
  residentClassifiedsEnabled: number;
};
type CommunityPost = {
  id: string;
  authorUserId: string;
  authorDisplayName: string;
  authorTowers?: string;
  category: string;
  title: string;
  body: string;
  status: string;
  moderationReason?: string;
  commentCount: number;
  imageDocumentId?: string;
  imageAltText?: string;
  mediaDocumentId?: string;
  mediaStatus?: string;
  isOwn: number;
  createdAt: number;
  publishedAt?: number;
};
type CommunityComment = {
  id: string;
  body?: string;
  status: string;
  authorDisplayName: string;
  authorTowers?: string;
  isOwn: number;
  createdAt: number;
};
type CommunityRemoval = {
  guidelineCategory:
    | "commercial_solicitation"
    | "harassment"
    | "personal_information"
    | "spam"
    | "misleading_information"
    | "safety_security"
    | "inappropriate_media"
    | "other";
  reason: string;
  restrictionDays: number;
};

function CommunityWarningDialog({ post, onClose, onConfirm }: { post: CommunityPost; onClose: () => void; onConfirm: (reason: string, days: number) => Promise<void> }) {
  const [reason, setReason] = useState(""); const [days, setDays] = useState(0); const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  return <ModalSurface title="Warn resident" onClose={onClose} busy={busy} size="compact"><form onSubmit={async (event) => { event.preventDefault(); setBusy(true); setError(""); try { await onConfirm(reason.trim(), days); } catch (caught) { setError(userFacingApiError(caught, "The warning could not be recorded. Try again.")); } finally { setBusy(false); } }}><p className="modal-copy"><b>{post.authorDisplayName}</b> · “{post.title}”</p><label>Guideline reason<textarea rows={4} value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} required autoFocus /></label><label>Posting restriction<select value={days} onChange={(event) => setDays(Number(event.target.value))}><option value={0}>Warning only</option><option value={1}>1 day</option><option value={3}>3 days</option><option value={7}>7 days</option><option value={30}>30 days</option></select></label>{error && <p className="form-error" role="alert">{error}</p>}<p className="callout">The resident will be notified with this reason and restriction period.</p><div className="form-actions"><button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button><button className="button danger" disabled={busy || reason.trim().length < 3}>{busy ? "Recording…" : "Issue warning"}</button></div></form></ModalSurface>;
}

function CommunityRemovalDialog({
  title,
  onClose,
  onRemove,
}: {
  title: string;
  onClose: () => void;
  onRemove: (removal: CommunityRemoval) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await onRemove({
        guidelineCategory: String(
          data.get("guidelineCategory"),
        ) as CommunityRemoval["guidelineCategory"],
        reason: String(data.get("reason")),
        restrictionDays: Number(data.get("restrictionDays")),
      });
      onClose();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The content could not be removed.",
      );
      setBusy(false);
    }
  };
  return (
    <ModalSurface title={`Remove ${title}`} onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        <p>The author will receive the exact guideline and reason you enter.</p>
        <label>
          Guideline
          <select name="guidelineCategory" required>
            <option value="commercial_solicitation">
              Commercial activity outside Marketplace
            </option>
            <option value="harassment">Harassment or abusive conduct</option>
            <option value="personal_information">
              Privacy or personal information
            </option>
            <option value="misleading_information">
              Misleading information
            </option>
            <option value="spam">Spam or repetitive content</option>
            <option value="safety_security">Safety or security risk</option>
            <option value="inappropriate_media">Inappropriate media</option>
            <option value="other">Other guideline</option>
          </select>
        </label>
        <label>
          Reason shown to the author
          <textarea
            name="reason"
            rows={4}
            required
            minLength={5}
            maxLength={500}
          />
        </label>
        <label>
          Restrict new posts for
          <input
            name="restrictionDays"
            type="number"
            min={0}
            max={365}
            defaultValue={0}
          />
          <small>Use 0 for removal without a posting restriction.</small>
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button danger" disabled={busy}>
            {busy ? "Removing…" : "Remove and notify"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

function CommunityBoard({ admin = false }: { admin?: boolean }) {
  const dialogs = useDialogs();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [settings, setSettings] = useState<CommunitySettings>();
  const [reports, setReports] = useState<
    Array<{
      id: string;
      targetType: string;
      reason: string;
      reporterDisplayName: string;
      createdAt: number;
    }>
  >([]);
  const [compose, setCompose] = useState(false);
  const [category, setCategory] = useState("all");
  const [openPost, setOpenPost] = useState("");
  const [removePost, setRemovePost] = useState<CommunityPost>();
  const [warningPost, setWarningPost] = useState<CommunityPost>();
  const [error, setError] = useState("");
  const load = async () => {
    const result = await apiJson<{
      posts: CommunityPost[];
      settings: CommunitySettings;
    }>("/api/community/posts");
    setPosts(result.posts);
    setSettings(result.settings);
    if (admin) {
      const reportResult = await apiJson<{ reports: typeof reports }>(
        "/api/community/reports",
      );
      setReports(reportResult.reports);
    }
  };
  useEffect(() => {
    if (demoMode) {
      setSettings({
        communityEnabled: 1,
        postModerationMode: "post_publication",
        commentsEnabled: 1,
        residentClassifiedsEnabled: 0,
      });
      setPosts([]);
      return;
    }
    load().catch(() => setError("The Community Board could not be loaded."));
  }, []);
  const moderate = async (
    post: CommunityPost,
    decision: "published" | "rejected",
  ) => {
    await dialogs.reason({ title: `${humanStatus(decision)} post`, label: `${humanStatus(decision)} reason`, minLength: 3, confirmLabel: humanStatus(decision), danger: decision === "rejected", errorFallback: "The moderation decision could not be saved. Review any attached image first.", onSubmit: async (reason) => { await apiJson(`/api/community/posts/${post.id}/moderate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ decision, reason }) }); await load(); } });
  };
  const remove = async (post: CommunityPost, removal: CommunityRemoval) => {
    await apiJson(`/api/community/posts/${post.id}/remove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(removal),
    });
    await load();
  };
  const reviewMedia = async (post: CommunityPost) => {
    if (!post.mediaDocumentId || !(await dialogs.confirm({ title: "Review staging image", description: "Mark this synthetic staging image as manually reviewed? Malware scanning will be bypassed.", confirmLabel: "Mark reviewed" })))
      return;
    try {
      await apiJson(`/api/documents/${post.mediaDocumentId}/staging-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason:
            "Manual administrator review of synthetic Community Board media",
        }),
      });
      await load();
    } catch {
      setError("The image could not be marked as reviewed.");
    }
  };
  const warn = async (post: CommunityPost, reason: string, days: number) => {
    await apiJson("/api/community/warnings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        residentUserId: post.authorUserId,
        relatedType: "post",
        relatedId: post.id,
        reason,
        restrictionDays: Number.isFinite(days) ? days : 0,
      }),
    });
    setError("Warning recorded.");
  };
  const resolveReport = async (reportId: string) => {
    await dialogs.reason({ title: "Resolve community report", label: "Resolution note", minLength: 3, confirmLabel: "Resolve report", errorFallback: "The report could not be resolved.", onSubmit: async (note) => { await apiJson(`/api/community/reports/${reportId}/resolve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note }) }); await load(); } });
  };
  const saveSettings = async (next: Partial<CommunitySettings>) => {
    if (!settings) return;
    const updated = {
      ...settings,
      ...next,
      postModerationMode: "post_publication" as const,
      residentClassifiedsEnabled: 0,
    };
    setSettings(updated);
    if (demoMode) return;
    try {
      await apiJson("/api/community/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          postModerationMode: "post_publication",
          commentsEnabled: Boolean(updated.commentsEnabled),
          residentClassifiedsEnabled: false,
        }),
      });
    } catch {
      setError("Community settings could not be saved.");
    }
  };
  const filtered = posts.filter(
    (post) => category === "all" || post.category === category,
  );
  return (
    <>
      <PageHeader
        eyebrow="RESIDENT COMMUNITY"
        title="Community Board"
        description={
          admin
            ? "Moderate resident discussions and issue guideline-based warnings."
            : "Share updates and connect with verified residents. Your tower is shown; your unit stays private."
        }
        action={
          <button className="button" onClick={() => setCompose(true)}>
            + New post
          </button>
        }
      />
      <section className="card community-commerce-warning">
        <b>Keep buying and selling in Marketplace</b>
        <p>
          Product listings, services, rentals, and other commercial posts are
          not allowed on the Community Board.
        </p>
        <a
          className="table-action"
          href={admin ? "/admin/marketplace" : "/resident/marketplace"}
        >
          Submit a Marketplace listing
        </a>
      </section>
      {admin && settings && (
        <section className="card community-settings">
          <p>
            Text-only posts publish immediately. Posts with images wait for
            security review before they can appear; administrators may remove
            content under community guidelines and notify its author.
          </p>
          <label>
            <input
              type="checkbox"
              checked={Boolean(settings.commentsEnabled)}
              onChange={(event) =>
                void saveSettings({
                  commentsEnabled: event.target.checked ? 1 : 0,
                })
              }
            />{" "}
            Comments enabled
          </label>
        </section>
      )}
      {admin && reports.length > 0 && (
        <section className="card report-queue">
          <CardTitle
            title="Open content reports"
            badge={`${reports.length} open`}
          />
          {reports.map((report) => (
            <div key={report.id}>
              <span>
                <b>{report.reporterDisplayName}</b>
                <small>
                  {humanStatus(report.targetType)} ·{" "}
                  {formatDateTime(report.createdAt)}
                </small>
              </span>
              <p>{report.reason}</p>
              <button
                className="table-action"
                onClick={() => void resolveReport(report.id)}
              >
                Resolve
              </button>
            </div>
          ))}
        </section>
      )}
      {compose && (
        <CommunityPostForm
          admin={admin}
          onClose={() => setCompose(false)}
          onCreated={() => {
            setCompose(false);
            void load();
          }}
        />
      )}
      {removePost && (
        <CommunityRemovalDialog
          title={`“${removePost.title}”`}
          onClose={() => setRemovePost(undefined)}
          onRemove={(removal) => remove(removePost, removal)}
        />
      )}
      {warningPost && <CommunityWarningDialog post={warningPost} onClose={() => setWarningPost(undefined)} onConfirm={async (reason, days) => { await warn(warningPost, reason, days); setWarningPost(undefined); }} />}
      <div className="community-filters">
        {[
          "all",
          "general",
          "recommendations",
          "lost_found",
          "events",
          "help_requests",
        ].map((value) => (
          <button
            key={value}
            className={category === value ? "active" : ""}
            onClick={() => setCategory(value)}
          >
            {value === "all" ? "All posts" : humanStatus(value)}
          </button>
        ))}
      </div>
      {error && (
        <p
          className={
            error === "Warning recorded." ? "form-success" : "form-error"
          }
        >
          {error}
        </p>
      )}
      <section className="community-feed">
        {filtered.map((post) => (
          <article className="community-post card" key={post.id}>
            <header>
              <span className="avatar small">
                {initials(post.authorDisplayName)}
              </span>
              <div>
                <b>{post.authorDisplayName}</b>
                <small>
                  {post.authorTowers || "Resident"} ·{" "}
                  {formatDateTime(post.publishedAt ?? post.createdAt)}
                </small>
              </div>
              <Status value={post.status} />
            </header>
            {post.status === "pending_review" && post.mediaStatus && post.mediaStatus !== "clean" && <p className="moderation-note">Image status: {humanStatus(post.mediaStatus)}. Review the image before approving this post.</p>}
            {post.imageDocumentId && (
              <img
                className="community-image"
                src={`/files/${post.imageDocumentId}`}
                alt={post.imageAltText ?? "Community post image"}
                width="16"
                height="9"
                loading="lazy"
                decoding="async"
              />
            )}
            <span className="community-category">
              {humanStatus(post.category)}
            </span>
            <h2>{post.title}</h2>
            <p>{post.body}</p>
            {post.moderationReason && post.status !== "published" && (
              <p className="moderation-note">{post.moderationReason}</p>
            )}
            <footer>
              <button
                onClick={() =>
                  setOpenPost((current) => (current === post.id ? "" : post.id))
                }
              >
                ◇ {post.commentCount} comments
              </button>
              {!admin && (
                <button
                  onClick={() => void dialogs.reason({
                    title: "Report community post",
                    label: "Why are you reporting this post?",
                    minLength: 3,
                    confirmLabel: "Submit report",
                    errorFallback: "The report could not be submitted.",
                    onSubmit: async (reason) => {
                      await apiJson("/api/community/reports", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ targetType: "post", targetId: post.id, reason }),
                      });
                    },
                  })}
                >
                  Report
                </button>
              )}
              {admin &&
                post.mediaDocumentId &&
                post.mediaStatus !== "clean" && (
                  <button onClick={() => void reviewMedia(post)}>
                    Review image
                  </button>
                )}
              {admin && post.status === "pending_review" && (
                <button onClick={() => void moderate(post, "published")}>
                  Approve
                </button>
              )}
              {admin && post.status === "pending_review" && (
                <button onClick={() => void moderate(post, "rejected")}>
                  Reject
                </button>
              )}
              {admin && post.status === "published" && (
                <button
                  className="danger-text"
                  onClick={() => setRemovePost(post)}
                >
                  Remove post
                </button>
              )}
              {admin && (
                <button onClick={() => setWarningPost(post)}>Warn resident</button>
              )}
            </footer>
            {openPost === post.id && (
              <CommunityComments
                postId={post.id}
                admin={admin}
                enabled={Boolean(settings?.commentsEnabled)}
              />
            )}
          </article>
        ))}
        {!filtered.length && (
          <Empty
            title="No community posts"
            body={
              admin
                ? "Published and pending posts will appear here."
                : "Start the first resident discussion."
            }
          />
        )}
      </section>
    </>
  );
}

function CommunityPostForm({
  admin,
  onClose,
  onCreated,
}: {
  admin: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const dialogs = useDialogs();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const image = data.get("image");
    setBusy(true);
    setError("");
    try {
      let media: Array<{ documentId: string; altText: string }> = [];
      if (image instanceof File && image.size) {
        if (admin && !(await dialogs.confirm({ title: "Review staging image", description: "Continue with a synthetic staging image and manual security-review override?", confirmLabel: "Continue" }))) {
          setBusy(false);
          return;
        }
        media = [
          {
            documentId: await uploadHubImage(image, "community_image", admin),
            altText: String(data.get("altText")),
          },
        ];
      }
      const result = await apiJson<{ post: { status: string } }>(
        "/api/community/posts",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            category: String(data.get("category")),
            title: String(data.get("title")),
            body: String(data.get("body")),
            media,
          }),
        },
      );
      await dialogs.notice({ title: result.post.status === "published" ? "Post published" : "Post submitted", description: result.post.status === "published" ? "Your post is now visible to the community." : "Your post was submitted for administrator review." });
      onCreated();
    } catch (caught) {
      setError(userFacingApiError(caught, "The post could not be submitted."));
      setBusy(false);
    }
  };
  return (
    <ModalSurface title="New community post" onClose={onClose} busy={busy} className="hub-form">
      <form onSubmit={submit}>
        <p className="callout">
          Do not post products, services, rentals, or other sales here.{" "}
          <a href={admin ? "/admin/marketplace" : "/resident/marketplace"}>
            Send those to Marketplace for review.
          </a>
        </p>
        <label>
          Category
          <select name="category">
            <option value="general">General community</option>
            <option value="recommendations">Recommendations</option>
            <option value="lost_found">Lost and found</option>
            <option value="events">Events and activities</option>
            <option value="help_requests">Help and requests</option>
          </select>
        </label>
        <label>
          Title
          <input name="title" required minLength={4} />
        </label>
        <label>
          Post
          <textarea name="body" rows={5} required minLength={10} />
        </label>
        <label>
          Image (optional)
          <input
            name="image"
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
          />
        </label>
        <label>
          Image description
          <input
            name="altText"
            placeholder="Describe the image for accessibility"
          />
        </label>
        <p className="callout">
          Your first name, last initial, and tower will be displayed. Your unit
          number remains private. Posts publish immediately and remain subject
          to community guidelines.
        </p>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>
            {busy ? "Submitting…" : "Publish post"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

function CommunityComments({
  postId,
  admin,
  enabled,
}: {
  postId: string;
  admin: boolean;
  enabled: boolean;
}) {
  const dialogs = useDialogs();
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [removeComment, setRemoveComment] = useState<CommunityComment>();
  const [error, setError] = useState("");
  const load = () =>
    apiJson<{ comments: CommunityComment[] }>(
      `/api/community/posts/${postId}/comments`,
    ).then((result) => setComments(result.comments));
  useEffect(() => {
    if (!demoMode)
      load().catch(() => setError("Comments could not be loaded."));
  }, [postId]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      await apiJson(`/api/community/posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: String(data.get("body")) }),
      });
      form.reset();
      await load();
    } catch (caught) {
      setError(userFacingApiError(caught, "The comment could not be posted."));
    }
  };
  const remove = async (commentId: string) => {
    if (!(await dialogs.confirm({ title: "Delete comment", description: "Delete this comment?", confirmLabel: "Delete", danger: true }))) return;
    try {
      await apiJson(`/api/community/comments/${commentId}`, {
        method: "DELETE",
      });
      await load();
    } catch {
      setError("The comment could not be deleted.");
    }
  };
  const removeAsAdmin = async (
    comment: CommunityComment,
    removal: CommunityRemoval,
  ) => {
    await apiJson(`/api/community/comments/${comment.id}/remove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(removal),
    });
    await load();
  };
  return (
    <div className="community-comments">
      {removeComment && (
        <CommunityRemovalDialog
          title="comment"
          onClose={() => setRemoveComment(undefined)}
          onRemove={(removal) => removeAsAdmin(removeComment, removal)}
        />
      )}
      {comments.map((comment) => (
        <div key={comment.id}>
          <b>
            {comment.authorDisplayName}{" "}
            <small>· {comment.authorTowers || "Resident"}</small>
          </b>
          <p>
            {comment.status === "published" ? comment.body : "Comment removed"}
          </p>
          <span>{formatDateTime(comment.createdAt)}</span>
          {comment.status === "published" && admin && (
            <button
              className="danger-text"
              onClick={() => setRemoveComment(comment)}
            >
              Remove
            </button>
          )}
          {comment.status === "published" &&
            !admin &&
            Boolean(comment.isOwn) && (
              <button onClick={() => void remove(comment.id)}>Delete</button>
            )}
        </div>
      ))}
      {enabled && (
        <form onSubmit={submit}>
          <input
            name="body"
            required
            maxLength={2000}
            placeholder="Write a comment…"
          />
          <button className="table-action">Post</button>
        </form>
      )}
      {!enabled && <p>Comments are disabled by management.</p>}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

type MarketplaceAd = {
  id: string;
  title: string;
  body: string;
  category: string;
  status: string;
  vendorName: string;
  publisherType?: string;
  vendorReviewStatus: string;
  priceCentavos?: number;
  audienceBuildings?: string;
  audienceRelationship: string;
  contactUrl?: string;
  disclaimer: string;
  expiresAt: number;
  imageDocumentId?: string;
  imageAltText?: string;
};

function Marketplace({ admin = false, canPublishDirect = false }: { admin?: boolean; canPublishDirect?: boolean }) {
  const { has, loaded } = usePermissions();
  const canReview = admin && (demoMode || has("marketplace.review"));
  const [ads, setAds] = useState<MarketplaceAd[]>([]);
  const [buildings, setBuildings] = useState<HubBuilding[]>([]);
  const [compose, setCompose] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [submissionRefresh, setSubmissionRefresh] = useState(0);
  const [error, setError] = useState("");
  const load = () =>
    apiJson<{ ads: MarketplaceAd[] }>("/api/marketplace/ads").then((result) =>
      setAds(result.ads),
    );
  useEffect(() => {
    if (admin && !demoMode && !loaded) return;
    if (admin && !canReview) return;
    if (demoMode) {
      setBuildings([
        { id: "tower-1", name: "Tower 1" },
        { id: "tower-2", name: "Tower 2" },
        { id: "tower-3", name: "Tower 3" },
      ]);
      return;
    }
    Promise.all([
      load(),
      apiJson<{ buildings: HubBuilding[] }>("/api/buildings").then((result) =>
        setBuildings(result.buildings),
      ),
    ]).catch(() => setError("The Marketplace could not be loaded."));
  }, [admin, canReview, loaded]);
  if (loaded && !demoMode && admin && !canReview)
    return <><header className="page-header"><div><small>ADMIN-REVIEWED LISTINGS</small><h1>Community Marketplace</h1><p>This workspace is not included in your assigned permissions.</p></div></header><p className="callout">Ask a property administrator to assign Marketplace review access if you need this workspace.</p></>;
  return (
    <>
      <PageHeader
        eyebrow="ADMIN-REVIEWED LISTINGS"
        title="Community Marketplace"
        description={
          admin
            ? canPublishDirect ? "Review resident and external requests, or publish an advertisement directly." : "Review resident and external requests and record decisions."
            : "Browse reviewed offers or request a listing. Two Carats does not process purchases."
        }
        action={
          admin && canReview && canPublishDirect ? (
            <button className="button" onClick={() => setCompose(true)}>
              + Publish directly
            </button>
          ) : (
            <button className="button" onClick={() => setRequesting(true)}>
              + Request a listing
            </button>
          )
        }
      />
      <p className="marketplace-public-link">
        Not a registered resident?{" "}
        <a href="/marketplace/submit">
          Use the public Marketplace submission form
        </a>
        .
      </p>
      {compose && (
        <MarketplaceForm
          buildings={buildings}
          onClose={() => setCompose(false)}
          onCreated={() => {
            setCompose(false);
            void load();
          }}
        />
      )}
      {requesting && (
        <MarketplaceSubmissionForm
          buildings={buildings}
          onClose={() => setRequesting(false)}
          onSubmitted={() => {
            setRequesting(false);
            setSubmissionRefresh((value) => value + 1);
          }}
        />
      )}
      <MarketplaceSubmissionList admin={admin} canReview={canReview} refreshKey={submissionRefresh} />
      {error && <p className="form-error">{error}</p>}
      <section className="marketplace-grid">
        {ads.map((ad) => (
          <article className="marketplace-card card" key={ad.id}>
            {ad.imageDocumentId && (
              <img
                src={`/files/${ad.imageDocumentId}`}
                alt={ad.imageAltText ?? "Marketplace advertisement image"}
                width="3"
                height="2"
                loading="lazy"
                decoding="async"
              />
            )}
            <span>
              {ad.publisherType?.startsWith("resident_")
                ? "Resident listing"
                : "Third-party listing"}{" "}
              · Admin reviewed
            </span>
            <h2>{ad.title}</h2>
            <b>{ad.vendorName}</b>
            <p>{ad.body}</p>
            {ad.priceCentavos != null && (
              <strong>{formatPeso(ad.priceCentavos)}</strong>
            )}
            <small>
              {ad.audienceBuildings ?? "Selected buildings"} · Expires{" "}
              {formatDateTime(ad.expiresAt)}
            </small>
            <em>{ad.disclaimer}</em>
            {ad.contactUrl && (
              <a
                className="button marketplace-contact"
                href={ad.contactUrl}
                target="_blank"
                rel="noreferrer"
              >
                Contact{" "}
                {ad.publisherType?.startsWith("resident_")
                  ? "resident"
                  : "vendor"}
              </a>
            )}
            {admin && <Status value={ad.status} />}
          </article>
        ))}
        {!ads.length && (
          <Empty
            title="No marketplace listings"
            body="Approved resident and third-party listings will appear here."
          />
        )}
      </section>
    </>
  );
}

function MarketplaceForm({
  buildings,
  onClose,
  onCreated,
}: {
  buildings: HubBuilding[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const dialogs = useDialogs();
  const [selected, setSelected] = useState(
    buildings.map((building) => building.id),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected.length) return setError("Select at least one building.");
    const data = new FormData(event.currentTarget);
    const image = data.get("image");
    setBusy(true);
    setError("");
    try {
      let media: Array<{ documentId: string; altText: string }> = [];
      if (image instanceof File && image.size) {
        if (!(await dialogs.confirm({ title: "Review staging image", description: "Continue with a synthetic staging image and manual security-review override?", confirmLabel: "Continue" }))) {
          setBusy(false);
          return;
        }
        media = [
          {
            documentId: await uploadHubImage(image, "marketplace_image", true),
            altText: String(data.get("altText")),
          },
        ];
      }
      const expiry = String(data.get("expiresAt"));
      await apiJson("/api/marketplace/ads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vendorName: String(data.get("vendorName")),
          reviewNotes: String(data.get("reviewNotes")),
          contactEmail: String(data.get("contactEmail")) || undefined,
          title: String(data.get("title")),
          body: String(data.get("body")),
          category: String(data.get("category")),
          buildingIds: selected,
          audienceRelationship: String(data.get("audienceRelationship")),
          contactUrl: String(data.get("contactUrl")) || undefined,
          disclaimer: String(data.get("disclaimer")),
          expiresAt: Math.floor(
            new Date(`${expiry}T23:59:59+08:00`).getTime() / 1000,
          ),
          media,
        }),
      });
      onCreated();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The advertisement could not be published.",
      );
      setBusy(false);
    }
  };
  return (
    <ModalSurface title="Publish reviewed advertisement" onClose={onClose} busy={busy} className="hub-form">
      <form onSubmit={submit}>
        <label>
          Vendor legal name
          <input name="vendorName" required />
        </label>
        <label>
          Vendor review notes
          <textarea name="reviewNotes" required rows={3} />
        </label>
        <label>
          Vendor email
          <input name="contactEmail" type="email" />
        </label>
        <label>
          Advertisement title
          <input name="title" required />
        </label>
        <label>
          Description
          <textarea name="body" required rows={4} />
        </label>
        <div className="form-row">
          <label>
            Category
            <input name="category" required placeholder="e.g. Home services" />
          </label>
          <label>
            Audience
            <select name="audienceRelationship">
              <option value="all">Owners and tenants</option>
              <option value="owner">Owners only</option>
              <option value="tenant">Tenants only</option>
            </select>
          </label>
        </div>
        <fieldset>
          <legend>Buildings</legend>
          <div className="check-grid">
            {buildings.map((building) => (
              <label key={building.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(building.id)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, building.id]
                        : current.filter((id) => id !== building.id),
                    )
                  }
                />
                {building.name}
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Contact or offer URL
          <input name="contactUrl" type="url" />
        </label>
        <label>
          Expiration date
          <input name="expiresAt" type="date" required />
        </label>
        <label>
          Advertisement image
          <input
            name="image"
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
          />
        </label>
        <label>
          Image description
          <input name="altText" />
        </label>
        <label>
          Disclaimer
          <textarea
            name="disclaimer"
            required
            rows={2}
            defaultValue="This advertisement is from a reviewed third party. Two Carats does not process or guarantee purchases."
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="button" disabled={busy}>
            {busy ? "Publishing…" : "Publish advertisement"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

function Imports() {
  type HeaderSelector = { normalized: string; occurrence: number };
  type SourceHeader = HeaderSelector & { source: string };
  type ImportTemplate = { id: string; importType: string; name: string; version: number; sourceHeaderSelectorsJson: string; status: string };
  const fieldsByType: Record<string, string[]> = {
    units: ["building_code", "unit_code", "floor", "status"],
    resident_links: ["email", "name", "relationship_type", "building_code", "unit_code"],
    unit_directory: ["building_code", "unit_code", "name", "relationship_type", "email", "phone", "invite_to_app"],
    payments: ["provider_code", "external_reference", "received_date", "amount", "payer_name", "payment_method", "customer_reference_number"],
    meter_readings: ["meter_serial", "reading_date", "reading_value", "utility_type"],
    utility_rates: ["utility_type", "unit_of_measure", "rate", "effective_from", "effective_to", "source_reference"],
    opening_balances: ["building_code", "unit_code", "category", "amount", "effective_date", "reference", "description"],
  };
  const [file, setFile] = useState<File>();
  const [type, setType] = useState("units");
  const [jobId, setJobId] = useState("");
  const [previewId, setPreviewId] = useState("");
  const [status, setStatus] = useState("idle");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sourceHeaders, setSourceHeaders] = useState<SourceHeader[]>([]);
  const [templates, setTemplates] = useState<ImportTemplate[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [mapping, setMapping] = useState<Record<string, HeaderSelector | string>>({});
  const [history, setHistory] = useState<Array<{ id: string; type: string; status: string; filename: string; createdAt: number }>>([]);
  const [balancingAccounts, setBalancingAccounts] = useState<Array<{ id: string; code: string; name: string; active: number }>>([]);
  const [balancingAccountId, setBalancingAccountId] = useState("");
  const [preview, setPreview] = useState<{
    validRows: number;
    errorRows: number;
    rows: { rowNumber: number; fieldErrorsJson: string }[];
  }>();
  const downloadTemplate = () => {
    const templates: Record<string, string> = {
      units: "building_code,unit_code,floor,status\n",
      resident_links: "email,name,relationship_type,building_code,unit_code\n",
      unit_directory: "building_code,unit_code,name,relationship_type,email,phone,invite_to_app\n",
      payments: "provider_code,external_reference,received_date,amount,payer_name,payment_method,customer_reference_number\n",
      meter_readings: "meter_serial,reading_date,reading_value,utility_type\n",
      utility_rates: "utility_type,unit_of_measure,rate,effective_from,effective_to,source_reference\n",
      opening_balances: "building_code,unit_code,category,amount,effective_date,reference,description\n",
    };
    const url = URL.createObjectURL(new Blob([templates[type]], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${type}-template.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const poll = async (id: string) => {
    for (let attempt = 0; attempt < 60; attempt += 1) {
      const response = await fetch(`/api/imports/${id}`);
      const result = (await response.json()) as { importJob?: { status: string; validRows?: number; errorRows?: number; sourceMetadataJson?: string; currentPreviewId?: string | null }; previews?: { id: string }[] };
      if (!response.ok || !result.importJob) throw new Error("Could not read import status.");
      setStatus(result.importJob.status);
      if (result.importJob.sourceMetadataJson) {
        try {
          const metadata = JSON.parse(result.importJob.sourceMetadataJson) as { sheets?: string[]; headers?: SourceHeader[] };
          setSheetNames(metadata.sheets ?? []);
          setSourceHeaders(metadata.headers ?? []);
        } catch { setSheetNames([]); setSourceHeaders([]); }
      }
      if (result.importJob.currentPreviewId) setPreviewId(result.importJob.currentPreviewId);
      if (["awaiting_mapping", "ready_for_review", "needs_correction", "failed", "committed"].includes(result.importJob.status)) {
        const latest = result.previews?.[0]?.id;
        if (latest) setPreviewId(latest);
        return result.importJob;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 500));
    }
    throw new Error("Import processing timed out.");
  };
  const refreshHistory = async () => {
    if (demoMode) return;
    const response = await fetch("/api/imports?limit=30");
    if (response.ok) setHistory(((await response.json()) as { imports?: typeof history }).imports ?? []);
  };
  const openHistoryJob = async (id: string) => {
    try {
      const response = await fetch(`/api/imports/${id}`);
      const result = (await response.json()) as { importJob?: { id: string; type: string; status: string; validRows?: number; errorRows?: number; sourceMetadataJson?: string; currentPreviewId?: string | null }; previews?: { id: string }[] };
      if (!response.ok || !result.importJob) throw new Error("Could not open import job.");
      setJobId(id); setType(result.importJob.type); setStatus(result.importJob.status); setFile(undefined); setConfirmed(false); setError("");
      setPreviewId(result.importJob.currentPreviewId ?? result.previews?.[0]?.id ?? "");
      try {
        const metadata = JSON.parse(result.importJob.sourceMetadataJson ?? "{}") as { sheets?: string[]; headers?: SourceHeader[] };
        setSheetNames(metadata.sheets ?? []);
        setSourceHeaders(metadata.headers ?? []);
      } catch { setSheetNames([]); setSourceHeaders([]); }
      const rowsResponse = await fetch(`/api/imports/${id}/rows?limit=100`);
      const rowsResult = (await rowsResponse.json()) as { rows?: { rowNumber: number; fieldErrorsJson: string }[] };
      setPreview({ validRows: result.importJob.validRows ?? 0, errorRows: result.importJob.errorRows ?? 0, rows: rowsResult.rows ?? [] });
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not open import job."); }
  };
  useEffect(() => {
    void refreshHistory();
    if (!demoMode) void fetch("/api/accounting").then((response) => response.ok ? response.json() as Promise<{ accounts?: typeof balancingAccounts }> : null).then((result) => setBalancingAccounts(result?.accounts?.filter((account) => account.active) ?? [])).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (demoMode) return;
    void fetch("/api/import-templates")
      .then((response) => response.ok ? response.json() as Promise<{ templates?: ImportTemplate[] }> : { templates: [] })
      .then((result) => setTemplates(result.templates ?? []))
      .catch(() => setTemplates([]));
  }, [type]);
  const requestPreview = async (id: string, selectedSheet?: string) => {
    try {
      setStatus("previewing");
      const previewResponse = await fetch(`/api/imports/${id}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sheetName: selectedSheet || undefined, templateId: templateId || undefined, mapping }) });
      if (!previewResponse.ok) throw new Error(((await previewResponse.json()) as { message?: string }).message ?? "Preview could not be started.");
      await poll(id);
      await refreshHistory();
      const rowsResponse = await fetch(`/api/imports/${id}/rows?limit=100`);
      const rowsResult = (await rowsResponse.json()) as { rows?: { rowNumber: number; fieldErrorsJson: string }[] };
      const detailResponse = await fetch(`/api/imports/${id}`);
      const detailResult = (await detailResponse.json()) as { importJob?: { validRows: number; errorRows: number } };
      setPreview({ validRows: detailResult.importJob?.validRows ?? 0, errorRows: detailResult.importJob?.errorRows ?? 0, rows: rowsResult.rows ?? [] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Preview failed.");
      setStatus("failed");
    }
  };
  const selectFile = async (selected?: File) => {
    setFile(selected);
    setPreview(undefined);
    setJobId("");
    setPreviewId("");
    setStatus("idle");
    setError("");
    setConfirmed(false);
    setSheetName("");
    setSourceHeaders([]);
    setTemplateId("");
    setTemplateName("");
    setMapping({});
    if (!selected) return;
    if (demoMode) {
      setPreview({
        validRows: 124,
        errorRows: 2,
        rows: [
          { rowNumber: 19, fieldErrorsJson: JSON.stringify([{ message: "amount must use 1234.56 format" }]) },
          { rowNumber: 73, fieldErrorsJson: JSON.stringify([{ message: "building_code is unknown" }]) },
        ],
      });
      return;
    }
    try {
      setStatus("uploading");
      const form = new FormData();
      form.set("type", type);
      form.set("file", selected);
      if (sheetName) form.set("sheetName", sheetName);
      const response = await fetch("/api/imports", { method: "POST", body: form });
      const result = (await response.json()) as { importJob?: { id: string; status: string; source?: { sheetNames?: string[] } }; message?: string };
      if (!response.ok || !result.importJob) throw new Error(result.message ?? "Upload failed.");
      const id = result.importJob.id;
      setJobId(id);
      setSheetNames(result.importJob.source?.sheetNames ?? []);
      await poll(id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Import failed.");
      setStatus("failed");
    }
  };
  const commit = async () => {
    if (demoMode) return setConfirmed(true);
    if (!jobId) return;
    try {
      if (type === "opening_balances" && !balancingAccountId) throw new Error("Select the opening-balance balancing account first.");
      const detail = (await (await fetch(`/api/imports/${jobId}`)).json()) as { importJob: { canonicalSha256: string } };
      const response = await fetch(`/api/imports/${jobId}/commit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expectedPreviewId: previewId, canonicalSha256: detail.importJob.canonicalSha256, idempotencyKey: crypto.randomUUID(), reviewNote: "Reviewed in Data Imports workspace", settings: type === "opening_balances" ? { balancingAccountId } : undefined }) });
      const result = (await response.json()) as { message?: string };
      if (!response.ok) throw new Error(result.message ?? "Commit failed.");
      setConfirmed(true);
      setStatus("committed");
      await refreshHistory();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Commit failed.");
    }
  };
  const importFields = fieldsByType[type] ?? [];
  const selectorKey = (selector: HeaderSelector | string | undefined) => {
    if (!selector) return "";
    return JSON.stringify(typeof selector === "string" ? { normalized: selector, occurrence: 0 } : selector);
  };
  const activeTemplates = templates.filter((template) => template.importType === type && template.status === "active");
  const useTemplate = (selectedId: string) => {
    setTemplateId(selectedId);
    const selected = activeTemplates.find((template) => template.id === selectedId);
    if (!selected) return setMapping({});
    try { setMapping(JSON.parse(selected.sourceHeaderSelectorsJson) as Record<string, HeaderSelector | string>); } catch { setMapping({}); }
  };
  const saveTemplate = async () => {
    if (!templateName.trim()) return setError("Enter a template name before saving the mapping.");
    try {
      const response = await fetch("/api/import-templates", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ importType: type, name: templateName.trim(), selectors: mapping }) });
      const result = (await response.json()) as { template?: { id: string; importType: string; name: string; version: number }; message?: string };
      if (!response.ok || !result.template) throw new Error(result.message ?? "Template could not be saved.");
      const saved = { ...result.template, sourceHeaderSelectorsJson: JSON.stringify(mapping), status: "active" };
      setTemplates((current) => [saved, ...current.filter((template) => !(template.importType === saved.importType && template.name === saved.name && template.version === saved.version))]);
      setTemplateId(saved.id);
      setTemplateName("");
      setError("");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Template could not be saved."); }
  };
  return (
    <>
      <PageHeader
        eyebrow="BULK ONBOARDING"
        title="Data Imports"
        description="Governed CSV/XLSX imports with immutable previews, reviewer approval, and traceable commits."
        action={
          <button className="button secondary" onClick={downloadTemplate}>Download template</button>
        }
      />
      <section className="import-layout">
        <div className="card">
          <CardTitle title="1 · Select import" />
          <label className="field-label">
            Import type
            <select
              value={type}
              onChange={(event) => { setType(event.target.value); setTemplateId(""); setTemplateName(""); setMapping({}); }}
            >
              <option value="units">Units</option>
              <option value="resident_links">Resident links</option>
              <option value="unit_directory">Unit directory</option>
              <option value="payments">Payments</option>
              <option value="meter_readings">Meter readings</option>
              <option value="utility_rates">Utility rates</option>
              <option value="opening_balances">Opening balances</option>
            </select>
          </label>
          <label className="upload-zone big">
            ⇧<b>{file?.name ?? "Drop a CSV or XLSX here"}</b>
            <span>UTF-8/Windows-1252 CSV · OOXML XLSX · formulas rejected</span>
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) => void selectFile(event.target.files?.[0])}
            />
          </label>
          {type === "opening_balances" && <label className="field-label">Balancing account<select value={balancingAccountId} onChange={(event) => setBalancingAccountId(event.target.value)}><option value="">Select an active account</option>{balancingAccounts.map((account) => <option key={account.id} value={account.id}>{account.code} · {account.name}</option>)}</select></label>}
          {sheetNames.length > 1 && <label className="field-label">Worksheet<select value={sheetName} onChange={(event) => setSheetName(event.target.value)}><option value="">Select a visible worksheet</option>{sheetNames.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>}
          {jobId && ["awaiting_mapping", "needs_correction", "ready_for_review", "failed"].includes(status) && <div className="mapping-panel">
            <label className="field-label">Mapping template<select value={templateId} onChange={(event) => useTemplate(event.target.value)}><option value="">Manual mapping</option>{activeTemplates.map((template) => <option key={template.id} value={template.id}>{template.name} · v{template.version}</option>)}</select></label>
            <p className="muted">Map each import field to a source column. Duplicate headers are listed by occurrence.</p>
            {importFields.map((field) => <label className="field-label" key={field}>{field}<select value={selectorKey(mapping[field])} onChange={(event) => {
              setTemplateId("");
              if (!event.target.value) return setMapping((current) => { const next = { ...current }; delete next[field]; return next; });
              setMapping((current) => ({ ...current, [field]: JSON.parse(event.target.value) as HeaderSelector }));
            }}><option value="">Automatic header matching</option>{sourceHeaders.map((header) => <option key={`${header.normalized}:${header.occurrence}`} value={JSON.stringify({ normalized: header.normalized, occurrence: header.occurrence })}>{header.source} · occurrence {header.occurrence + 1}</option>)}</select></label>)}
            <div className="form-actions"><input value={templateName} onChange={(event) => setTemplateName(event.target.value)} placeholder="New template name" aria-label="New template name" /><button className="button secondary" type="button" onClick={() => void saveTemplate()}>Save mapping</button></div>
            <button className="button secondary full" type="button" disabled={sheetNames.length > 1 && !sheetName} onClick={() => void requestPreview(jobId, sheetName)}>Build preview</button>
          </div>}
          {!demoMode && history.length > 0 && <div className="import-history"><b>Review queue</b>{history.slice(0, 8).map((item) => <button className="button secondary full" type="button" key={item.id} onClick={() => void openHistoryJob(item.id)}>{item.type} · {item.status.replaceAll("_", " ")} · {item.filename}</button>)}</div>}
          {status !== "idle" && <p className="muted">State: {status.replaceAll("_", " ")}</p>}
          {error && <p className="form-error">{error}</p>}
        </div>
        <div className="card">
          <CardTitle title="2 · Validate & confirm" />
          {preview ? (
            <>
              <div className="validation-summary">
                <b>✓ {preview.validRows} valid rows</b>
                <span>{preview.errorRows} rows need attention</span>
              </div>
              {preview.rows
                .filter((row) => row.fieldErrorsJson !== "[]")
                .slice(0, 5)
                .map((row) => (
                  <div className="error-row" key={row.rowNumber}>
                    <span>Row {row.rowNumber}</span>
                    <b>{JSON.parse(row.fieldErrorsJson)[0]?.message ?? "Needs attention"}</b>
                    <small>Correct this row and upload the CSV again.</small>
                  </div>
                ))}
              {jobId && preview?.errorRows ? <a className="button secondary full" href={`/api/imports/${jobId}/errors.csv`}>Download exception file</a> : null}
              <button
                className="button full"
                disabled={!preview.validRows || confirmed || status !== "ready_for_review"}
                onClick={() => void commit()}
              >
                {confirmed
                  ? "Import committed"
                  : `Confirm ${preview.validRows} valid rows`}
              </button>
            </>
          ) : (
            <Empty
              title="No file selected"
              body="Choose a template type and CSV to run the full preview."
            />
          )}
        </div>
      </section>
    </>
  );
}

type AuditEvent = {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  requestId?: string;
  metadataJson: string;
  createdAt: number;
  actorName?: string;
};

function Audit() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    demoMode ? "ready" : "loading",
  );
  const [expandedId, setExpandedId] = useState<string>();

  useEffect(() => {
    if (demoMode) return;
    apiJson<{ events?: AuditEvent[] }>("/api/audit?limit=100")
      .then((result) => {
        setEvents(result.events ?? []);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  const exportCsv = () => {
    const cells = (value: unknown) =>
      `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = [
      [
        "timestamp",
        "action",
        "actor",
        "target_type",
        "target_id",
        "request_id",
        "metadata",
      ],
      ...events.map((event) => [
        new Date(event.createdAt * 1000).toISOString(),
        event.action,
        event.actorName ?? "System",
        event.targetType,
        event.targetId,
        event.requestId ?? "",
        event.metadataJson,
      ]),
    ]
      .map((row) => row.map(cells).join(","))
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `two-carats-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        eyebrow="APPEND-ONLY RECORD"
        title="Audit trail"
        description="PII-minimized business and security events for this tenant."
      />
      <section className="card">
        <div className="panel-tools">
          <b>RECENT EVENTS</b>
          <button disabled={!events.length} onClick={exportCsv}>
            Export CSV
          </button>
        </div>
        {status === "loading" ? (
          <Empty
            title="Loading audit events"
            body="Reading the tenant's append-only history."
          />
        ) : status === "error" ? (
          <Empty
            title="Audit trail unavailable"
            body="Refresh the page to try loading the events again."
          />
        ) : !events.length ? (
          <Empty
            title="No audit events"
            body="Business and security actions will appear here."
          />
        ) : (
          <div className="audit-list">
            {events.map((event, index) => (
              <div key={event.id}>
                <span className="audit-icon" aria-hidden="true">{index + 1}</span>
                <span>
                  <b>{event.action}</b>
                  <small>
                    {event.actorName ?? "System"} ·{" "}
                    {formatDateTime(event.createdAt)} ·{" "}
                    {humanStatus(event.targetType)} {event.targetId}
                  </small>
                </span>
                <code>{(event.requestId ?? event.id).slice(0, 8)}</code>
                <button
                  aria-expanded={expandedId === event.id}
                  onClick={() =>
                    setExpandedId(
                      expandedId === event.id ? undefined : event.id,
                    )
                  }
                >
                  {expandedId === event.id ? "Hide" : "Details"}
                </button>
                {expandedId === event.id && (
                  <pre className="audit-details">
                    {JSON.stringify(
                      JSON.parse(event.metadataJson || "{}"),
                      null,
                      2,
                    )}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

type RegistrationUnitDraft = {
  key: string;
  buildingId: string;
  unitCode: string;
  relationshipType: "owner" | "co_owner" | "tenant";
};

function ResidentRegistration() {
  const [buildings, setBuildings] = useState<
    Array<{ id: string; code: string; name: string }>
  >([]);
  const [unitLinks, setUnitLinks] = useState<RegistrationUnitDraft[]>([
    {
      key: crypto.randomUUID(),
      buildingId: "",
      unitCode: "",
      relationshipType: "owner",
    },
  ]);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (demoMode)
      return setBuildings([
        { id: "building-tower-1", code: "T1", name: "Tower 1" },
        { id: "building-north", code: "T2", name: "Tower 2" },
        { id: "building-east", code: "T3", name: "Tower 3" },
      ]);
    apiJson<{ buildings: Array<{ id: string; code: string; name: string }> }>(
      "/api/resident-registrations/options",
    )
      .then((result) => setBuildings(result.buildings))
      .catch(() => setError("Registration options could not be loaded."));
  }, []);

  const updateUnit = (key: string, changes: Partial<RegistrationUnitDraft>) => {
    setUnitLinks((current) =>
      current.map((unit) =>
        unit.key === key ? { ...unit, ...changes } : unit,
      ),
    );
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const email = String(data.get("email"));
    if (data.get("password") !== data.get("confirmPassword")) {
      setError("Passwords do not match.");
      setBusy(false);
      return;
    }
    if (unitLinks.some((unit) => !unit.buildingId || !unit.unitCode.trim())) {
      setError("Complete every unit or remove the unfinished row.");
      setBusy(false);
      return;
    }
    const uniqueUnits = new Set(
      unitLinks.map(
        (unit) => `${unit.buildingId}:${unit.unitCode.trim().toUpperCase()}`,
      ),
    );
    if (uniqueUnits.size !== unitLinks.length) {
      setError("The same unit cannot be added twice.");
      setBusy(false);
      return;
    }
    if (demoMode) {
      setSubmittedEmail(email);
      setBusy(false);
      return;
    }
    const response = await fetch("/api/resident-registrations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: String(data.get("name")),
        email,
        password: String(data.get("password")),
        unitLinks: unitLinks.map(
          ({ buildingId, unitCode, relationshipType }) => ({
            buildingId,
            unitCode: unitCode.trim(),
            relationshipType,
          }),
        ),
        turnstileToken,
      }),
    });
    const result = (await response.json()) as { error?: string };
    if (response.ok) setSubmittedEmail(email);
    else
      setError(
        response.status === 429 || result.error?.includes("RATE_LIMITED")
          ? "Too many registration attempts. Please try again later."
          : result.error === "UNIT_NOT_FOUND"
            ? "One or more units were not found. Check each tower and unit number."
            : result.error === "REGISTRATION_ACCOUNT_EXISTS"
              ? "An account already uses this email. Sign in instead."
              : "Registration could not be created.",
      );
    setBusy(false);
  };

  if (submittedEmail)
    return (
      <div className="auth-screen registration-screen">
        <div className="auth-brand">
          <span className="brand-mark large">2C</span>
          <h1>Almost there</h1>
          <p>Verify your email to continue your resident registration.</p>
          <div className="auth-art">
            <i />
            <i />
            <i />
            <i />
          </div>
        </div>
        <section className="auth-card registration-confirmation" role="status">
          <span className="confirmation-icon" aria-hidden="true">
            ✉
          </span>
          <span className="eyebrow">EMAIL VERIFICATION</span>
          <h2>Check your email</h2>
          <p>We sent a verification link to:</p>
          <strong className="confirmation-email">{submittedEmail}</strong>
          <ol className="confirmation-steps">
            <li>
              <b>Open the email from Two Carats.</b>
              <span>
                Check your spam or junk folder if it is not in your inbox.
              </span>
            </li>
            <li>
              <b>Select “Verify email.”</b>
              <span>
                Your account cannot be used until verification is complete.
              </span>
            </li>
            <li>
              <b>Return and sign in.</b>
              <span>
                Then upload your ID and the required ownership or lease
                documents for administrator review.
              </span>
            </li>
          </ol>
          <a className="button full" href="/login">
            I verified my email — sign in
          </a>
          <button
            className="button secondary full"
            type="button"
            onClick={() => setSubmittedEmail("")}
          >
            The email address is wrong
          </button>
          <small className="auth-foot">
            Delivery may take a few minutes. You can safely close this page.
          </small>
        </section>
      </div>
    );

  return (
    <div className="auth-screen registration-screen">
      <div className="auth-brand">
        <span className="brand-mark large">2C</span>
        <h1>Join Celadon Park</h1>
        <p>Request secure resident access to one or more units.</p>
        <div className="auth-art">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      <form className="auth-card registration-card" onSubmit={submit}>
        <span className="eyebrow">RESIDENT REGISTRATION</span>
        <h2>Create your account</h2>
        <p>
          Every unit relationship and supporting document requires administrator
          approval.
        </p>
        <label>
          Full name
          <input name="name" autoComplete="name" required />
        </label>
        <label>
          Email address
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <fieldset className="registration-units">
          <legend>Units</legend>
          {unitLinks.map((unit, index) => (
            <div className="registration-unit-row" key={unit.key}>
              <span>Unit {index + 1}</span>
              <label>
                Tower
                <select
                  value={unit.buildingId}
                  onChange={(event) =>
                    updateUnit(unit.key, { buildingId: event.target.value })
                  }
                  required
                >
                  <option value="">Select tower</option>
                  {buildings.map((building) => (
                    <option value={building.id} key={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Unit number
                <input
                  value={unit.unitCode}
                  onChange={(event) =>
                    updateUnit(unit.key, { unitCode: event.target.value })
                  }
                  placeholder="e.g. 3012B"
                  required
                />
              </label>
              <label>
                Relationship
                <select
                  value={unit.relationshipType}
                  onChange={(event) =>
                    updateUnit(unit.key, {
                      relationshipType: event.target
                        .value as RegistrationUnitDraft["relationshipType"],
                    })
                  }
                >
                  <option value="owner">Owner</option>
                  <option value="co_owner">Co-owner</option>
                  <option value="tenant">Tenant</option>
                </select>
              </label>
              {unitLinks.length > 1 && (
                <button
                  type="button"
                  className="table-action danger-text"
                  onClick={() =>
                    setUnitLinks((current) =>
                      current.filter((candidate) => candidate.key !== unit.key),
                    )
                  }
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="button secondary add-unit"
            disabled={unitLinks.length >= 10}
            onClick={() =>
              setUnitLinks((current) => [
                ...current,
                {
                  key: crypto.randomUUID(),
                  buildingId: "",
                  unitCode: "",
                  relationshipType: "owner",
                },
              ])
            }
          >
            + Add another unit
          </button>
        </fieldset>
        <div className="form-row">
          <label>
            Password
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
            />
          </label>
          <label>
            Confirm password
            <input
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
            />
          </label>
        </div>
        <Turnstile onToken={setTurnstileToken} />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="button full"
          disabled={busy || !turnstileToken || !buildings.length}
        >
          {busy ? "Creating account…" : "Create registration"}
        </button>
        <small className="auth-foot">
          Already registered? <a href="/login">Sign in</a>
        </small>
      </form>
    </div>
  );
}

type RegistrationState = {
  registration: {
    id: string;
    status: string;
    relationshipType: string;
    verificationMode: "pre_verified" | "documents_required";
    rejectionReason?: string;
  };
  nextAction: "verify_email" | "upload_documents" | "wait_for_scan" | "awaiting_review" | "resubmit" | "complete";
  history: Array<{ event: string; status: string; reason?: string; createdAt: number }>;
  units: Array<{
    id: string;
    code: string;
    buildingName: string;
    relationshipType: string;
    status: string;
  }>;
  documents: Array<{
    id: string;
    type: string;
    originalName: string;
    status: string;
    failureReason?: string;
    createdAt: number;
  }>;
};

function RegistrationWorkspace() {
  const [state, setState] = useState<RegistrationState | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const load = () =>
    apiJson<RegistrationState>("/api/registration/me")
      .then(setState)
      .catch(() => setError("Your registration could not be loaded."));
  useEffect(() => {
    load();
  }, []);
  const upload = async (file: File | undefined, type: string) => {
    if (!file) return;
    setBusy(type);
    setError("");
    const body = new FormData();
    body.set("file", file);
    body.set("type", type);
    const response = await fetch("/api/documents", { method: "POST", body });
    if (!response.ok)
      setError(
        "The document could not be uploaded. Use PDF, JPEG, PNG, HEIC, or HEIF up to 10 MB.",
      );
    else await load();
    setBusy("");
  };
  const resubmit = async () => {
    setBusy("resubmit");
    const response = await fetch("/api/registration/me/resubmit", {
      method: "POST",
    });
    if (response.ok) await load();
    else setError("The registration could not be resubmitted.");
    setBusy("");
  };
  const signOut = async () => {
    try {
      await fetch("/api/auth/sign-out", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        credentials: "same-origin",
      });
    } finally {
      localStorage.removeItem("two-carats-persona");
      localStorage.removeItem("crysta-persona");
      window.location.assign("/login");
    }
  };
  if (!state)
    return (
      <div className="registration-workspace">
        <div className="registration-shell">
          <span className="brand-mark">2C</span>
          <Empty
            title="Loading registration"
            body={error || "Checking your verification and document status."}
          />
        </div>
      </div>
    );
  const needsOwnershipProof = state.units.some(
    (unit) =>
      unit.relationshipType === "owner" || unit.relationshipType === "co_owner",
  );
  const needsLeaseDocument = state.units.some(
    (unit) => unit.relationshipType === "tenant",
  );
  const preVerified = state.registration.verificationMode === "pre_verified";
  const latest = (type: string) =>
    state.documents.find((document) => document.type === type);
  const governmentId = latest("government_id");
  const ownershipProof = latest("ownership_proof");
  const leaseDocument = latest("lease_document");
  const nextActionLabel = {
    verify_email: "Verify your email",
    upload_documents: "Upload required documents",
    wait_for_scan: "Wait for document scanning",
    awaiting_review: "Awaiting management review",
    resubmit: "Resubmit corrected information",
    complete: "Registration complete",
  }[state.nextAction];
  return (
    <div className="registration-workspace">
      <div className="registration-shell">
        <header>
          <div className="brand">
            <span className="brand-mark">2C</span>
            <span>
              <b>Two Carats</b>
              <small>Resident registration</small>
            </span>
          </div>
          <button className="button secondary" onClick={signOut}>
            Sign out
          </button>
        </header>
        <PageHeader
          eyebrow="ACCESS REQUEST"
          title="Complete your registration"
          description="Only registration information is available until an administrator approves your account."
        />
        <section className="registration-status-grid">
          <div className="card">
            <small>Status</small>
            <Status value={state.registration.status} />
            <p>
              {state.registration.status === "rejected"
                ? state.registration.rejectionReason
                : preVerified
                  ? "Property Management verified your information before sending the invitation. No files are required."
                  : "Your email is verified. Required documents and the requested units still need review."}
            </p>
            <small>Next step</small>
            <b>{nextActionLabel}</b>
          </div>
          <div className="card">
            <small>Requested units</small>
            <b>
              {state.units
                .map((unit) => `${unit.buildingName} · ${unit.code}`)
                .join(", ")}
            </b>
            <p>
              {state.units
                .map(
                  (unit) =>
                    `${unit.code} · ${humanStatus(unit.relationshipType)}`,
                )
                .join(" · ")}
            </p>
          </div>
        </section>
        {state.registration.status === "rejected" && !preVerified && (
          <p className="callout">
            Upload corrected documents, then resubmit your registration for
            review.
          </p>
        )}
        {preVerified ? (
          <section className="card registration-documents">
            <CardTitle
              title="Management verification"
              badge="No uploads required"
            />
            <p className="registration-preverified">
              Your identity and residency records were verified offline by
              Property Management. Your account activates after email
              verification.
            </p>
          </section>
        ) : (
          <section className="card registration-documents">
            <CardTitle
              title="Required documents"
              badge="Private and scanner controlled"
            />
            <RegistrationDocument
              label="Government ID"
              document={governmentId}
              busy={busy === "government_id"}
              onFile={(file) => upload(file, "government_id")}
            />
            {needsOwnershipProof && (
              <RegistrationDocument
                label="Proof of ownership"
                document={ownershipProof}
                busy={busy === "ownership_proof"}
                onFile={(file) => upload(file, "ownership_proof")}
              />
            )}
            {needsLeaseDocument && (
              <RegistrationDocument
                label="Lease agreement"
                document={leaseDocument}
                busy={busy === "lease_document"}
                onFile={(file) => upload(file, "lease_document")}
              />
            )}
          </section>
        )}
        {state.history.length > 0 && (
          <section className="card registration-documents">
            <CardTitle title="Registration history" />
            <div className="timeline">
              {state.history.map((event, index) => (
                <p key={`${event.event}-${event.createdAt}-${index}`}>
                  <i />
                  {humanStatus(event.event)} · {humanStatus(event.status)}
                  {event.reason ? ` — ${event.reason}` : ""}
                  <small>{formatDateTime(event.createdAt)}</small>
                </p>
              ))}
            </div>
          </section>
        )}
        {error && <p className="form-error">{error}</p>}
        {state.registration.status === "rejected" && !preVerified && (
          <button
            className="button"
            disabled={Boolean(busy)}
            onClick={resubmit}
          >
            {busy === "resubmit" ? "Resubmitting…" : "Resubmit for review"}
          </button>
        )}
        <p className="registration-note">
          Two-factor authentication is optional for residents. Administrators
          must use it.
        </p>
      </div>
    </div>
  );
}

function RegistrationDocument({
  label,
  document,
  busy,
  onFile,
}: {
  label: string;
  document?: RegistrationState["documents"][number];
  busy: boolean;
  onFile: (file?: File) => void;
}) {
  const residentMessage =
    document?.status === "scan_delayed"
      ? "Your document was uploaded and is awaiting security review. You may sign out and return later."
      : document?.status === "scan_failed"
        ? "We could not complete the security review. Replace the file or contact the management office."
        : document?.status === "rejected"
          ? "This file could not be accepted. Please replace it with another copy."
          : undefined;
  return (
    <div className="registration-document">
      <span>▧</span>
      <div>
        <b>{label}</b>
        <small>
          {document
            ? document.originalName
            : "PDF, JPEG, PNG, HEIC, or HEIF · 10 MB max"}
        </small>
        {residentMessage && <p>{residentMessage}</p>}
      </div>
      {document && <Status value={document.status} />}
      <label className="table-action">
        {busy ? "Uploading…" : document ? "Replace" : "Upload"}
        <input
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
          hidden
          disabled={busy}
          onChange={(event) => onFile(event.target.files?.[0])}
        />
      </label>
    </div>
  );
}

function AuthScreen({ invite }: { invite: boolean }) {
  const [turnstileToken, setTurnstileToken] = useState("");
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [secondFactor, setSecondFactor] = useState<"totp" | "backup">("totp");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  useEffect(() => {
    if (!retryAfter) return;
    const timer = window.setInterval(() => setRetryAfter((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [retryAfter]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const data = new FormData(event.currentTarget);
    const turnstileCredential = String(
      data.get("cf-turnstile-response") || turnstileToken,
    );
    try {
      if (requiresTotp) {
        const enteredCode = String(data.get("secondFactorCode") ?? "").trim();
        const code = secondFactor === "backup"
          ? enteredCode.match(/[A-Za-z0-9]{5}-[A-Za-z0-9]{5}/u)?.[0] ?? enteredCode
          : enteredCode.replace(/\s+/gu, "");
        const response = await fetch(
          secondFactor === "totp"
            ? "/api/auth/two-factor/verify-totp"
            : "/api/auth/two-factor/verify-backup-code",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              code,
              trustDevice: false,
              disableSession: false,
            }),
          },
        );
        if (!response.ok)
          throw new Error(
            secondFactor === "totp"
              ? "The authenticator code was not accepted."
              : "The backup code was not accepted or has already been used.",
          );
        window.location.assign("/");
        return;
      }
      if (invite) {
        const token = new URLSearchParams(location.hash.slice(1)).get("token");
        if (!token)
          throw new Error("This invitation link is incomplete or expired.");
        const response = await fetch("/api/invitations/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            turnstileToken: turnstileCredential,
            name: String(data.get("name")),
            password: String(data.get("password")),
          }),
        });
        const result = await response.json<{
          error?: string;
          verificationMode?: "pre_verified" | "documents_required";
        }>();
        if (!response.ok)
          throw new Error(
            response.status === 429 || result.error?.includes("RATE_LIMITED")
              ? "Too many invitation attempts. Please try again later."
              : result.error === "INVITATION_INVALID"
                ? "This invitation is invalid, expired, or already used."
                : "We could not accept this invitation.",
          );
        history.replaceState(null, "", "/accept-invite");
        setMessage(
          result.verificationMode === "pre_verified"
            ? "Account created. Verify your email, then sign in—Property Management has already verified your documents."
            : "Account created. Verify your email, then sign in to upload the required documents.",
        );
        return;
      }
      const response = await fetch("/api/auth/sign-in/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: String(data.get("email")),
          password: String(data.get("password")),
          turnstileToken: turnstileCredential,
        }),
      });
      const result = await response.json<{
        twoFactorRedirect?: boolean;
        error?: string;
        message?: string;
        retryAfter?: number;
      }>();
      if (!response.ok)
        throw new Error(
          response.status === 429 || result.error === "TOO_MANY_REQUESTS" || result.message?.toLowerCase().includes("too many requests")
            ? (() => {
                const seconds = Number(result.retryAfter ?? response.headers.get("Retry-After") ?? 0);
                if (seconds > 0) setRetryAfter(seconds);
                return seconds > 0 ? `Too many sign-in attempts. Try again in ${Math.ceil(seconds / 60)} minute${Math.ceil(seconds / 60) === 1 ? "" : "s"}.` : "Too many sign-in attempts. Please try again later.";
              })()
            : result.error === "TURNSTILE_REQUIRED"
            ? "Complete the security check and try again."
            : "Email or password was not accepted.",
        );
      if (result.twoFactorRedirect) {
        setRequiresTotp(true);
        return;
      }
      window.location.assign("/");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth-screen">
      <div className="auth-brand">
        <span className="brand-mark large">2C</span>
        <h1>Two Carats</h1>
        <p>A better way to manage your community.</p>
        <div className="auth-art">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      <form className="auth-card" onSubmit={submit}>
        <span className="eyebrow">
          {invite ? "SECURE INVITATION" : "WELCOME BACK"}
        </span>
        <h2>
          {requiresTotp
            ? "Two-factor verification"
            : invite
              ? "Accept your invitation"
              : "Sign in to your property"}
        </h2>
        <p>
          {requiresTotp
            ? secondFactor === "totp"
              ? "Enter the six-digit code from your authenticator app."
              : "Enter one of the one-time backup codes you saved during setup."
            : invite
              ? "Create your account to access your assigned workspace."
              : "Use the email associated with your residence or team."}
        </p>
        {requiresTotp ? (
          <>
            <label>
              {secondFactor === "totp" ? "Authenticator code" : "Backup code"}
              <input
                name="secondFactorCode"
                inputMode={secondFactor === "totp" ? "numeric" : "text"}
                autoComplete="one-time-code"
                minLength={secondFactor === "totp" ? 6 : 8}
                maxLength={secondFactor === "totp" ? 6 : 64}
                onPaste={(event) => {
                  if (secondFactor !== "backup") return;
                  const pasted = event.clipboardData.getData("text").trim();
                  const code = pasted.match(/[A-Za-z0-9]{5}-[A-Za-z0-9]{5}/u)?.[0];
                  if (code) {
                    event.preventDefault();
                    event.currentTarget.value = code;
                  }
                }}
                required
              />
            </label>
            <button
              type="button"
              className="table-action"
              onClick={() => {
                setSecondFactor((current) =>
                  current === "totp" ? "backup" : "totp",
                );
                setError("");
              }}
            >
              {secondFactor === "totp"
                ? "Use a backup code"
                : "Use authenticator instead"}
            </button>
          </>
        ) : (
          <>
            {invite && (
              <label>
                Full name
                <input
                  name="name"
                  autoComplete="name"
                  placeholder="Your full name"
                  required
                />
              </label>
            )}
            {!invite && (
              <label>
                Email address
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                />
              </label>
            )}
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete={invite ? "new-password" : "current-password"}
                placeholder="At least 12 characters"
                minLength={12}
                required
              />
            </label>
            <Turnstile onToken={setTurnstileToken} />
          </>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="form-success" role="status">
            {message}
          </p>
        )}
        <button
          className="button full"
          disabled={busy || retryAfter > 0 || (invite && !turnstileToken)}
        >
          {busy
            ? "Please wait…"
            : requiresTotp
              ? "Verify code"
              : invite
                ? "Create secure account"
                : retryAfter > 0
                  ? `Try again in ${retryAfter}s`
                  : "Sign in"}
        </button>
        <small className="auth-foot">
          {invite ? (
            "Invitation links are single-use."
          ) : (
            <>
              New resident? <a href="/register">Request access</a>
            </>
          )}
        </small>
      </form>
    </div>
  );
}

function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (import.meta.env.DEV) {
      onToken("dev-turnstile-token");
      return;
    }
    let cancelled = false;
    fetch("/api/tenant")
      .then(
        async (response) =>
          (await response.json()) as { turnstileSiteKey?: string },
      )
      .then((result) => {
        if (!result.turnstileSiteKey || cancelled) return;
        const render = () =>
          window.turnstile?.render(container.current!, {
            sitekey: result.turnstileSiteKey!,
            callback: onToken,
            "expired-callback": () => onToken(""),
          });
        if (window.turnstile) render();
        else {
          const script = document.createElement("script");
          script.src =
            "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
          script.async = true;
          script.defer = true;
          script.onload = render;
          document.head.appendChild(script);
        }
      })
      .catch(() => onToken(""));
    return () => {
      cancelled = true;
    };
  }, [onToken]);
  return (
    <div
      className="turnstile"
      ref={container}
      aria-label="Security verification"
    />
  );
}

function TwoFactorSetup() {
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const enable = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const response = await fetch("/api/auth/two-factor/enable", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    const result = (await response.json()) as {
      totpURI?: string;
      backupCodes?: string[];
      message?: string;
    };
    if (!response.ok || !result.totpURI) {
      setError(result.message ?? "Could not enable two-factor authentication.");
      return;
    }
    setSecret(
      new URL(result.totpURI).searchParams.get("secret") ?? result.totpURI,
    );
    setCodes(result.backupCodes ?? []);
    const QRCode = (await import("qrcode")).default;
    setQr(
      await QRCode.toDataURL(result.totpURI, {
        width: 280,
        margin: 2,
        color: { dark: "#062f43", light: "#ffffff" },
        errorCorrectionLevel: "M",
      }),
    );
  };
  const verify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const response = await fetch("/api/auth/two-factor/verify-totp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: String(data.get("code")),
        trustDevice: false,
      }),
    });
    if (!response.ok) {
      setError("The authenticator code was not accepted.");
      return;
    }
    window.location.reload();
  };
  const downloadCodes = () => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([`Two Carats backup codes\n\n${codes.join("\n")}\n`], {
        type: "text/plain",
      }),
    );
    link.download = "two-carats-backup-codes.txt";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <div className="auth-screen">
      <div className="auth-brand">
        <span className="brand-mark large">2C</span>
        <h1>Secure admin access</h1>
        <p>
          Two-factor authentication is required for property administrators.
        </p>
        <div className="auth-art">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      {secret ? (
        <form className="auth-card" onSubmit={verify}>
          <span className="eyebrow">STEP 2 OF 2</span>
          <h2>Verify your authenticator</h2>
          <p>
            Scan the QR code with your authenticator app. If scanning is
            unavailable, enter the secret manually.
          </p>
          {qr && (
            <img
              className="totp-qr"
              src={qr}
              alt="QR code for Two Carats authenticator setup"
              width="210"
              height="210"
            />
          )}
          <div className="totp-manual">
            <small>Manual setup key</small>
            <code className="totp-secret">{secret}</code>
            <button
              type="button"
              className="table-action"
              onClick={() => void navigator.clipboard.writeText(secret)}
            >
              Copy key
            </button>
          </div>
          {codes.length > 0 && (
            <div className="backup-codes">
              <b>One-time backup codes</b>
              <p>
                Store these somewhere safe. Each code can be used only once.
              </p>
              {codes.map((code) => (
                <code key={code}>{code}</code>
              ))}
              <button
                type="button"
                className="table-action"
                onClick={downloadCodes}
              >
                Download codes
              </button>
            </div>
          )}
          <label className="checkbox-line">
            <input type="checkbox" required /> I saved my backup codes.
          </label>
          <label>
            Authenticator code
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              minLength={6}
              maxLength={6}
              required
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="button full">Verify and continue</button>
        </form>
      ) : (
        <form className="auth-card" onSubmit={enable}>
          <span className="eyebrow">STEP 1 OF 2</span>
          <h2>Enable two-factor authentication</h2>
          <p>
            Confirm your password to generate an authenticator QR code and
            one-time backup codes.
          </p>
          <label>
            Current password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="button full">Continue securely</button>
        </form>
      )}
    </div>
  );
}

declare global {
  interface Crypto {
    randomUUID(): string;
  }
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback": () => void;
        },
      ) => string;
    };
  }
}

function Status({ value, label }: { value: string; label?: string }) {
  return (
    <span className={`status ${value.toLowerCase().replaceAll(" ", "_")}`}>
      {label ?? humanStatus(value)}
    </span>
  );
}
function humanStatus(value: string) {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/gu, (char) => char.toUpperCase());
}
function formatDateTime(epochSeconds: number) {
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(new Date(epochSeconds * 1000))
    .toUpperCase();
}
function formatEventSchedule(startsAt: number, endsAt: number) {
  const day = new Intl.DateTimeFormat("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric" });
  const time = new Intl.DateTimeFormat("en-PH", { timeZone: "Asia/Manila", hour: "numeric", minute: "2-digit" });
  const start = new Date(startsAt * 1000);
  const end = new Date(endsAt * 1000);
  return `${formatDateTime(startsAt)}–${day.format(start) === day.format(end) ? time.format(end) : `${day.format(end)} ${time.format(end)}`}`;
}
async function apiJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) throw new Error(body?.error ?? `Request failed: ${response.status}`);
  return body as T;
}
function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");
}
function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <span aria-hidden="true">◇</span>
      <b>{title}</b>
      <p>{body}</p>
    </div>
  );
}
